/**
 * useChat.js - Chat State Management Hook
 * Centralizes chat-related state and logic for the ChatApp component
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { config } from '../config';
import { logger } from '../logger';
import { 
  generateId, 
  loadConversations, 
  saveConversations, 
  loadActiveConversationId, 
  saveActiveConversationId,
  mergeConversations,
  getUserConversationsKey,
  getUserActiveConversationKey
} from '../utils/storage';
import { 
  loadHistoryFromServer, 
  clearHistoryOnServer, 
  deleteConversationOnServer,
  sendChatMessage,
  analyzeDictionary,
  approveDictionaryAnalysis,
  saveDictionaryCanvasEntry,
  updateDictionaryCanvasEntry,
  approveQuestion as approveQuestionApi,
  revokeQuestionApproval as revokeQuestionApprovalApi,
  getQuestionApprovals,
  startProtestReportJob,
  getProtestReportStatus,
  getActiveProtestReportJobs,
  getProtestMonitorTargets,
  updateProtestMonitorTargets,
  PROTEST_REPORT_POLL_INTERVAL_MS,
  PROTEST_REPORT_MAX_WAIT_MS,
  summarizeVideo,
  summarizeVideoUrl,
  scoreVideoUrl,
  scoreVideoFile,
  transcribeVideo,
  transcribeVideoUrl,
  analyzeDeepfake,
  analyzeDeepfakeUrl,
  startDeepResearchJob,
  getDeepResearchStatus,
  getActiveDeepResearchJobs,
  DEEP_RESEARCH_POLL_INTERVAL_MS,
  streamDeepResearchEvents,
  getPdfTaskStatus,
  PDF_TASK_POLL_INTERVAL_MS,
  streamPdfTaskEvents,
  startGmsReportJob,
  getGmsReportStatus,
  getActiveGmsReportJobs,
  GMS_REPORT_POLL_INTERVAL_MS,
  streamGmsReportEvents,
  startAviationReportJob,
  getAviationReportStatus,
  getActiveAviationReportJobs,
  AVIATION_REPORT_POLL_INTERVAL_MS,
  streamAviationReportEvents,
  startEnergyReportJob,
  getEnergyReportStatus,
  getActiveEnergyReportJobs,
  ENERGY_REPORT_POLL_INTERVAL_MS,
  streamEnergyReportEvents,
  startDataCenterReportJob,
  getDataCenterReportStatus,
  getActiveDataCenterReportJobs,
  DATA_CENTER_REPORT_POLL_INTERVAL_MS,
  streamDataCenterReportEvents,
  startGamingReportJob,
  getGamingReportStatus,
  getActiveGamingReportJobs,
  GAMING_REPORT_POLL_INTERVAL_MS,
  streamGamingReportEvents,
  startUsChinaTradeReportJob,
  getUsChinaTradeReportStatus,
  US_CHINA_TRADE_REPORT_POLL_INTERVAL_MS,
  streamUsChinaTradeReportEvents,
  startLatamSummaryJob,
  getLatamSummaryStatus,
  getActiveLatamSummaryJobs,
  LATAM_SUMMARY_POLL_INTERVAL_MS,
  streamLatamSummaryEvents,
  startLatamToCsvJob,
  getLatamToCsvStatus,
  getActiveLatamToCsvJobs,
  LATAM_TO_CSV_POLL_INTERVAL_MS,
  streamLatamToCsvEvents,
  startSequoiaStructureJob,
  getSequoiaStructureStatus,
  getActiveSequoiaStructureJobs,
  SEQUOIA_STRUCTURE_POLL_INTERVAL_MS,
  streamSequoiaStructureEvents,
  startTwaSummaryJob,
  getTwaSummaryStatus,
  getActiveTwaSummaryJobs,
  TWA_SUMMARY_POLL_INTERVAL_MS,
  streamTwaSummaryEvents,
  cancelDeepResearchJob,
  cancelGmsReportJob,
  cancelAviationReportJob,
  retryAviationReportArtifacts,
  downloadAviationReportPptx,
  cancelEnergyReportJob,
  cancelDataCenterReportJob,
  cancelGamingReportJob,
  cancelUsChinaTradeReportJob,
  cancelLatamSummaryJob,
  cancelLatamToCsvJob,
  cancelSequoiaStructureJob,
  cancelTwaSummaryJob,
  analyzeStatisticalSummary,
  generateInfographic,
  createHtmlBlobUrl,
} from '../services/api';
import { heuristicRouteIntent } from '../services/toolRoutingHeuristics';

// URL pattern for detecting dictionary analysis URLs
const DICTIONARY_URL_PATTERN = /https?:\/\/media\.tamcglobal\.com\/[^\s]+\.(jpg|jpeg|png|gif)/gi;
const PROTEST_ACTIVE_JOBS_STORAGE_PREFIX = 'protest_active_jobs_v1';
const DEEP_RESEARCH_ACTIVE_JOBS_STORAGE_PREFIX = 'deep_research_active_jobs_v1';
const GMS_ACTIVE_JOBS_STORAGE_PREFIX = 'gms_active_jobs_v1';
const LATAM_ACTIVE_JOBS_STORAGE_PREFIX = 'latam_active_jobs_v1';
const LATAM_CSV_ACTIVE_JOBS_STORAGE_PREFIX = 'latam_csv_active_jobs_v1';
const SEQUOIA_STRUCTURE_ACTIVE_JOBS_STORAGE_PREFIX = 'sequoia_structure_active_jobs_v1';
const TWA_ACTIVE_JOBS_STORAGE_PREFIX = 'twa_active_jobs_v1';
const AVIATION_ACTIVE_JOBS_STORAGE_PREFIX = 'aviation_active_jobs_v1';
const ENERGY_ACTIVE_JOBS_STORAGE_PREFIX = 'energy_active_jobs_v1';
const DATA_CENTER_ACTIVE_JOBS_STORAGE_PREFIX = 'data_center_active_jobs_v1';
const GAMING_ACTIVE_JOBS_STORAGE_PREFIX = 'gaming_active_jobs_v1';
const US_CHINA_TRADE_ACTIVE_JOBS_STORAGE_PREFIX = 'us_china_trade_active_jobs_v1';
const PDF_TASK_ACTIVE_JOBS_STORAGE_PREFIX = 'pdf_task_active_jobs_v1';
const INFOGRAPHIC_STATUS_STEPS = [
  'Reading the complete PDF…',
  'Selecting the most important insights…',
  'Rendering the 2K portrait infographic…',
  'Finalizing the image and saving it to this chat…',
];

const getProtestJobsStorageKey = (userId) =>
  `${PROTEST_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getDeepResearchJobsStorageKey = (userId) =>
  `${DEEP_RESEARCH_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getGmsJobsStorageKey = (userId) =>
  `${GMS_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getLatamJobsStorageKey = (userId) =>
  `${LATAM_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getLatamCsvJobsStorageKey = (userId) =>
  `${LATAM_CSV_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getSequoiaStructureJobsStorageKey = (userId) =>
  `${SEQUOIA_STRUCTURE_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getTwaJobsStorageKey = (userId) =>
  `${TWA_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getAviationJobsStorageKey = (userId) =>
  `${AVIATION_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getEnergyJobsStorageKey = (userId) =>
  `${ENERGY_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getDataCenterJobsStorageKey = (userId) =>
  `${DATA_CENTER_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getGamingJobsStorageKey = (userId) =>
  `${GAMING_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getUsChinaTradeJobsStorageKey = (userId) =>
  `${US_CHINA_TRADE_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const getPdfTaskJobsStorageKey = (userId) =>
  `${PDF_TASK_ACTIVE_JOBS_STORAGE_PREFIX}_${userId}`;

const formatDeepResearchProgress = (payload) => {
  const progress = payload?.progress || {};
  const step = payload?.step || progress?.step;
  const message = payload?.message || '';
  const progressParts = [];
  if (progress.current_step != null && progress.total_steps != null) {
    progressParts.push(`step ${progress.current_step}/${progress.total_steps}`);
  }
  if (progress.percent != null) {
    progressParts.push(`${progress.percent}%`);
  }
  const progressText = progressParts.length ? ` (${progressParts.join(', ')})` : '';

  if (payload?.status === 'ready') return 'Deep research report ready.';
  if (payload?.status === 'failed') {
    return `Research failed: ${payload.detail || payload.error || 'Unknown error'}`;
  }

  const lines = [];
  if (step) lines.push(`Stage: ${step}${progressText}`);
  if (message) lines.push(message);
  return lines.join('\n') || 'Running deep research…';
};

const getProtestPhaseText = (status) => {
  const progress = status?.progress || {};
  const hasPercent = progress.percent != null;
  const hasSteps = progress.current_step != null && progress.total_steps != null;
  const progressParts = [];
  if (hasSteps) {
    progressParts.push(`step ${progress.current_step}/${progress.total_steps}`);
  }
  if (hasPercent) {
    progressParts.push(`${progress.percent}%`);
  }
  const progressText = progressParts.length ? ` (${progressParts.join(', ')})` : '';

  if (status?.status === 'ready') return 'Report ready.';
  if (status?.status === 'failed') return `Report failed: ${status.detail || 'Unknown error'}`;
  if (!status?.upstream_job_id && status?.phase === 'starting_upstream_job') {
    return `${status.message || 'Submitting report job to upstream...'}${progressText}`;
  }
  if (status?.message && status?.stage) {
    return `${status.message}\nStage: ${status.stage}${progressText}`;
  }
  if (status?.message) return `${status.message}${progressText}`;
  if (status?.stage) {
    return `Stage: ${status.stage}${progressText}`;
  }
  if (status?.phase === 'waiting_for_report_file') {
    return 'Generation was triggered. Checking for the report file...';
  }
  return 'Checking upstream report job status...';
};

const mapProtestStats = (stats) => ({
  totalQueries: stats?.total_queries || 0,
  successfulQueries: stats?.successful_queries || 0,
  dateChunks: stats?.date_chunks || 0,
  region: stats?.region,
  reportDate: stats?.report_date,
  eventsDiscovered: stats?.events_discovered,
  newInReport: stats?.new_in_report,
  existingInReport: stats?.existing_in_report,
  linksValidated: stats?.links_validated,
  locationsGeocoded: stats?.locations_geocoded,
  discoveryEventsExtracted: stats?.discovery_events_extracted,
  discoveryEventsAfterFilter: stats?.discovery_events_after_filter,
  filteredInvalidDate: stats?.discovery_filtered_invalid_date,
  filteredOutOfRange: stats?.discovery_filtered_out_of_range,
  filteredLocationMismatch: stats?.discovery_filtered_location_mismatch,
  filteredKeywordMismatch: stats?.discovery_filtered_keyword_mismatch,
  filteredLowRelevance: stats?.discovery_filtered_low_relevance,
  deduplicated: stats?.discovery_deduplicated,
});

const appendZeroEventDiagnostics = (response, stats) => {
  if (!stats || stats.events_discovered !== 0) return response;

  const diagnostics = [];
  const extracted = stats.discovery_events_extracted;
  if (Number.isFinite(extracted)) {
    diagnostics.push(`Events extracted before filtering: ${extracted}`);
  }
  const reasons = [
    ['outside the requested date range', stats.discovery_filtered_out_of_range],
    ['with invalid dates', stats.discovery_filtered_invalid_date],
    ['outside the requested location', stats.discovery_filtered_location_mismatch],
    ['not matching the selected keywords', stats.discovery_filtered_keyword_mismatch],
    ['with low protest relevance', stats.discovery_filtered_low_relevance],
    ['as duplicates', stats.discovery_deduplicated],
  ].filter(([, count]) => Number.isFinite(count) && count > 0);
  reasons.forEach(([label, count]) => diagnostics.push(`Filtered ${count} ${label}`));

  if (!diagnostics.length) return response;
  return `${response}\n\n## Why no events matched\n\n${diagnostics.map((line) => `- ${line}`).join('\n')}`;
};

/**
 * Custom hook for managing chat state and operations
 */
export function useChat(user, token) {
  // Conversation state
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  
  // Input state
  const [input, setInput] = useState('');
  const [selectedModel, setSelectedModel] = useState(config.defaultModel);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [selectedPdfName, setSelectedPdfName] = useState(null);
  const [selectedExcel, setSelectedExcel] = useState(null);
  const [selectedExcelName, setSelectedExcelName] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedVideoName, setSelectedVideoName] = useState(null);
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [copiedMessageIdx, setCopiedMessageIdx] = useState(null);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [isUploadDropdownOpen, setIsUploadDropdownOpen] = useState(false);
  const [expandedCommunications, setExpandedCommunications] = useState({});
  
  // Dictionary Canvas state
  const [isDictionaryCanvasOpen, setIsDictionaryCanvasOpen] = useState(false);
  const [dictionaryResults, setDictionaryResults] = useState(null);
  const [dictionaryEdits, setDictionaryEdits] = useState(null);
  const [dictionaryImageUrl, setDictionaryImageUrl] = useState(null);
  const [dictionaryImageData, setDictionaryImageData] = useState(null);
  const [dictionaryPoiName, setDictionaryPoiName] = useState('');
  const [isDictionaryLoading, setIsDictionaryLoading] = useState(false);
  const [dictionaryError, setDictionaryError] = useState(null);
  const [detectedDictionaryUrl, setDetectedDictionaryUrl] = useState(null);
  
  // Canvas entry tracking for persistence
  const [currentCanvasEntryId, setCurrentCanvasEntryId] = useState(null);
  const [, setCanvasAnchorMessageIdx] = useState(null);
  const [canvasStatus, setCanvasStatus] = useState('pending'); // pending, analyzed, approved
  const [isCanvasReadOnly, setIsCanvasReadOnly] = useState(false);
  const [, setIsCanvasReopened] = useState(false); // Track if canvas was reopened from a link
  
  // Protest Query Modal state
  const [isProtestModalOpen, setIsProtestModalOpen] = useState(false);
  const [isProtestLoading, setIsProtestLoading] = useState(false);
  const [protestProgress, setProtestProgress] = useState({ current: 0, total: 0, phase: 'searching' });
  const [protestInitialValues, setProtestInitialValues] = useState(null);

  // Daily Protest Monitor configuration modal state
  const [isProtestMonitorConfigOpen, setIsProtestMonitorConfigOpen] = useState(false);
  const [protestMonitorConfig, setProtestMonitorConfig] = useState(null);
  const [isProtestMonitorConfigLoading, setIsProtestMonitorConfigLoading] = useState(false);
  const [isProtestMonitorConfigSaving, setIsProtestMonitorConfigSaving] = useState(false);
  const [protestMonitorConfigError, setProtestMonitorConfigError] = useState(null);
  const [protestMonitorConfigSuccess, setProtestMonitorConfigSuccess] = useState('');
  
  // Video Summarize Modal state
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [isVideoScoreLoading, setIsVideoScoreLoading] = useState(false);
  const [videoInitialValues, setVideoInitialValues] = useState(null);
  const [videoMode, setVideoMode] = useState('summarize');

  // Deepfake Detection Modal state
  const [isDeepfakeModalOpen, setIsDeepfakeModalOpen] = useState(false);
  const [isDeepfakeLoading, setIsDeepfakeLoading] = useState(false);
  const [deepfakeInitialValues, setDeepfakeInitialValues] = useState(null);

  const [isDeepResearchModalOpen, setIsDeepResearchModalOpen] = useState(false);
  const [isDeepResearchLoading, setIsDeepResearchLoading] = useState(false);
  const [deepResearchProgress, setDeepResearchProgress] = useState({ message: '', step: '' });
  const [deepResearchInitialValues, setDeepResearchInitialValues] = useState(null);
  const deepResearchPollersRef = useRef({});

  // Statistical summary state
  const [isStatisticalSummaryModalOpen, setIsStatisticalSummaryModalOpen] = useState(false);
  const [isStatisticalSummaryLoading, setIsStatisticalSummaryLoading]   = useState(false);

  // Infographic state
  const [isInfographicModalOpen, setIsInfographicModalOpen]   = useState(false);
  const [isInfographicLoading, setIsInfographicLoading]       = useState(false);

  // GMS report state – completely separate from deep research
  const gmsPollersRef = useRef({});
  const gmsResumeCheckedRef = useRef(null);
  const gmsSseCancelledRef = useRef({});
  const latamPollersRef = useRef({});
  const latamResumeCheckedRef = useRef(null);
  const latamSseCancelledRef = useRef({});
  const latamCsvPollersRef = useRef({});
  const latamCsvResumeCheckedRef = useRef(null);
  const latamCsvSseCancelledRef = useRef({});
  const sequoiaStructurePollersRef = useRef({});
  const sequoiaStructureResumeCheckedRef = useRef(null);
  const sequoiaStructureSseCancelledRef = useRef({});
  const twaPollersRef = useRef({});
  const twaResumeCheckedRef = useRef(null);
  const twaSseCancelledRef = useRef({});
  // Aviation report state – completely separate from deep research and GMS
  const aviationPollersRef = useRef({});
  const aviationResumeCheckedRef = useRef(null);
  const aviationSseCancelledRef = useRef({});
  const aviationArtifactRefreshRef = useRef({
    conversationId: null,
    jobIds: new Set(),
  });
  // Energy report state – completely separate from other pipelines
  const energyPollersRef = useRef({});
  const energyResumeCheckedRef = useRef(null);
  const energySseCancelledRef = useRef({});
  // Data Centers report state – completely separate from other pipelines
  const dataCenterPollersRef = useRef({});
  const dataCenterResumeCheckedRef = useRef(null);
  const dataCenterSseCancelledRef = useRef({});
  const gamingPollersRef = useRef({});
  const gamingResumeCheckedRef = useRef(null);
  const gamingSseCancelledRef = useRef({});
  const usChinaTradePollersRef = useRef({});
  const usChinaTradeSseCancelledRef = useRef({});

  // Per-question approval tracking
  // Format: { express: { id, approved_at }, suspected: { id, approved_at }, acknowledge: { id, approved_at } }
  const [questionApprovals, setQuestionApprovals] = useState({});
  
  // Refs
  const messagesEndRef = useRef(null);
  const imageInputRef = useRef(null);
  const analysisEntryIdRef = useRef(null); // Track entry ID for background analysis updates
  const pdfInputRef = useRef(null);
  const excelInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const modelDropdownRef = useRef(null);
  const uploadDropdownRef = useRef(null);
  const textareaRef = useRef(null);
  const activeProtestPollsRef = useRef(new Set());
  const protestResumeCheckedRef = useRef(null);
  const deepResearchResumeCheckedRef = useRef(null);
  const deepResearchSseCancelledRef = useRef({});
  // (gmsResumeCheckedRef and gmsSseCancelledRef declared above with gmsPollersRef)

  // PDF task refs
  const pdfTaskPollersRef = useRef({});
  const pdfTaskSseCancelledRef = useRef({});
  const pdfTaskResumeCheckedRef = useRef(null);

  // AbortController for the in-flight main chat request
  const chatAbortControllerRef = useRef(null);

  // Tracks which async job IDs are currently active (from React's perspective).
  // Used to show the Stop button while SSE/polling is running in the background,
  // even after isDeepResearchLoading has been reset by the finally block.
  const knownAsyncJobIds = useRef(new Set());
  const [activeAsyncJobCount, setActiveAsyncJobCount] = useState(0);

  // Check if current model supports multimodal
  const currentModel = config.models.find(m => m.id === selectedModel);
  const isMultimodal = currentModel?.multimodal ?? false;

  // ============================================
  // Effects
  // ============================================

  // Load conversations when user is authenticated
  useEffect(() => {
    if (user?.id && token) {
      loadHistoryFromServer(token).then((serverConversations) => {
        const localConversations = loadConversations(user.id);
        const merged = mergeConversations(
          serverConversations || [],
          localConversations || []
        );

        if (merged.length > 0) {
          setConversations(merged);
          saveConversations(user.id, merged);
          logger.info('Loaded conversations (server + local merge)', {
            server: (serverConversations || []).length,
            local: (localConversations || []).length,
            merged: merged.length,
          });

          const savedActiveId = loadActiveConversationId(user.id);
          if (savedActiveId && merged.find((c) => c.id === savedActiveId)) {
            setActiveConversationId(savedActiveId);
            const activeConv = merged.find((c) => c.id === savedActiveId);
            if (activeConv) {
              setMessages(activeConv.messages || []);
            }
          }
        } else {
          logger.info('No conversations on server or in local cache');
          setConversations([]);
          setActiveConversationId(null);
          setMessages([]);
        }
      }).catch(error => {
        logger.error('Failed to load from server, falling back to localStorage', error);
        const loaded = loadConversations(user.id);
        setConversations(loaded);

        const savedActiveId = loadActiveConversationId(user.id);
        if (savedActiveId && loaded.find((c) => c.id === savedActiveId)) {
          setActiveConversationId(savedActiveId);
          const activeConv = loaded.find((c) => c.id === savedActiveId);
          if (activeConv) {
            setMessages(activeConv.messages || []);
          }
        }
      });
    }
  }, [user, token]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target)) {
        setIsModelDropdownOpen(false);
      }
      if (uploadDropdownRef.current && !uploadDropdownRef.current.contains(event.target)) {
        setIsUploadDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Save conversations when they change
  useEffect(() => {
    if (user?.id && conversations.length > 0) {
      saveConversations(user.id, conversations);
    }
  }, [conversations, user]);

  // Save active conversation ID
  useEffect(() => {
    if (user?.id) {
      saveActiveConversationId(user.id, activeConversationId);
    }
  }, [activeConversationId, user]);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [input, adjustTextareaHeight]);

  // Setup paste handler
  useEffect(() => {
    const handlePaste = (event) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64 = reader.result?.split(',')[1];
              setSelectedImage(base64);
              logger.debug('Image pasted', { size: file.size, type: file.type });
            };
            reader.readAsDataURL(file);
          }
          event.preventDefault();
          break;
        }
      }
    };

    document.body.addEventListener('paste', handlePaste);
    return () => document.body.removeEventListener('paste', handlePaste);
  }, []);

  // ============================================
  // Handlers
  // ============================================

  const updateConversationMessages = useCallback((conversationId, newMessages) => {
    setConversations(prev => 
      prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, messages: newMessages }
          : conv
      )
    );
  }, []);

  const updateProtestJobMessage = useCallback((conversationId, messageId, updates) => {
    const applyUpdates = (msgs) =>
      msgs.map((msg) =>
        msg.id === messageId
          ? { ...msg, ...(typeof updates === 'function' ? updates(msg) : updates) }
          : msg
      );

    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === conversationId
          ? { ...conv, messages: applyUpdates(conv.messages || []) }
          : conv
      )
    );

    setMessages((prev) =>
      prev.some((msg) => msg.id === messageId) ? applyUpdates(prev) : prev
    );
  }, []);

  const saveActiveProtestJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getProtestJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    const next = [
      ...existing.filter((j) => j.jobId !== jobRecord.jobId),
      jobRecord,
    ];
    localStorage.setItem(storageKey, JSON.stringify(next));
  }, [user]);

  const removeActiveProtestJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getProtestJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((j) => j.jobId !== jobId))
    );
  }, [user]);

  const updateDeepResearchJobMessage = useCallback((conversationId, messageId, updates) => {
    const applyUpdates = (msgs) =>
      msgs.map((msg) =>
        msg.id === messageId
          ? { ...msg, ...(typeof updates === 'function' ? updates(msg) : updates) }
          : msg
      );

    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === conversationId
          ? { ...conv, messages: applyUpdates(conv.messages || []) }
          : conv
      )
    );

    setMessages((prev) =>
      prev.some((msg) => msg.id === messageId) ? applyUpdates(prev) : prev
    );
  }, []);

  // ---------------------------------------------------------------------------
  // PDF task helpers
  // ---------------------------------------------------------------------------

  const updatePdfTaskMessage = useCallback((conversationId, messageId, updates) => {
    const applyUpdates = (msgs) =>
      msgs.map((msg) =>
        msg.id === messageId
          ? { ...msg, ...(typeof updates === 'function' ? updates(msg) : updates) }
          : msg
      );
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === conversationId
          ? { ...conv, messages: applyUpdates(conv.messages || []) }
          : conv
      )
    );
    setMessages((prev) =>
      prev.some((msg) => msg.id === messageId) ? applyUpdates(prev) : prev
    );
  }, []);

  const pollPdfTaskJobToMessage = useCallback(
    (jobId, conversationId, messageId, onSessionExpired) => {
      if (pdfTaskPollersRef.current[jobId]) return;

      const poll = async () => {
        try {
          const status = await getPdfTaskStatus(token, jobId);

          if (status.status === 'ready') {
            pdfTaskSseCancelledRef.current[jobId] = true;
            delete pdfTaskPollersRef.current[jobId];
            updatePdfTaskMessage(conversationId, messageId, {
              text: status.result_text || 'PDF processing completed but the result was empty. Please try again.',
              pdfTaskJobStatus: status.result_text ? 'ready' : 'failed',
            });
            return;
          }

          if (status.status === 'failed') {
            pdfTaskSseCancelledRef.current[jobId] = true;
            delete pdfTaskPollersRef.current[jobId];
            updatePdfTaskMessage(conversationId, messageId, {
              text: `PDF processing failed: ${status.error || 'Unknown error'}`,
              pdfTaskJobStatus: 'failed',
            });
            return;
          }

          // Still running — update progress text and schedule next poll
          if (status.message) {
            updatePdfTaskMessage(conversationId, messageId, {
              text: status.message,
              pdfTaskJobStatus: 'running',
            });
          }

          pdfTaskPollersRef.current[jobId] = setTimeout(poll, PDF_TASK_POLL_INTERVAL_MS);
        } catch (err) {
          if (err.message === 'SESSION_EXPIRED') {
            onSessionExpired?.();
          } else {
            // Retry on transient errors
            pdfTaskPollersRef.current[jobId] = setTimeout(poll, PDF_TASK_POLL_INTERVAL_MS * 2);
          }
        }
      };

      pdfTaskPollersRef.current[jobId] = setTimeout(poll, PDF_TASK_POLL_INTERVAL_MS);
    },
    [token, updatePdfTaskMessage]
  );

  const saveActivePdfTaskJob = useCallback((record) => {
    if (!user?.id) return;
    const key = getPdfTaskJobsStorageKey(user.id);
    try {
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const updated = [...existing.filter((j) => j.jobId !== record.jobId), record];
      localStorage.setItem(key, JSON.stringify(updated));
    } catch (_) {}
  }, [user]);

  const removeActivePdfTaskJob = useCallback((jobId) => {
    if (!user?.id) return;
    const key = getPdfTaskJobsStorageKey(user.id);
    try {
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      localStorage.setItem(key, JSON.stringify(existing.filter((j) => j.jobId !== jobId)));
    } catch (_) {}
  }, [user]);

  // ---------------------------------------------------------------------------

  const saveActiveDeepResearchJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getDeepResearchJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    const next = [
      ...existing.filter((j) => j.jobId !== jobRecord.jobId),
      jobRecord,
    ];
    localStorage.setItem(storageKey, JSON.stringify(next));
    if (!knownAsyncJobIds.current.has(jobRecord.jobId)) {
      knownAsyncJobIds.current.add(jobRecord.jobId);
      setActiveAsyncJobCount(c => c + 1);
    }
  }, [user]);

  const removeActiveDeepResearchJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getDeepResearchJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((j) => j.jobId !== jobId))
    );
    delete deepResearchSseCancelledRef.current[jobId];
    if (knownAsyncJobIds.current.has(jobId)) {
      knownAsyncJobIds.current.delete(jobId);
      setActiveAsyncJobCount(c => Math.max(0, c - 1));
    }
  }, [user]);

  const saveActiveGmsJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getGmsJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    const next = [
      ...existing.filter((j) => j.jobId !== jobRecord.jobId),
      jobRecord,
    ];
    localStorage.setItem(storageKey, JSON.stringify(next));
    if (!knownAsyncJobIds.current.has(jobRecord.jobId)) {
      knownAsyncJobIds.current.add(jobRecord.jobId);
      setActiveAsyncJobCount(c => c + 1);
    }
  }, [user]);

  const removeActiveGmsJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getGmsJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((j) => j.jobId !== jobId))
    );
    delete gmsSseCancelledRef.current[jobId];
    if (knownAsyncJobIds.current.has(jobId)) {
      knownAsyncJobIds.current.delete(jobId);
      setActiveAsyncJobCount(c => Math.max(0, c - 1));
    }
  }, [user]);

  const saveActiveLatamJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getLatamJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    const next = [
      ...existing.filter((j) => j.jobId !== jobRecord.jobId),
      jobRecord,
    ];
    localStorage.setItem(storageKey, JSON.stringify(next));
    if (!knownAsyncJobIds.current.has(jobRecord.jobId)) {
      knownAsyncJobIds.current.add(jobRecord.jobId);
      setActiveAsyncJobCount(c => c + 1);
    }
  }, [user]);

  const removeActiveLatamJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getLatamJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((j) => j.jobId !== jobId))
    );
    delete latamSseCancelledRef.current[jobId];
    if (knownAsyncJobIds.current.has(jobId)) {
      knownAsyncJobIds.current.delete(jobId);
      setActiveAsyncJobCount(c => Math.max(0, c - 1));
    }
  }, [user]);

  const saveActiveLatamCsvJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getLatamCsvJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify([...existing.filter((job) => job.jobId !== jobRecord.jobId), jobRecord])
    );
    if (!knownAsyncJobIds.current.has(jobRecord.jobId)) {
      knownAsyncJobIds.current.add(jobRecord.jobId);
      setActiveAsyncJobCount((count) => count + 1);
    }
  }, [user]);

  const removeActiveLatamCsvJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getLatamCsvJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((job) => job.jobId !== jobId))
    );
    delete latamCsvSseCancelledRef.current[jobId];
    if (knownAsyncJobIds.current.has(jobId)) {
      knownAsyncJobIds.current.delete(jobId);
      setActiveAsyncJobCount((count) => Math.max(0, count - 1));
    }
  }, [user]);

  const saveActiveSequoiaStructureJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getSequoiaStructureJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify([...existing.filter((job) => job.jobId !== jobRecord.jobId), jobRecord])
    );
    if (!knownAsyncJobIds.current.has(jobRecord.jobId)) {
      knownAsyncJobIds.current.add(jobRecord.jobId);
      setActiveAsyncJobCount((count) => count + 1);
    }
  }, [user]);

  const removeActiveSequoiaStructureJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getSequoiaStructureJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((job) => job.jobId !== jobId))
    );
    delete sequoiaStructureSseCancelledRef.current[jobId];
    if (knownAsyncJobIds.current.has(jobId)) {
      knownAsyncJobIds.current.delete(jobId);
      setActiveAsyncJobCount((count) => Math.max(0, count - 1));
    }
  }, [user]);

  const saveActiveTwaJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getTwaJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    const next = [
      ...existing.filter((j) => j.jobId !== jobRecord.jobId),
      jobRecord,
    ];
    localStorage.setItem(storageKey, JSON.stringify(next));
    if (!knownAsyncJobIds.current.has(jobRecord.jobId)) {
      knownAsyncJobIds.current.add(jobRecord.jobId);
      setActiveAsyncJobCount(c => c + 1);
    }
  }, [user]);

  const removeActiveTwaJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getTwaJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((j) => j.jobId !== jobId))
    );
    delete twaSseCancelledRef.current[jobId];
    if (knownAsyncJobIds.current.has(jobId)) {
      knownAsyncJobIds.current.delete(jobId);
      setActiveAsyncJobCount(c => Math.max(0, c - 1));
    }
  }, [user]);

  const saveActiveAviationJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getAviationJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    const next = [
      ...existing.filter((j) => j.jobId !== jobRecord.jobId),
      jobRecord,
    ];
    localStorage.setItem(storageKey, JSON.stringify(next));
    if (!knownAsyncJobIds.current.has(jobRecord.jobId)) {
      knownAsyncJobIds.current.add(jobRecord.jobId);
      setActiveAsyncJobCount(c => c + 1);
    }
  }, [user]);

  const removeActiveAviationJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getAviationJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((j) => j.jobId !== jobId))
    );
    delete aviationSseCancelledRef.current[jobId];
    if (knownAsyncJobIds.current.has(jobId)) {
      knownAsyncJobIds.current.delete(jobId);
      setActiveAsyncJobCount(c => Math.max(0, c - 1));
    }
  }, [user]);

  const saveActiveEnergyJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getEnergyJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    const next = [
      ...existing.filter((j) => j.jobId !== jobRecord.jobId),
      jobRecord,
    ];
    localStorage.setItem(storageKey, JSON.stringify(next));
    if (!knownAsyncJobIds.current.has(jobRecord.jobId)) {
      knownAsyncJobIds.current.add(jobRecord.jobId);
      setActiveAsyncJobCount(c => c + 1);
    }
  }, [user]);

  const removeActiveEnergyJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getEnergyJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((j) => j.jobId !== jobId))
    );
    delete energySseCancelledRef.current[jobId];
    if (knownAsyncJobIds.current.has(jobId)) {
      knownAsyncJobIds.current.delete(jobId);
      setActiveAsyncJobCount(c => Math.max(0, c - 1));
    }
  }, [user]);

  const saveActiveDataCenterJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getDataCenterJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    const next = [
      ...existing.filter((j) => j.jobId !== jobRecord.jobId),
      jobRecord,
    ];
    localStorage.setItem(storageKey, JSON.stringify(next));
    if (!knownAsyncJobIds.current.has(jobRecord.jobId)) {
      knownAsyncJobIds.current.add(jobRecord.jobId);
      setActiveAsyncJobCount(c => c + 1);
    }
  }, [user]);

  const removeActiveDataCenterJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getDataCenterJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((j) => j.jobId !== jobId))
    );
    delete dataCenterSseCancelledRef.current[jobId];
    if (knownAsyncJobIds.current.has(jobId)) {
      knownAsyncJobIds.current.delete(jobId);
      setActiveAsyncJobCount(c => Math.max(0, c - 1));
    }
  }, [user]);

  const saveActiveGamingJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getGamingJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    const next = [
      ...existing.filter((j) => j.jobId !== jobRecord.jobId),
      jobRecord,
    ];
    localStorage.setItem(storageKey, JSON.stringify(next));
    if (!knownAsyncJobIds.current.has(jobRecord.jobId)) {
      knownAsyncJobIds.current.add(jobRecord.jobId);
      setActiveAsyncJobCount(c => c + 1);
    }
  }, [user]);

  const removeActiveGamingJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getGamingJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((j) => j.jobId !== jobId))
    );
    delete gamingSseCancelledRef.current[jobId];
    if (knownAsyncJobIds.current.has(jobId)) {
      knownAsyncJobIds.current.delete(jobId);
      setActiveAsyncJobCount(c => Math.max(0, c - 1));
    }
  }, [user]);

  const saveActiveUsChinaTradeJob = useCallback((jobRecord) => {
    if (!user?.id) return;
    const storageKey = getUsChinaTradeJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    const next = [
      ...existing.filter((j) => j.jobId !== jobRecord.jobId),
      jobRecord,
    ];
    localStorage.setItem(storageKey, JSON.stringify(next));
    if (!knownAsyncJobIds.current.has(jobRecord.jobId)) {
      knownAsyncJobIds.current.add(jobRecord.jobId);
      setActiveAsyncJobCount(c => c + 1);
    }
  }, [user]);

  const removeActiveUsChinaTradeJob = useCallback((jobId) => {
    if (!user?.id) return;
    const storageKey = getUsChinaTradeJobsStorageKey(user.id);
    let existing = [];
    try {
      existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      existing = [];
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify(existing.filter((j) => j.jobId !== jobId))
    );
    delete usChinaTradeSseCancelledRef.current[jobId];
    if (knownAsyncJobIds.current.has(jobId)) {
      knownAsyncJobIds.current.delete(jobId);
      setActiveAsyncJobCount(c => Math.max(0, c - 1));
    }
  }, [user]);

  const handleCopyMessage = useCallback(async (text, messageIdx, reportHtml) => {
    try {
      if (reportHtml && navigator.clipboard?.write) {
        const htmlBlob = new Blob([reportHtml], { type: 'text/html' });
        const textBlob = new Blob([text], { type: 'text/plain' });
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': htmlBlob,
            'text/plain': textBlob,
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      setCopiedMessageIdx(messageIdx);
      setTimeout(() => setCopiedMessageIdx(null), 2000);
      logger.debug('Message copied to clipboard');
    } catch (error) {
      logger.error('Failed to copy message', error);
    }
  }, []);

  const toggleCommunications = useCallback((messageIdx) => {
    setExpandedCommunications(prev => ({
      ...prev,
      [messageIdx]: !prev[messageIdx]
    }));
  }, []);

  const handleImageChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result?.split(',')[1];
        setSelectedImage(base64);
        logger.debug('Image selected', { size: file.size, type: file.type });
      };
      reader.onerror = () => logger.error('Failed to read image file');
      reader.readAsDataURL(file);
    }
  }, []);

  const handlePdfChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result?.split(',')[1];
        setSelectedPdf(base64);
        setSelectedPdfName(file.name);
        logger.debug('PDF selected', { size: file.size, name: file.name });
      };
      reader.onerror = () => logger.error('Failed to read PDF file');
      reader.readAsDataURL(file);
    }
  }, []);

  const handleExcelChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result?.split(',')[1];
        setSelectedExcel(base64);
        setSelectedExcelName(file.name);
        logger.debug('Excel selected', { size: file.size, name: file.name });
      };
      reader.onerror = () => logger.error('Failed to read Excel file');
      reader.readAsDataURL(file);
    }
  }, []);

  const handleVideoChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedVideo(file);
      setSelectedVideoName(file.name);
      logger.debug('Video selected', { size: file.size, name: file.name, type: file.type });
    }
  }, []);

  const createNewConversation = useCallback(() => {
    const newConversation = {
      id: generateId(),
      messages: [],
      createdAt: new Date().toISOString(),
    };
    
    setConversations(prev => [newConversation, ...prev]);
    setActiveConversationId(newConversation.id);
    setMessages([]);
    setIsSidebarOpen(false);
    
    logger.userAction('Create new conversation', { id: newConversation.id });
  }, []);

  const selectConversation = useCallback((conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setActiveConversationId(conversationId);
      setMessages(conversation.messages || []);
      setIsSidebarOpen(false);
      logger.userAction('Select conversation', { id: conversationId });
    }
  }, [conversations]);

  const deleteConversation = useCallback(async (conversationId, event) => {
    event.stopPropagation();
    
    if (token) {
      try {
        await deleteConversationOnServer(token, conversationId);
      } catch (error) {
        logger.error('Failed to delete conversation on server', error);
      }
    }
    
    setConversations(prev => prev.filter(c => c.id !== conversationId));
    
    if (activeConversationId === conversationId) {
      setActiveConversationId(null);
      setMessages([]);
    }
    
    logger.userAction('Delete conversation', { id: conversationId });
  }, [token, activeConversationId]);

  const handleClearAllHistory = useCallback(async () => {
    if (!window.confirm('Are you sure you want to clear all chat history? This action cannot be undone.')) {
      return;
    }
    
    logger.userAction('Clear all history', { email: user?.email });
    
    if (token) {
      try {
        await clearHistoryOnServer(token);
        logger.info('Cleared history on server');
      } catch (error) {
        logger.error('Failed to clear history on server', error);
        alert('Failed to clear history on server. Please try again.');
        return;
      }
    }
    
    setMessages([]);
    setConversations([]);
    setActiveConversationId(null);
    
    if (user?.id) {
      const storageKey = getUserConversationsKey(user.id);
      localStorage.removeItem(storageKey);
      const activeKey = getUserActiveConversationKey(user.id);
      localStorage.removeItem(activeKey);
    }
    
    logger.info('All chat history cleared');
  }, [user, token]);

  const handleSubmit = async (e, onSessionExpired) => {
    e.preventDefault();
    if ((!input.trim() && !selectedImage && !selectedPdf && !selectedExcel && !selectedVideo) || !token) return;

    const userMessage = input.trim();
    const capturedImage = selectedImage;
    const capturedPdf = selectedPdf;
    const capturedPdfName = selectedPdfName;
    const capturedExcel = selectedExcel;
    const capturedExcelName = selectedExcelName;
    const capturedVideo = selectedVideo;
    const capturedVideoName = selectedVideoName;

    const routeContext = {
      has_image: !!capturedImage,
      has_pdf: !!capturedPdf,
      has_excel: !!capturedExcel,
      has_video: !!capturedVideo,
    };

    const handleUiRoute = (route) => {
      if (route.tool_id === 'latam_to_csv' && route.action === 'start_direct') {
        setInput('');
        submitLatamToCsv(route.prefill?.reportText || '', onSessionExpired);
        return true;
      }

      if (route.tool_id === 'sequoia_structure' && route.action === 'start_direct') {
        setInput('');
        submitSequoiaStructure(route.prefill?.reportText || '', onSessionExpired);
        return true;
      }

      if (route.tool_id === 'twa_summary' && route.action === 'start_direct') {
        setInput('');
        submitTwaSummary(
          route.prefill?.urls || [],
          route.prefill?.pastedTexts || [],
          route.prefill?.template || '',
          onSessionExpired,
          capturedPdf,
        );
        return true;
      }

      if (route.tool_id === 'latam_summary' && route.action === 'start_direct') {
        setInput('');
        submitLatamSummary(route.prefill?.urls || []);
        return true;
      }

      if (route.tool_id === 'score_video' && route.action === 'start_direct') {
        setInput('');
        submitVideoScore(route.prefill?.url, capturedVideo, onSessionExpired);
        return true;
      }

      if (route.tool_id === 'gms_report' && route.action === 'start_direct') {
        setInput('');
        submitGmsReport();
        return true;
      }

      if (route.tool_id === 'aviation_report' && route.action === 'start_direct') {
        setInput('');
        submitAviationReport();
        return true;
      }

      if (route.tool_id === 'energy_report' && route.action === 'start_direct') {
        setInput('');
        submitEnergyReport();
        return true;
      }

      if (route.tool_id === 'data_center_report' && route.action === 'start_direct') {
        setInput('');
        submitDataCenterReport(onSessionExpired);
        return true;
      }

      if (route.tool_id === 'gaming_report' && route.action === 'start_direct') {
        setInput('');
        submitGamingReport(onSessionExpired);
        return true;
      }

      if (route.tool_id === 'us_china_trade' && route.action === 'start_direct') {
        setInput('');
        submitUsChinaTradeReport(onSessionExpired);
        return true;
      }

      if (route.action === 'open_ui') {
        setInput('');
        logger.userAction('Open routed tool UI', { tool: route.tool_id, reason: route.reason });

        if (
          route.tool_id === 'protest_city_monitor_config'
          || route.ui_type === 'protest_monitor_modal'
        ) {
          openProtestMonitorConfig(onSessionExpired);
          return true;
        }
        if (route.tool_id === 'protest') {
          openProtestModal(route.prefill || {});
          return true;
        }
        if (route.tool_id === 'summarize_video' || route.tool_id === 'transcribe_video') {
          openVideoModal({
            ...(route.prefill || {}),
            initialFile: capturedVideo || null,
            prompt: route.prefill?.prompt || userMessage,
          }, route.tool_id === 'transcribe_video' ? 'transcribe' : 'summarize');
          setSelectedVideo(null);
          setSelectedVideoName(null);
          if (videoInputRef.current) videoInputRef.current.value = '';
          return true;
        }
        if (route.tool_id === 'deepfake_detection') {
          openDeepfakeModal({
            ...(route.prefill || {}),
            initialFile: capturedVideo || null,
          });
          setSelectedVideo(null);
          setSelectedVideoName(null);
          if (videoInputRef.current) videoInputRef.current.value = '';
          return true;
        }
        if (route.tool_id === 'dictionary_analysis') {
          openDictionaryCanvas(route.prefill?.image_url || null, capturedImage || null);
          setSelectedImage(null);
          setInput('');
          return true;
        }
        if (route.tool_id === 'deep_research') {
          openDeepResearchModal(route.prefill || {});
          return true;
        }
        if (route.tool_id === 'statistical_summary') {
          openStatisticalSummaryModal();
          return true;
        }
        if (route.tool_id === 'infographic') {
          openInfographicModal();
          return true;
        }
        return false;
      }

      if (route.action === 'clarify') {
        setInput('');
        let currentConversationId = activeConversationId;
        if (!currentConversationId) {
          const newConversation = {
            id: generateId(),
            messages: [],
            createdAt: new Date().toISOString(),
          };
          setConversations(prev => [newConversation, ...prev]);
          currentConversationId = newConversation.id;
          setActiveConversationId(newConversation.id);
        }
        const clarifyMessages = [
          ...messages,
          { type: 'user', text: userMessage },
          { type: 'assistant', text: route.reason || 'I need one more input before I can use that tool.' }
        ];
        setMessages(clarifyMessages);
        updateConversationMessages(currentConversationId, clarifyMessages);
        return true;
      }

      return false;
    };

    // Image generation models skip all client-side tool routing — they only
    // generate an image; no web search, video, or other tool makes sense.
    const isImageGenerationModel = currentModel?.imageGeneration === true;

    // clientTools: backend-only tools identified by the client-side heuristic
    // (e.g. argo_guide). Passing them directly skips the backend re-routing step.
    let clientTools = null;
    let clientToolRoutingChecked = false;

    if (!isImageGenerationModel && (userMessage || capturedImage || capturedVideo)) {
      const route = heuristicRouteIntent(userMessage, routeContext);
      if (handleUiRoute(route)) {
        return;
      }
      // For run_chat decisions with pre-determined backend tools, pass them through
      // so the backend skips its own routing and goes straight to tool execution.
      if (route.action === 'run_chat' && route.tools && route.tools.length > 0) {
        clientTools = route.tools;
        clientToolRoutingChecked = true;
      }
    }

    setInput('');

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations(prev => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const newUserMessage = {
      type: 'user',
      text: userMessage,
      image_data: capturedImage,
      pdf_data: capturedPdf,
      pdf_name: capturedPdfName,
      excel_data: capturedExcel,
      excel_name: capturedExcelName,
      video_name: capturedVideoName
    };

    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);
    chatAbortControllerRef.current = new AbortController();
    setIsLoading(true);

    logger.userAction('Send message', {
      model: selectedModel,
      hasImage: !!capturedImage,
      hasPdf: !!capturedPdf,
      hasExcel: !!capturedExcel,
      messageLength: userMessage.length
    });

    try {
      const data = await sendChatMessage(token, {
        message: userMessage,
        model: selectedModel,
        image_data: capturedImage,
        pdf_data: capturedPdf,
        pdf_name: capturedPdfName,
        excel_data: capturedExcel,
        excel_name: capturedExcelName,
        tools: clientTools,
        // Skip backend re-routing when client heuristics already resolved the tools,
        // or when using an image generation model (no tools apply).
        tool_routing_checked: isImageGenerationModel ? true : clientToolRoutingChecked,
        conversation_id: currentConversationId
      }, chatAbortControllerRef.current?.signal);
      
      const pdfTaskJobId = data.pdf_task_job_id || null;
      const messageId = generateId();
      const responseMessage = {
        id: messageId,
        type: 'assistant',
        text: data.response,
        usedWebSearch: data.used_web_search,
        searchQuery: data.search_query,
        searchStatus: data.search_status,
        usedCommunicationSearch: data.used_communication_search,
        communicationSearchStatus: data.communication_search_status,
        communicationSearchError: data.communication_search_error,
        communicationsFound: data.communications_found,
        communications: data.communications,
        usedFaceRecognition: data.used_face_recognition,
        facesFound: data.faces_found,
        faceRecognitionResults: data.face_recognition_results,
        usedPlaceRecognition: data.used_place_recognition,
        placesFound: data.places_found,
        placeRecognitionResults: data.place_recognition_results,
        pdfTaskJobId: pdfTaskJobId || undefined,
        pdfTaskJobStatus: pdfTaskJobId ? 'running' : undefined,
        // Image generation response
        generated_image_data: data.generated_image_data || undefined,
      };
      
      const finalMessages = [...updatedMessages, responseMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
      setSelectedImage(null);
      setSelectedPdf(null);
      setSelectedPdfName(null);
      setSelectedExcel(null);
      setSelectedExcelName(null);
      setSelectedVideo(null);
      setSelectedVideoName(null);

      // If the backend started an async PDF task, subscribe to its SSE stream
      // and fall back to polling when SSE is unavailable.
      if (pdfTaskJobId) {
        // Persist the job in a lightweight localStorage key so it survives
        // a page refresh even if the full conversation can't be stored
        // (large PDFs often exceed the localStorage quota).
        saveActivePdfTaskJob({
          jobId: pdfTaskJobId,
          conversationId: currentConversationId,
          messageId,
          startedAt: Date.now(),
        });

        pdfTaskSseCancelledRef.current[pdfTaskJobId] = false;
        pollPdfTaskJobToMessage(pdfTaskJobId, currentConversationId, messageId, onSessionExpired);

        streamPdfTaskEvents(
          token,
          pdfTaskJobId,
          (event) => {
            if (event.step === 'COMPLETED') {
              pdfTaskSseCancelledRef.current[pdfTaskJobId] = true;
              clearTimeout(pdfTaskPollersRef.current[pdfTaskJobId]);
              delete pdfTaskPollersRef.current[pdfTaskJobId];
              removeActivePdfTaskJob(pdfTaskJobId);
              updatePdfTaskMessage(currentConversationId, messageId, {
                text: event.result_text || 'PDF processing completed but the result was empty. Please try again.',
                pdfTaskJobStatus: event.result_text ? 'ready' : 'failed',
              });
            } else if (event.step === 'FAILED') {
              pdfTaskSseCancelledRef.current[pdfTaskJobId] = true;
              clearTimeout(pdfTaskPollersRef.current[pdfTaskJobId]);
              delete pdfTaskPollersRef.current[pdfTaskJobId];
              removeActivePdfTaskJob(pdfTaskJobId);
              updatePdfTaskMessage(currentConversationId, messageId, {
                text: `PDF processing failed: ${event.message || 'Unknown error'}`,
                pdfTaskJobStatus: 'failed',
              });
            } else if (event.message) {
              updatePdfTaskMessage(currentConversationId, messageId, {
                text: event.message,
                pdfTaskJobStatus: 'running',
              });
            }
          },
          () => pdfTaskSseCancelledRef.current[pdfTaskJobId] === true,
        ).catch(() => {
          // polling remains the fallback if SSE fails
        });
      }
      
      if (imageInputRef.current) imageInputRef.current.value = '';
      if (pdfInputRef.current) pdfInputRef.current.value = '';
      if (excelInputRef.current) excelInputRef.current.value = '';
      if (videoInputRef.current) videoInputRef.current.value = '';
      
      logger.debug('Response received', { 
        usedWebSearch: data.used_web_search,
        responseLength: data.response?.length 
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        // User pressed Stop — rollback is already handled by handleStopChat
        return;
      }

      if (error.message === 'SESSION_EXPIRED') {
        logger.warn('Session expired');
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }

      logger.error('Chat request failed', error);
      
      let errorText = 'Could not connect to server. Please try again.';
      if (error.message) {
        if (error.message.includes('Backend error:')) {
          try {
            const jsonMatch = error.message.match(/\{.*\}/);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[0]);
              errorText = parsed.detail || error.message;
            } else {
              errorText = error.message.replace('Backend error: ', '');
            }
          } catch {
            errorText = error.message;
          }
        } else if (error.message !== 'Failed to fetch') {
          errorText = error.message;
        }
      }
      
      const errorMessage = { 
        type: 'error', 
        text: `Error: ${errorText}` 
      };
      
      const finalMessages = [...updatedMessages, errorMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Note: handleSubmit needs to be called with the event and logout handler from the component
    }
  }, []);

  const clearState = useCallback(() => {
    setMessages([]);
    setConversations([]);
    setActiveConversationId(null);
    setIsProtestMonitorConfigOpen(false);
    setProtestMonitorConfig(null);
    setProtestMonitorConfigError(null);
    setProtestMonitorConfigSuccess('');
  }, []);

  /**
   * Abort the in-flight chat request and roll back the last user message.
   * Called when the user presses the Stop button while a response is loading.
   */
  const handleStopChat = useCallback(() => {
    chatAbortControllerRef.current?.abort();
    setMessages(prev => {
      const lastUserIdx = [...prev].reverse().findIndex(m => m.type === 'user');
      if (lastUserIdx === -1) return prev;
      const trimmed = prev.slice(0, prev.length - 1 - lastUserIdx);
      updateConversationMessages(activeConversationId, trimmed);
      return trimmed;
    });
    setIsLoading(false);
    logger.userAction('Stop chat generation');
  }, [activeConversationId, updateConversationMessages]);

  /**
   * Cancel any in-progress async report/research job (Data Centers, Energy, GMS, Aviation, LATAM, TWA, Deep Research).
   * All these jobs share isDeepResearchLoading as their loading indicator.
   * Stops the SSE reader, clears the poller, calls the cancel API, updates the chat message,
   * and removes the job from localStorage.
   */
  const handleStopAsyncJob = useCallback(async () => {
    if (!user?.id) return;

    const jobTypes = [
      {
        storageKey: getDataCenterJobsStorageKey(user.id),
        sseCancelledRef: dataCenterSseCancelledRef,
        pollersRef: dataCenterPollersRef,
        cancelFn: (jobId) => cancelDataCenterReportJob(token, jobId),
        removeFn: removeActiveDataCenterJob,
      },
      {
        storageKey: getGamingJobsStorageKey(user.id),
        sseCancelledRef: gamingSseCancelledRef,
        pollersRef: gamingPollersRef,
        cancelFn: (jobId) => cancelGamingReportJob(token, jobId),
        removeFn: removeActiveGamingJob,
      },
      {
        storageKey: getUsChinaTradeJobsStorageKey(user.id),
        sseCancelledRef: usChinaTradeSseCancelledRef,
        pollersRef: usChinaTradePollersRef,
        cancelFn: (jobId) => cancelUsChinaTradeReportJob(token, jobId),
        removeFn: removeActiveUsChinaTradeJob,
      },
      {
        storageKey: getEnergyJobsStorageKey(user.id),
        sseCancelledRef: energySseCancelledRef,
        pollersRef: energyPollersRef,
        cancelFn: (jobId) => cancelEnergyReportJob(token, jobId),
        removeFn: removeActiveEnergyJob,
      },
      {
        storageKey: getGmsJobsStorageKey(user.id),
        sseCancelledRef: gmsSseCancelledRef,
        pollersRef: gmsPollersRef,
        cancelFn: (jobId) => cancelGmsReportJob(token, jobId),
        removeFn: removeActiveGmsJob,
      },
      {
        storageKey: getAviationJobsStorageKey(user.id),
        sseCancelledRef: aviationSseCancelledRef,
        pollersRef: aviationPollersRef,
        cancelFn: (jobId) => cancelAviationReportJob(token, jobId),
        removeFn: removeActiveAviationJob,
      },
      {
        storageKey: getLatamJobsStorageKey(user.id),
        sseCancelledRef: latamSseCancelledRef,
        pollersRef: latamPollersRef,
        cancelFn: (jobId) => cancelLatamSummaryJob(token, jobId),
        removeFn: removeActiveLatamJob,
      },
      {
        storageKey: getLatamCsvJobsStorageKey(user.id),
        sseCancelledRef: latamCsvSseCancelledRef,
        pollersRef: latamCsvPollersRef,
        cancelFn: (jobId) => cancelLatamToCsvJob(token, jobId),
        removeFn: removeActiveLatamCsvJob,
      },
      {
        storageKey: getSequoiaStructureJobsStorageKey(user.id),
        sseCancelledRef: sequoiaStructureSseCancelledRef,
        pollersRef: sequoiaStructurePollersRef,
        cancelFn: (jobId) => cancelSequoiaStructureJob(token, jobId),
        removeFn: removeActiveSequoiaStructureJob,
      },
      {
        storageKey: getTwaJobsStorageKey(user.id),
        sseCancelledRef: twaSseCancelledRef,
        pollersRef: twaPollersRef,
        cancelFn: (jobId) => cancelTwaSummaryJob(token, jobId),
        removeFn: removeActiveTwaJob,
      },
      {
        storageKey: getDeepResearchJobsStorageKey(user.id),
        sseCancelledRef: deepResearchSseCancelledRef,
        pollersRef: deepResearchPollersRef,
        cancelFn: (jobId) => cancelDeepResearchJob(token, jobId),
        removeFn: removeActiveDeepResearchJob,
      },
    ];

    let anyCancelled = false;
    for (const jobType of jobTypes) {
      let jobs = [];
      try {
        jobs = JSON.parse(localStorage.getItem(jobType.storageKey) || '[]');
      } catch (_) {
        jobs = [];
      }
      for (const job of jobs) {
        const { jobId, conversationId, messageId } = job;
        // Stop the SSE reader for this job
        jobType.sseCancelledRef.current[jobId] = true;
        // Clear the polling timeout
        if (jobType.pollersRef.current[jobId]) {
          clearTimeout(jobType.pollersRef.current[jobId]);
          delete jobType.pollersRef.current[jobId];
        }
        // Call the backend cancel endpoint (fire-and-forget)
        jobType.cancelFn(jobId).catch(() => {});
        // Update the in-chat message to reflect cancellation
        if (conversationId && messageId) {
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: 'Generation stopped by user.',
            deepResearchJobStatus: 'cancelled',
            deepResearchJobStep: 'CANCELLED',
          });
        }
        // Remove from localStorage
        jobType.removeFn(jobId);
        anyCancelled = true;
      }
    }

    if (anyCancelled) {
      setIsDeepResearchLoading(false);
      setDeepResearchProgress({ message: '', step: '' });
      logger.userAction('Stop async job');
    }
  }, [
    user,
    token,
    updateDeepResearchJobMessage,
    removeActiveDataCenterJob,
    removeActiveUsChinaTradeJob,
    removeActiveEnergyJob,
    removeActiveGmsJob,
    removeActiveAviationJob,
    removeActiveLatamJob,
    removeActiveLatamCsvJob,
    removeActiveTwaJob,
    removeActiveDeepResearchJob,
  ]);

  // ============================================
  // Dictionary Analysis Handlers
  // ============================================

  // Detect dictionary URLs in input
  const checkForDictionaryUrl = useCallback((text) => {
    const matches = text.match(DICTIONARY_URL_PATTERN);
    if (matches && matches.length > 0) {
      setDetectedDictionaryUrl(matches[0]);
      return matches[0];
    }
    setDetectedDictionaryUrl(null);
    return null;
  }, []);

  // Handle input change with URL detection
  const handleInputChange = useCallback((value) => {
    setInput(value);
    checkForDictionaryUrl(value);
  }, [checkForDictionaryUrl]);

  // Start dictionary analysis - continues in background and updates link when complete
  const startDictionaryAnalysis = useCallback(async (imageUrl, imageData = null, poiName = '') => {
    if (!token) {
      logger.error('No token available for dictionary analysis');
      return;
    }

    // Store entry ID in ref for background update (survives state changes)
    analysisEntryIdRef.current = currentCanvasEntryId;
    
    setIsDictionaryLoading(true);
    setDictionaryError(null);
    setDictionaryImageUrl(imageUrl);
    setDictionaryPoiName(poiName);
    setIsDictionaryCanvasOpen(true);

    try {
      const payload = {
        image_url: imageUrl,
        image_data: imageData,
        poi_name: poiName || null
      };

      logger.userAction('Start dictionary analysis', { imageUrl, hasPoi: !!poiName, entryId: currentCanvasEntryId });
      
      const response = await analyzeDictionary(token, payload);
      
      if (response.success) {
        // Initialize edits with LLM results
        const initialEdits = {};
        response.results.forEach(result => {
          initialEdits[result.question_type] = {
            score: result.score,
            reasoning: result.reasoning,
            detected_countries: result.detected_countries,
            confidence: result.confidence
          };
        });
        
        // Update local state (if canvas is still showing this entry)
        setDictionaryResults(response.results);
        setCanvasStatus('analyzed');
        setDictionaryEdits(initialEdits);
        
        logger.info('Dictionary analysis completed', { 
          resultCount: response.results.length 
        });
        
        // Update server and link message (works even if canvas was closed during analysis)
        const savedEntryId = analysisEntryIdRef.current;
        if (savedEntryId && token) {
          try {
            await updateDictionaryCanvasEntry(token, savedEntryId, {
              status: 'analyzed',
              poi_name: poiName || null,
              results: response.results,
              edits: initialEdits
            });
            logger.info('Canvas entry updated after analysis', { entryId: savedEntryId });
            
            // Update link message silently (even if canvas is closed)
            setMessages(prev => {
              const updatedMessages = prev.map(msg => 
                msg.type === 'dictionary_canvas' && msg.entryId === savedEntryId
                  ? { ...msg, status: 'analyzed', poiName: poiName || null, results: response.results, edits: initialEdits }
                  : msg
              );
              
              // Also update conversations
              if (activeConversationId) {
                setConversations(prevConvs => prevConvs.map(conv =>
                  conv.id === activeConversationId
                    ? { ...conv, messages: updatedMessages }
                    : conv
                ));
              }
              
              return updatedMessages;
            });
          } catch (updateError) {
            logger.error('Failed to update canvas entry after analysis', updateError);
          }
        }
      } else {
        throw new Error(response.error || 'Analysis failed');
      }
    } catch (error) {
      logger.error('Dictionary analysis failed', error);
      setDictionaryError(error.message);
    } finally {
      setIsDictionaryLoading(false);
    }
  }, [token, currentCanvasEntryId, activeConversationId]);

  // Update dictionary edit
  const updateDictionaryEdit = useCallback((questionType, field, value) => {
    setDictionaryEdits(prev => ({
      ...prev,
      [questionType]: {
        ...prev[questionType],
        [field]: value
      }
    }));
  }, []);

  // Submit dictionary approval (legacy - all questions at once)
  const submitDictionaryApproval = useCallback(async () => {
    if (!token || !dictionaryEdits || !dictionaryImageUrl) {
      logger.error('Missing data for dictionary approval');
      return false;
    }

    try {
      const payload = {
        image_url: dictionaryImageUrl,
        poi_name: dictionaryPoiName || null,
        express: dictionaryEdits.express || {
          score: 0,
          reasoning: '',
          detected_countries: [],
          confidence: 0
        },
        suspected: dictionaryEdits.suspected || {
          score: 0,
          reasoning: '',
          detected_countries: [],
          confidence: 0
        },
        acknowledge: dictionaryEdits.acknowledge || {
          score: 0,
          reasoning: '',
          detected_countries: [],
          confidence: 0
        }
      };

      logger.userAction('Submit dictionary approval');
      
      const response = await approveDictionaryAnalysis(token, payload);
      
      if (response.success) {
        logger.info('Dictionary approval saved', { approvalId: response.approval_id });
        
        // Update status to approved
        setCanvasStatus('approved');
        
        // Update canvas entry on server if we have an entry ID
        if (currentCanvasEntryId) {
          try {
            await updateDictionaryCanvasEntry(token, currentCanvasEntryId, {
              status: 'approved',
              results: dictionaryResults,
              edits: dictionaryEdits
            });
            
            // Update the canvas link message in the messages array
            setMessages(prev => prev.map(msg => 
              msg.type === 'dictionary_canvas' && msg.entryId === currentCanvasEntryId
                ? { ...msg, status: 'approved', approvedAt: new Date().toISOString(), edits: dictionaryEdits }
                : msg
            ));
            
            // Also update in conversations
            if (activeConversationId) {
              setConversations(prev => prev.map(conv => 
                conv.id === activeConversationId
                  ? {
                      ...conv,
                      messages: conv.messages.map(msg =>
                        msg.type === 'dictionary_canvas' && msg.entryId === currentCanvasEntryId
                          ? { ...msg, status: 'approved', approvedAt: new Date().toISOString(), edits: dictionaryEdits }
                          : msg
                      )
                    }
                  : conv
              ));
            }
          } catch (updateError) {
            logger.error('Failed to update canvas entry status', updateError);
          }
        }
        
        // Close canvas and reset state
        setIsDictionaryCanvasOpen(false);
        setDictionaryResults(null);
        setDictionaryEdits(null);
        setDictionaryImageUrl(null);
        setDictionaryImageData(null);
        setDictionaryPoiName('');
        setDetectedDictionaryUrl(null);
        setCurrentCanvasEntryId(null);
        setCanvasAnchorMessageIdx(null);
        setIsCanvasReadOnly(false);
        setQuestionApprovals({});
        
        return true;
      } else {
        throw new Error(response.message || 'Approval failed');
      }
    } catch (error) {
      logger.error('Dictionary approval failed', error);
      setDictionaryError(error.message);
      return false;
    }
  }, [token, dictionaryEdits, dictionaryImageUrl, dictionaryPoiName, currentCanvasEntryId, dictionaryResults, activeConversationId]);

  // Approve a single question
  const approveQuestion = useCallback(async (questionType) => {
    if (!token || !currentCanvasEntryId || !dictionaryEdits?.[questionType]) {
      logger.error('Missing data for question approval', { questionType });
      return { success: false, error: 'Missing required data' };
    }

    const editData = dictionaryEdits[questionType];

    try {
      const payload = {
        canvas_entry_id: currentCanvasEntryId,
        question_type: questionType,
        score: editData.score || 0,
        reasoning: editData.reasoning || '',
        detected_countries: editData.detected_countries || [],
        confidence: editData.confidence || 0
      };

      logger.userAction('Approve single question', { questionType });
      
      const response = await approveQuestionApi(token, payload);
      
      if (response.success) {
        logger.info('Question approved', { questionType, approvalId: response.approval_id });
        
        // Update local approvals state
        setQuestionApprovals(prev => ({
          ...prev,
          [questionType]: {
            id: response.approval_id,
            approved_at: new Date().toISOString()
          }
        }));
        
        return { success: true, approvalId: response.approval_id };
      } else {
        throw new Error(response.message || 'Approval failed');
      }
    } catch (error) {
      logger.error('Question approval failed', error);
      return { success: false, error: error.message };
    }
  }, [token, currentCanvasEntryId, dictionaryEdits]);

  // Revoke a question approval (unapprove)
  const unapproveQuestion = useCallback(async (questionType) => {
    const approval = questionApprovals[questionType];
    if (!token || !approval?.id) {
      logger.error('No approval to revoke', { questionType });
      return { success: false, error: 'No approval found' };
    }

    try {
      logger.userAction('Revoke question approval', { questionType, approvalId: approval.id });
      
      const response = await revokeQuestionApprovalApi(token, approval.id);
      
      if (response.success) {
        logger.info('Question approval revoked', { questionType });
        
        // Remove from local approvals state
        setQuestionApprovals(prev => {
          const updated = { ...prev };
          delete updated[questionType];
          return updated;
        });
        
        return { success: true };
      } else {
        throw new Error(response.message || 'Revoke failed');
      }
    } catch (error) {
      logger.error('Question revoke failed', error);
      return { success: false, error: error.message };
    }
  }, [token, questionApprovals]);

  // Load question approvals for a canvas entry
  const loadQuestionApprovals = useCallback(async (canvasEntryId) => {
    if (!token || !canvasEntryId) return;

    try {
      const response = await getQuestionApprovals(token, canvasEntryId);
      
      if (response.success && response.approvals) {
        // Convert to our format with id and approved_at
        const approvals = {};
        for (const [qType, data] of Object.entries(response.approvals)) {
          approvals[qType] = {
            id: data.id,
            approved_at: data.approved_at
          };
        }
        setQuestionApprovals(approvals);
        logger.info('Loaded question approvals', { count: Object.keys(approvals).length });
      }
    } catch (error) {
      logger.error('Failed to load question approvals', error);
      // Don't throw - approvals loading failure shouldn't break canvas
    }
  }, [token]);

  // Open dictionary canvas - saves entry and inserts link immediately
  const openDictionaryCanvas = useCallback(async (imageUrl, imageData = null, anchorIdx = null) => {
    // Generate a new entry ID for this canvas session
    const entryId = generateId();
    
    // Create conversation if none exists (canvas-only chat)
    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations(prev => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
      logger.info('Created new conversation for canvas-only chat', { conversationId: currentConversationId });
    }
    
    // Set anchor to current message count (after the user message that triggered this)
    // If no anchor provided, use the current message count
    const anchor = anchorIdx !== null ? anchorIdx : messages.length;
    
    // Set state first
    setCurrentCanvasEntryId(entryId);
    setCanvasAnchorMessageIdx(anchor);
    setDictionaryImageUrl(imageUrl);
    setDictionaryImageData(imageData);
    setDictionaryError(null);
    setDictionaryResults(null);
    setDictionaryEdits(null);
    setCanvasStatus('pending');
    setIsCanvasReadOnly(false);
    setIsCanvasReopened(false); // This is a fresh open, not a reopen
    setQuestionApprovals({}); // Reset per-question approvals
    setIsDictionaryCanvasOpen(true);
    
    logger.userAction('Open dictionary canvas', { imageUrl, hasImageData: !!imageData, entryId, anchorIdx: anchor, conversationId: currentConversationId });
    
    // Save to server and insert link immediately
    if (token) {
      // Create canvas link message
      const canvasLinkMessage = {
        type: 'dictionary_canvas',
        entryId,
        imageUrl,
        poiName: null,
        status: 'pending',
        results: null,
        edits: null,
        anchorMessageIdx: anchor,
        createdAt: new Date().toISOString(),
        approvedAt: null
      };
      
      // Insert link message after the anchor position
      const insertPosition = anchor + 1;
      setMessages(prev => {
        const newMessages = [
          ...prev.slice(0, insertPosition),
          canvasLinkMessage,
          ...prev.slice(insertPosition)
        ];
        // Also update conversation
        updateConversationMessages(currentConversationId, newMessages);
        return newMessages;
      });
      
      // Save to server
      try {
        await saveDictionaryCanvasEntry(token, {
          entry_id: entryId,
          conversation_id: currentConversationId,
          anchor_message_idx: anchor,
          image_url: imageUrl,
          poi_name: null,
          status: 'pending',
          results: null,
          edits: null
        });
        logger.info('Canvas entry saved on open', { entryId });
      } catch (error) {
        logger.error('Failed to save initial canvas entry', error);
        // Don't throw - canvas is already open, entry will be saved on close
      }
    }
  }, [token, activeConversationId, messages.length, updateConversationMessages]);

  // Close dictionary canvas - updates existing entry and link (no new link insertion)
  const closeDictionaryCanvas = useCallback(async () => {
    // Update server and existing link message (link was already inserted on open)
    if (token && currentCanvasEntryId) {
      try {
        // Update entry on server with current state
        await updateDictionaryCanvasEntry(token, currentCanvasEntryId, {
          status: canvasStatus,
          poi_name: dictionaryPoiName || null,
          results: dictionaryResults,
          edits: dictionaryEdits
        });
        logger.info('Canvas entry updated on close', { entryId: currentCanvasEntryId, status: canvasStatus });
        
        // Update existing link message in messages array
        const updatedMessages = messages.map(msg => 
          msg.type === 'dictionary_canvas' && msg.entryId === currentCanvasEntryId
            ? { 
                ...msg, 
                status: canvasStatus, 
                poiName: dictionaryPoiName || null, 
                results: dictionaryResults, 
                edits: dictionaryEdits 
              }
            : msg
        );
        
        setMessages(updatedMessages);
        
        // Also update conversation
        if (activeConversationId) {
          updateConversationMessages(activeConversationId, updatedMessages);
        }
        
        logger.info('Canvas link updated', { entryId: currentCanvasEntryId });
      } catch (error) {
        logger.error('Failed to update canvas entry on close', error);
        // Still close the canvas even if update failed
      }
    }
    
    // Reset canvas state
    setIsDictionaryCanvasOpen(false);
    setDictionaryResults(null);
    setDictionaryEdits(null);
    setDictionaryImageUrl(null);
    setDictionaryImageData(null);
    setDictionaryPoiName('');
    setDictionaryError(null);
    setCurrentCanvasEntryId(null);
    setCanvasAnchorMessageIdx(null);
    setCanvasStatus('pending');
    setIsCanvasReadOnly(false);
    setIsCanvasReopened(false);
    setQuestionApprovals({});
  }, [token, activeConversationId, currentCanvasEntryId, dictionaryPoiName, 
      canvasStatus, dictionaryResults, dictionaryEdits, messages, updateConversationMessages]);

  // Reopen dictionary canvas from a saved link
  const reopenDictionaryCanvas = useCallback(async (canvasMessage) => {
    if (!canvasMessage || canvasMessage.type !== 'dictionary_canvas') {
      logger.error('Invalid canvas message for reopen');
      return;
    }
    
    // Restore canvas state from message
    setCurrentCanvasEntryId(canvasMessage.entryId);
    setDictionaryImageUrl(canvasMessage.imageUrl);
    setDictionaryPoiName(canvasMessage.poiName || '');
    setDictionaryResults(canvasMessage.results);
    setDictionaryEdits(canvasMessage.edits);
    setCanvasStatus(canvasMessage.status);
    setCanvasAnchorMessageIdx(canvasMessage.anchorMessageIdx);
    setDictionaryError(null);
    setDictionaryImageData(null);
    setQuestionApprovals({}); // Reset first, will be loaded below
    
    // Set read-only mode if approved (legacy - all questions approved together)
    // Note: With per-question approval, individual questions manage their own read-only state
    const isReadOnly = canvasMessage.status === 'approved';
    setIsCanvasReadOnly(isReadOnly);
    
    // Mark as reopened so close will update instead of inserting new link
    setIsCanvasReopened(true);
    
    setIsDictionaryCanvasOpen(true);
    logger.userAction('Reopen canvas from link', { 
      entryId: canvasMessage.entryId, 
      status: canvasMessage.status,
      isReadOnly,
      isReopened: true
    });
    
    // Load existing question approvals from server
    if (token && canvasMessage.entryId) {
      loadQuestionApprovals(canvasMessage.entryId);
    }
  }, [token, loadQuestionApprovals]);

  // ============================================
  // Protest Query Handlers
  // ============================================

  const pollProtestJobToMessage = useCallback(async ({
    jobId,
    conversationId,
    messageId,
    queryTitle,
    startedAt = Date.now(),
    onSessionExpired,
  }) => {
    if (!token || activeProtestPollsRef.current.has(jobId)) return;

    activeProtestPollsRef.current.add(jobId);

    try {
      while (true) {
        const st = await getProtestReportStatus(token, jobId);

        if (st.status === 'ready' && st.success) {
          const stats = mapProtestStats(st.stats);
          updateProtestJobMessage(conversationId, messageId, {
            type: 'assistant',
            text: appendZeroEventDiagnostics(st.response, st.stats),
            usedProtestSearch: true,
            protestQueryTitle: queryTitle,
            protestStats: stats,
            summarizedByOllama: false,
            protestJobStatus: 'ready',
          });
          removeActiveProtestJob(jobId);
          return;
        }

        if (st.status === 'failed') {
          updateProtestJobMessage(conversationId, messageId, {
            type: 'error',
            text: `Error: ${st.detail || st.error?.detail || 'Report generation failed'}`,
            protestJobStatus: 'failed',
            protestJobPhase: st.stage || st.phase,
            protestUpstreamJobId: st.upstream_job_id,
          });
          removeActiveProtestJob(jobId);
          return;
        }

        const elapsedMs = Date.now() - startedAt;
        const statusText = getProtestPhaseText(st);
        const shouldPause = elapsedMs >= PROTEST_REPORT_MAX_WAIT_MS;
        updateProtestJobMessage(conversationId, messageId, {
          text: shouldPause
            ? `${statusText}\n\nThis report is still running on the server. You can refresh or come back later; the app will check this job again while it is active.`
            : `${statusText}\n\nJob ID: ${jobId}${st.upstream_job_id ? `\nUpstream Job ID: ${st.upstream_job_id}` : ''}`,
          protestJobStatus: 'pending',
          protestJobPhase: st.stage || st.phase,
          protestUpstreamJobId: st.upstream_job_id,
        });

        saveActiveProtestJob({
          jobId,
          conversationId,
          messageId,
          queryTitle,
          startedAt,
          upstreamJobId: st.upstream_job_id,
        });

        if (shouldPause) {
          return;
        }

        await new Promise((resolve) =>
          setTimeout(resolve, PROTEST_REPORT_POLL_INTERVAL_MS)
        );
      }
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        logger.warn('Session expired while polling protest job');
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }

      if (error.isDefinitiveNotFound || error.status === 404) {
        logger.warn('Protest job is unknown or expired', { jobId });
        removeActiveProtestJob(jobId);
        updateProtestJobMessage(conversationId, messageId, {
          type: 'error',
          text: 'This report job is no longer available. It may have expired or been removed, so it cannot be resumed.',
          protestJobStatus: 'expired',
          protestJobPhase: 'failed',
        });
        return;
      }

      logger.error('Protest job polling failed', error);
      updateProtestJobMessage(conversationId, messageId, {
        text: `Report status check hit a temporary error: ${error.message || 'Unknown error'}\n\nThe job is still saved. Refresh or try again later to resume checking.`,
        protestJobStatus: 'pending',
        protestJobPhase: 'checking_report',
      });
    } finally {
      activeProtestPollsRef.current.delete(jobId);
    }
  }, [
    token,
    updateProtestJobMessage,
    removeActiveProtestJob,
    saveActiveProtestJob,
  ]);

  useEffect(() => {
    if (!token || !user?.id || conversations.length === 0) return;
    if (protestResumeCheckedRef.current === user.id) return;
    protestResumeCheckedRef.current = user.id;

    const resumeJobs = async () => {
      const storageKey = getProtestJobsStorageKey(user.id);
      let savedJobs = [];
      try {
        savedJobs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (_) {
        savedJobs = [];
      }

      let backendJobs = [];
      let loadedBackendJobs = false;
      try {
        backendJobs = await getActiveProtestReportJobs(token);
        loadedBackendJobs = true;
      } catch (error) {
        logger.error('Failed to load active protest jobs', error);
        backendJobs = [];
      }

      const backendJobIds = new Set(backendJobs.map((j) => j.job_id));
      const backendJobById = new Map(backendJobs.map((j) => [j.job_id, j]));
      // The active endpoint intentionally omits completed jobs. Poll every saved
      // job once so ready/failed results can be recovered and only prune a job
      // after its status endpoint returns a definitive 404.
      const knownJobs = loadedBackendJobs ? savedJobs : [];

      for (const job of knownJobs) {
        if (activeProtestPollsRef.current.has(job.jobId)) {
          continue;
        }

        const backendJob = backendJobById.get(job.jobId);
        let conversationId = job.conversationId;
        let conversation = conversations.find((conv) => conv.id === conversationId);
        if (!conversation) {
          conversation = conversations.find((conv) => conv.id === activeConversationId) || conversations[0];
          conversationId = conversation?.id;
        }
        if (!conversationId) continue;

        let messageId = job.messageId;
        const hasPlaceholder = (conversation?.messages || []).some((msg) => msg.id === messageId);
        if (!hasPlaceholder) {
          messageId = generateId();
          const queryTitle = job.queryTitle || 'Resumed Protest Report';
          const placeholder = {
            id: messageId,
            type: 'assistant',
            text: `${getProtestPhaseText(backendJob)}\n\nResumed background report job.\nJob ID: ${job.jobId}`,
            usedProtestSearch: true,
            protestQueryTitle: queryTitle,
            protestJobId: job.jobId,
            protestUpstreamJobId: backendJob?.upstream_job_id || job.upstreamJobId,
            protestJobStatus: 'pending',
            protestJobPhase: backendJob?.stage || backendJob?.phase,
            summarizedByOllama: false,
          };

          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversationId
                ? { ...conv, messages: [...(conv.messages || []), placeholder] }
                : conv
            )
          );
          if (conversationId === activeConversationId) {
            setMessages((prev) => [...prev, placeholder]);
          }
        }

        const record = {
          ...job,
          conversationId,
          messageId,
          queryTitle: job.queryTitle || 'Resumed Protest Report',
          upstreamJobId: backendJob?.upstream_job_id || job.upstreamJobId,
        };
        saveActiveProtestJob(record);
        pollProtestJobToMessage(record);
      }

      for (const backendJob of backendJobs) {
        const jobId = backendJob.job_id;
        if (savedJobs.some((j) => j.jobId === jobId) || activeProtestPollsRef.current.has(jobId)) {
          continue;
        }

        const conversationId = activeConversationId || conversations[0]?.id;
        if (!conversationId) continue;

        const messageId = generateId();
        const queryTitle = 'Resumed Protest Report';
        const placeholder = {
          id: messageId,
          type: 'assistant',
          text: `${getProtestPhaseText(backendJob)}\n\nResumed background report job.\nJob ID: ${jobId}${backendJob.upstream_job_id ? `\nUpstream Job ID: ${backendJob.upstream_job_id}` : ''}`,
          usedProtestSearch: true,
          protestQueryTitle: queryTitle,
          protestJobId: jobId,
          protestUpstreamJobId: backendJob.upstream_job_id,
          protestJobStatus: 'pending',
          protestJobPhase: backendJob.stage || backendJob.phase,
          summarizedByOllama: false,
        };

        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId
              ? { ...conv, messages: [...(conv.messages || []), placeholder] }
              : conv
          )
        );
        if (conversationId === activeConversationId) {
          setMessages((prev) => [...prev, placeholder]);
        }

        const record = {
          jobId,
          conversationId,
          messageId,
          queryTitle,
          upstreamJobId: backendJob.upstream_job_id,
          startedAt: backendJob.started_at ? backendJob.started_at * 1000 : Date.now(),
        };
        saveActiveProtestJob(record);
        pollProtestJobToMessage(record);
      }
    };

    resumeJobs();
  }, [
    token,
    user,
    conversations,
    activeConversationId,
    pollProtestJobToMessage,
    saveActiveProtestJob,
  ]);

  // Open protest modal
  const openProtestModal = useCallback((initialValues = null) => {
    setProtestInitialValues(initialValues);
    setIsProtestModalOpen(true);
    logger.userAction('Open protest query modal', { routed: !!initialValues });
  }, []);

  // Close protest modal
  const closeProtestModal = useCallback(() => {
    setIsProtestModalOpen(false);
    setProtestInitialValues(null);
  }, []);

  const openProtestMonitorConfig = useCallback(async (onSessionExpired) => {
    setIsProtestMonitorConfigOpen(true);
    setIsProtestMonitorConfigLoading(true);
    setProtestMonitorConfig(null);
    setProtestMonitorConfigError(null);
    setProtestMonitorConfigSuccess('');
    logger.userAction('Open protest monitor configuration');

    try {
      const data = await getProtestMonitorTargets(token);
      setProtestMonitorConfig(data);
    } catch (error) {
      setProtestMonitorConfigError(error);
      if (error.code === 'SESSION_EXPIRED' || error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
      }
    } finally {
      setIsProtestMonitorConfigLoading(false);
    }
  }, [token]);

  const closeProtestMonitorConfig = useCallback(() => {
    if (isProtestMonitorConfigSaving) return;
    setIsProtestMonitorConfigOpen(false);
    setProtestMonitorConfig(null);
    setProtestMonitorConfigError(null);
    setProtestMonitorConfigSuccess('');
  }, [isProtestMonitorConfigSaving]);

  const saveProtestMonitorConfig = useCallback(async ({ targets, recipients }, onSessionExpired) => {
    if (!protestMonitorConfig) return;
    setIsProtestMonitorConfigSaving(true);
    setProtestMonitorConfigError(null);
    setProtestMonitorConfigSuccess('');

    try {
      const data = await updateProtestMonitorTargets(
        token,
        targets,
        protestMonitorConfig.version,
        recipients
      );
      setProtestMonitorConfig({
        ...protestMonitorConfig,
        ...data,
        targets: data.targets || targets,
        recipients: data.recipients || recipients,
      });
      setProtestMonitorConfigSuccess('Configuration saved. Changes will apply to the next daily run.');
      logger.userAction('Save protest monitor configuration', {
        targetCount: targets.length,
        recipientCount: recipients.length,
      });
    } catch (error) {
      setProtestMonitorConfigError(error);
      if (error.code === 'SESSION_EXPIRED' || error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
      }
    } finally {
      setIsProtestMonitorConfigSaving(false);
    }
  }, [protestMonitorConfig, token]);

  // Submit protest query — backend uses company Protest Report API (daily XLSX pipeline)
  const submitProtestQuery = useCallback(async (
    queryTitle,
    startDate,
    endDate,
    location,
    keywords,
    region,
    locationScope = 'city',
    onSessionExpired
  ) => {
    if (!startDate || !endDate) return;

    setIsProtestLoading(true);
    setProtestProgress({ current: 0, total: 0, phase: 'searching' });
    
    // Create or use existing conversation
    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations(prev => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    // Format dates for display
    const startFormatted = new Date(startDate).toLocaleDateString('en-US', { 
      month: 'long', day: 'numeric', year: 'numeric'
    });
    const endFormatted = new Date(endDate).toLocaleDateString('en-US', { 
      month: 'long', day: 'numeric', year: 'numeric'
    });

    // Add user message showing the query with correct keywords
    const keywordsList = keywords ? keywords.join(', ') : 'events';
    const regionLine = region ? `\nRegion: ${region}` : '';
    const scopeLabel = locationScope === 'country' ? 'Country' : 'City';
    const userMessage = {
      type: 'user',
      text: `[Search Intelligence: ${queryTitle}]\n\nSearching for: ${keywordsList}\nLocation scope: ${scopeLabel}\n${scopeLabel}: ${location || 'Any city'}${regionLine}\nPeriod: ${startFormatted} to ${endFormatted}`
    };
    
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    logger.userAction('Submit protest query', {
      queryTitle,
      startDate,
      endDate,
      location,
      locationScope,
      keywords,
      region,
    });

    try {
      setProtestProgress({ current: 0, total: 0, phase: 'generating' });
      const { jobId, upstreamJobId, message } = await startProtestReportJob(token, {
        startDate,
        endDate,
        location,
        locationScope,
        keywords,
        region: region || null,
        conversationId: currentConversationId,
        message: userMessage.text,
      });

      const startedAt = Date.now();
      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: `${message || 'Report generation job accepted.'}\n\nThis can take a long time. You can keep using the chat while I check the upstream job status.\nJob ID: ${jobId}${upstreamJobId ? `\nUpstream Job ID: ${upstreamJobId}` : ''}`,
        usedProtestSearch: true,
        protestQueryTitle: queryTitle,
        protestJobId: jobId,
        protestUpstreamJobId: upstreamJobId,
        protestJobStatus: 'pending',
        protestJobPhase: 'post_in_progress',
        summarizedByOllama: false
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        queryTitle,
        locationScope,
        startedAt,
        upstreamJobId,
        onSessionExpired,
      };
      saveActiveProtestJob(jobRecord);
      pollProtestJobToMessage(jobRecord);
      
      // Close modal once the job is safely running in the background.
      setIsProtestModalOpen(false);
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        logger.warn('Session expired while starting protest job');
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }

      logger.error('Protest query failed', error);
      
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to execute search query. Please try again.'}`
      };
      
      const finalMessages = [...updatedMessages, errorMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
    } finally {
      setIsProtestLoading(false);
      setProtestProgress({ current: 0, total: 0, phase: 'searching' });
    }
  }, [
    activeConversationId,
    messages,
    updateConversationMessages,
    token,
    saveActiveProtestJob,
    pollProtestJobToMessage,
  ]);

  // ============================================
  // Video Summarize Handlers
  // ============================================

  // Open video summarize modal
  const openVideoModal = useCallback((initialValues = null, mode = 'summarize') => {
    setVideoInitialValues(initialValues);
    setVideoMode(mode || 'summarize');
    setIsVideoModalOpen(true);
    logger.userAction('Open video modal', { mode: mode || 'summarize', routed: !!initialValues });
  }, []);

  // Close video summarize modal
  const closeVideoModal = useCallback(() => {
    setIsVideoModalOpen(false);
    setVideoInitialValues(null);
    setVideoMode('summarize');
  }, []);

  // Submit video for summarization
  const submitVideoSummarize = useCallback(async (file, userPrompt) => {
    if (!file || !token) return;

    setIsVideoLoading(true);

    // Create or use existing conversation
    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations(prev => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    // Add user message
    const displayPrompt = userPrompt
      ? `[Video Analysis] ${userPrompt} (video: ${file.name})`
      : `[Video Analysis] Summarizing uploaded video: ${file.name}`;
    const userMessage = {
      type: 'user',
      text: displayPrompt
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    logger.userAction('Submit video for summarization', { fileName: file.name, fileSize: file.size, userPrompt });

    try {
      const response = await summarizeVideo(token, file, currentConversationId, userPrompt);

      const assistantMessage = {
        type: 'assistant',
        text: response.summary,
        usedVideoSummarize: true,
        videoFileName: file.name,
        videoMetadata: response.metadata,
        transcriptEnglish: response.transcript_english ?? null,
        transcriptOriginal: response.transcript_original ?? null,
        detectedLanguage: response.detected_language ?? null,
        translationApplied: response.translation_applied ?? false,
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      logger.info('Video summarization completed', {
        fileName: file.name,
        metadata: response.metadata
      });

      // Close modal on success
      setIsVideoModalOpen(false);
      setVideoInitialValues(null);
      setVideoMode('summarize');
    } catch (error) {
      logger.error('Video summarization failed', error);

      let errorText = 'Failed to summarize video. Please try again.';
      if (error.message) {
        if (error.message.includes('CONTEXT_TOO_LARGE')) {
          errorText = 'The video is too long and exceeds the model context window. Please try a shorter video or reduce quality.';
        } else if (error.message.includes('Backend error:')) {
          try {
            const jsonMatch = error.message.match(/\{.*\}/);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[0]);
              errorText = parsed.detail || error.message;
            } else {
              errorText = error.message.replace('Backend error: ', '');
            }
          } catch {
            errorText = error.message;
          }
        } else if (error.message !== 'Failed to fetch') {
          errorText = error.message;
        }
      }

      const errorMessage = {
        type: 'error',
        text: `Error: ${errorText}`
      };

      const finalMessages = [...updatedMessages, errorMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
    } finally {
      setIsVideoLoading(false);
    }
  }, [activeConversationId, messages, updateConversationMessages, token]);

  // Submit video URL for summarization
  const submitVideoSummarizeUrlHandler = useCallback(async (url, userPrompt) => {
    if (!url || !token) return;

    setIsVideoLoading(true);

    // Create or use existing conversation
    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations(prev => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    // Add user message
    const displayPrompt = userPrompt
      ? `[Video Analysis] ${userPrompt} (URL: ${url})`
      : `[Video Analysis] Summarizing video from URL: ${url}`;
    const userMessage = {
      type: 'user',
      text: displayPrompt
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    logger.userAction('Submit video URL for summarization', { url, userPrompt });

    try {
      const response = await summarizeVideoUrl(token, url, currentConversationId, userPrompt);

      const assistantMessage = {
        type: 'assistant',
        text: response.summary,
        usedVideoSummarize: true,
        videoFileName: response.metadata?.title || url,
        videoMetadata: response.metadata,
        transcriptEnglish: response.transcript_english ?? null,
        transcriptOriginal: response.transcript_original ?? null,
        detectedLanguage: response.detected_language ?? null,
        translationApplied: response.translation_applied ?? false,
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      logger.info('Video URL summarization completed', {
        url,
        metadata: response.metadata
      });

      // Close modal on success
      setIsVideoModalOpen(false);
      setVideoInitialValues(null);
      setVideoMode('summarize');
    } catch (error) {
      logger.error('Video URL summarization failed', error);

      let errorText = 'Failed to summarize video. Please try again.';
      if (error.message) {
        if (error.message.includes('CONTEXT_TOO_LARGE')) {
          errorText = 'The video is too long and exceeds the model context window. Please try a shorter video.';
        } else if (error.message.includes('Backend error:')) {
          try {
            const jsonMatch = error.message.match(/\{.*\}/);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[0]);
              errorText = parsed.detail || error.message;
            } else {
              errorText = error.message.replace('Backend error: ', '');
            }
          } catch {
            errorText = error.message;
          }
        } else if (error.message !== 'Failed to fetch') {
          errorText = error.message;
        }
      }

      const errorMessage = {
        type: 'error',
        text: `Error: ${errorText}`
      };

      const finalMessages = [...updatedMessages, errorMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
    } finally {
      setIsVideoLoading(false);
    }
  }, [activeConversationId, messages, updateConversationMessages, token]);

  const submitVideoScore = useCallback(async (url, file, onSessionExpired) => {
    if (!token || (!url && !file)) return;

    setIsLoading(true);
    setIsVideoScoreLoading(true);

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((prev) => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const displayPrompt = url
      ? `[Video Dictionary Score] Scoring video: ${url}`
      : `[Video Dictionary Score] Scoring uploaded video: ${file.name}`;
    const userMessage = { type: 'user', text: displayPrompt };
    const progressMessage = {
      type: 'assistant',
      text: 'Downloading the video, transcribing speech, summarizing content, scoring against Carinae dictionaries, and generating explanations.',
      usedVideoScore: true,
      videoScoreInProgress: true,
    };

    const updatedMessages = [...messages, userMessage, progressMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    logger.userAction('Submit video for dictionary scoring', { url, fileName: file?.name });

    try {
      const response = url
        ? await scoreVideoUrl(token, url, currentConversationId)
        : await scoreVideoFile(token, file, currentConversationId);

      const assistantMessage = {
        type: 'assistant',
        text: response.response_text,
        usedVideoScore: true,
        videoFileName: response.metadata?.title || url || file?.name,
        videoMetadata: response.metadata,
        videoScore: response.score,
        matchedDictionaries: response.matched_dictionaries,
        transcriptEnglish: response.transcript_english ?? null,
        transcriptOriginal: response.transcript_original ?? null,
        detectedLanguage: response.detected_language ?? null,
        translationApplied: response.translation_applied ?? false,
      };

      setMessages((prev) => {
        const finalMessages = [
          ...prev.filter((message) => !message.videoScoreInProgress),
          assistantMessage,
        ];
        updateConversationMessages(currentConversationId, finalMessages);
        return finalMessages;
      });

      logger.info('Video dictionary scoring completed', {
        url,
        score: response.score,
        matchedCount: response.matched_dictionaries?.length ?? 0,
      });
    } catch (error) {
      logger.error('Video dictionary scoring failed', error);

      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }

      let errorText = 'Failed to score video. Please try again.';
      if (error.message) {
        if (error.message.includes('CONTEXT_TOO_LARGE')) {
          errorText = 'The video is too long and exceeds the model context window. Please try a shorter video.';
        } else if (error.message.includes('Backend error:')) {
          try {
            const jsonMatch = error.message.match(/\{.*\}/);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[0]);
              errorText = parsed.detail || error.message;
            } else {
              errorText = error.message.replace('Backend error: ', '');
            }
          } catch {
            errorText = error.message;
          }
        } else if (error.message !== 'Failed to fetch') {
          errorText = error.message;
        }
      }

      const errorMessage = { type: 'error', text: `Error: ${errorText}` };
      setMessages((prev) => {
        const finalMessages = [
          ...prev.filter((message) => !message.videoScoreInProgress),
          errorMessage,
        ];
        updateConversationMessages(currentConversationId, finalMessages);
        return finalMessages;
      });
    } finally {
      setIsLoading(false);
      setIsVideoScoreLoading(false);
    }
  }, [activeConversationId, messages, updateConversationMessages, token]);

  // Submit video file for transcription only
  const submitVideoTranscribe = useCallback(async (file, sourceLanguage = null) => {
    if (!file || !token) return;

    setIsVideoLoading(true);

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations(prev => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = {
      type: 'user',
      text: `[Video Transcription] Transcribing uploaded video: ${file.name}`
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const response = await transcribeVideo(token, file, currentConversationId, sourceLanguage);
      const assistantMessage = {
        type: 'assistant',
        text: response.transcript,
        usedVideoTranscription: true,
        videoFileName: file.name,
        videoMetadata: response.metadata,
        transcriptEnglish: response.transcript_english ?? null,
        transcriptOriginal: response.transcript_original ?? null,
        detectedLanguage: response.detected_language ?? null,
        translationApplied: response.translation_applied ?? false,
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
      setIsVideoModalOpen(false);
      setVideoInitialValues(null);
      setVideoMode('summarize');
    } catch (error) {
      logger.error('Video transcription failed', error);
      const finalMessages = [
        ...updatedMessages,
        { type: 'error', text: `Error: ${error.message || 'Failed to transcribe video. Please try again.'}` }
      ];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
    } finally {
      setIsVideoLoading(false);
    }
  }, [activeConversationId, messages, updateConversationMessages, token]);

  // Submit video URL for transcription only
  const submitVideoTranscribeUrlHandler = useCallback(async (url, sourceLanguage = null) => {
    if (!url || !token) return;

    setIsVideoLoading(true);

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations(prev => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = {
      type: 'user',
      text: `[Video Transcription] Transcribing video from URL: ${url}`
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const response = await transcribeVideoUrl(token, url, currentConversationId, sourceLanguage);
      const assistantMessage = {
        type: 'assistant',
        text: response.transcript,
        usedVideoTranscription: true,
        videoFileName: response.metadata?.title || url,
        videoMetadata: response.metadata,
        transcriptEnglish: response.transcript_english ?? null,
        transcriptOriginal: response.transcript_original ?? null,
        detectedLanguage: response.detected_language ?? null,
        translationApplied: response.translation_applied ?? false,
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
      setIsVideoModalOpen(false);
      setVideoInitialValues(null);
      setVideoMode('summarize');
    } catch (error) {
      logger.error('Video URL transcription failed', error);
      const finalMessages = [
        ...updatedMessages,
        { type: 'error', text: `Error: ${error.message || 'Failed to transcribe video URL. Please try again.'}` }
      ];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
    } finally {
      setIsVideoLoading(false);
    }
  }, [activeConversationId, messages, updateConversationMessages, token]);

  // Open deepfake detection modal
  const openDeepfakeModal = useCallback((initialValues = null) => {
    setDeepfakeInitialValues(initialValues);
    setIsDeepfakeModalOpen(true);
    logger.userAction('Open deepfake detection modal', { routed: !!initialValues });
  }, []);

  // Close deepfake detection modal
  const closeDeepfakeModal = useCallback(() => {
    setIsDeepfakeModalOpen(false);
    setDeepfakeInitialValues(null);
  }, []);

  // Submit file (video or image) for deepfake analysis
  const submitDeepfakeFile = useCallback(async (file) => {
    if (!file || !token) return;

    setIsDeepfakeLoading(true);

    const isImage = config.deepfakeAllowedImageFormats.some(
      fmt => file.name.toLowerCase().endsWith(fmt)
    );
    const mediaLabel = isImage ? 'image' : 'video';

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations(prev => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = {
      type: 'user',
      text: `[Deepfake Detection] Analyzing uploaded ${mediaLabel}: ${file.name}`
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    logger.userAction(`Submit ${mediaLabel} for deepfake analysis`, { fileName: file.name, fileSize: file.size });

    try {
      const response = await analyzeDeepfake(token, file, currentConversationId);

      const assistantMessage = {
        type: 'assistant',
        text: response.report,
        usedDeepfakeDetection: true,
        deepfakeVerdict: response.verdict,
        deepfakeConfidence: response.confidence,
        deepfakeFrameCount: response.frame_count,
        deepfakeFlaggedCount: response.deepfake_frame_count,
        deepfakeFileName: file.name,
        deepfakeMetadata: response.metadata
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      logger.info('Deepfake file analysis completed', { fileName: file.name, verdict: response.verdict });

      setIsDeepfakeModalOpen(false);
    } catch (error) {
      logger.error('Deepfake file analysis failed', error);

      let errorText = 'Failed to analyze file for deepfakes. Please try again.';
      if (error.message) {
        if (error.message.includes('SESSION_EXPIRED')) {
          errorText = 'Your session has expired. Please log in again.';
        } else if (error.message.includes('CONTEXT_TOO_LARGE')) {
          errorText = 'The file is too large. Please try a smaller file.';
        } else if (error.message.includes('Backend error:')) {
          try {
            const jsonMatch = error.message.match(/\{.*\}/);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[0]);
              errorText = parsed.detail || error.message;
            } else {
              errorText = error.message.replace('Backend error: ', '');
            }
          } catch {
            errorText = error.message;
          }
        } else if (error.message !== 'Failed to fetch') {
          errorText = error.message;
        }
      }

      const errorMessage = { type: 'error', text: `Error: ${errorText}` };
      const finalMessages = [...updatedMessages, errorMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
    } finally {
      setIsDeepfakeLoading(false);
    }
  }, [activeConversationId, messages, updateConversationMessages, token]);

  // Submit URL for deepfake analysis (video or image)
  const submitDeepfakeUrlHandler = useCallback(async (url) => {
    if (!url || !token) return;

    setIsDeepfakeLoading(true);

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations(prev => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = {
      type: 'user',
      text: `[Deepfake Detection] Analyzing media from URL: ${url}`
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    logger.userAction('Submit URL for deepfake analysis', { url });

    try {
      const response = await analyzeDeepfakeUrl(token, url, currentConversationId);

      const assistantMessage = {
        type: 'assistant',
        text: response.report,
        usedDeepfakeDetection: true,
        deepfakeVerdict: response.verdict,
        deepfakeConfidence: response.confidence,
        deepfakeFrameCount: response.frame_count,
        deepfakeFlaggedCount: response.deepfake_frame_count,
        deepfakeFileName: response.metadata?.title || url,
        deepfakeMetadata: response.metadata
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      logger.info('Deepfake URL analysis completed', { url, verdict: response.verdict });

      setIsDeepfakeModalOpen(false);
    } catch (error) {
      logger.error('Deepfake URL analysis failed', error);

      let errorText = 'Failed to analyze media for deepfakes. Please try again.';
      if (error.message) {
        if (error.message.includes('SESSION_EXPIRED')) {
          errorText = 'Your session has expired. Please log in again.';
        } else if (error.message.includes('CONTEXT_TOO_LARGE')) {
          errorText = 'The file is too large. Please try a smaller file.';
        } else if (error.message.includes('Backend error:')) {
          try {
            const jsonMatch = error.message.match(/\{.*\}/);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[0]);
              errorText = parsed.detail || error.message;
            } else {
              errorText = error.message.replace('Backend error: ', '');
            }
          } catch {
            errorText = error.message;
          }
        } else if (error.message !== 'Failed to fetch') {
          errorText = error.message;
        }
      }

      const errorMessage = { type: 'error', text: `Error: ${errorText}` };
      const finalMessages = [...updatedMessages, errorMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
    } finally {
      setIsDeepfakeLoading(false);
    }
  }, [activeConversationId, messages, updateConversationMessages, token]);

  const openDeepResearchModal = useCallback((initialValues = null) => {
    setDeepResearchInitialValues(initialValues);
    setIsDeepResearchModalOpen(true);
    logger.userAction('Open deep research modal', { routed: !!initialValues });
  }, []);

  const closeDeepResearchModal = useCallback(() => {
    setIsDeepResearchModalOpen(false);
    setDeepResearchInitialValues(null);
    setDeepResearchProgress({ message: '', step: '' });
  }, []);

  // ── Statistical Summary modal handlers ─────────────────────────────────────

  const openStatisticalSummaryModal = useCallback(() => {
    setIsStatisticalSummaryModalOpen(true);
    logger.userAction('Open statistical summary modal');
  }, []);

  const closeStatisticalSummaryModal = useCallback(() => {
    setIsStatisticalSummaryModalOpen(false);
  }, []);

  const submitStatisticalSummary = useCallback(
    async ({ currentFile, previousFile }, onSessionExpired) => {
      if (!currentFile) return;
      setIsStatisticalSummaryLoading(true);

      // Ensure there is an active conversation to post the result into
      let convId = activeConversationId;
      if (!convId) {
        const newConv = { id: generateId(), messages: [], createdAt: new Date().toISOString() };
        setConversations((prev) => [newConv, ...prev]);
        setActiveConversationId(newConv.id);
        convId = newConv.id;
      }

      try {
        const result = await analyzeStatisticalSummary(token, currentFile, previousFile || null);

        // Build the ordered list of reports to show (A1 current, A3, A1 prev, A2).
        // Filter to only reports that were actually generated.
        const reportDefs = [
          { html: result.a1CurrentHtml,  label: 'A1 — Current Week Report' },
          { html: result.a3Html,         label: 'A3 — Threat Actor Spotlight' },
          { html: result.a1PreviousHtml, label: 'A1 — Previous Week Report' },
          { html: result.a2Html,         label: 'A2 — Two-Week Comparison' },
        ].filter((r) => r.html);

        // Create a blob URL for every report. These URLs survive the await and
        // can be embedded as links in the chat message so the user can always
        // open them manually — even when the browser blocks auto-open pop-ups.
        const reportLinks = reportDefs.map((r) => ({
          label: r.label,
          url: createHtmlBlobUrl(r.html),
        }));

        // Attempt to auto-open every tab. Browsers typically allow only the
        // first window.open() after an await; subsequent calls may be blocked.
        // The links in the chat message act as a reliable fallback.
        reportLinks.forEach((r, i) => {
          setTimeout(() => window.open(r.url, r.label || '_blank'), i * 80);
        });

        // Build the summary text (no blob URLs in markdown — they get sanitised)
        const summaryText =
          `Statistical summary generated for **${result.dateRange || 'the selected period'}**.\n\n` +
          `- Top threat actor: **${result.topActor}**\n` +
          `- Total incidents: **${result.total}**`;

        // Store report links separately so MessageList renders them as real <a> tags
        const responseMsg = {
          id: generateId(),
          type: 'assistant',
          text: summaryText,
          reportLinks,
        };
        setMessages((prev) => [...prev, responseMsg]);
        updateConversationMessages(convId, [...messages, responseMsg]);
        closeStatisticalSummaryModal();
      } catch (err) {
        if (err.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          return;
        }
        const errMsg = {
          id: generateId(),
          type: 'assistant',
          text: `Statistical summary failed: ${err.message || 'Unknown error. Please check your CSV and try again.'}`,
        };
        setMessages((prev) => [...prev, errMsg]);
        updateConversationMessages(convId, [...messages, errMsg]);
      } finally {
        setIsStatisticalSummaryLoading(false);
      }
    },
    [token, closeStatisticalSummaryModal, activeConversationId, messages, updateConversationMessages]
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Infographic handlers
  // ──────────────────────────────────────────────────────────────────────────

  const openInfographicModal = useCallback(() => {
    if (isInfographicLoading) return;
    setIsInfographicModalOpen(true);
    logger.userAction('Open infographic modal');
  }, [isInfographicLoading]);

  const closeInfographicModal = useCallback(() => {
    setIsInfographicModalOpen(false);
  }, []);

  const submitInfographic = useCallback(
    async ({ pdfFile, focus, detail }, onSessionExpired) => {
      if (!pdfFile) return;
      setIsInfographicLoading(true);

      let convId = activeConversationId;
      if (!convId) {
        const newConv = { id: generateId(), messages: [], createdAt: new Date().toISOString() };
        setConversations((prev) => [newConv, ...prev]);
        setActiveConversationId(newConv.id);
        convId = newConv.id;
      }

      const requestMsg = {
        id: generateId(),
        type: 'user',
        text: `Create infographic from **${pdfFile.name}**.`,
      };
      const progressMsg = {
        id: generateId(),
        type: 'assistant',
        text: INFOGRAPHIC_STATUS_STEPS[0],
        infographicPending: true,
      };
      const replaceProgressMessage = (updates) => {
        const applyUpdates = (items) => items.map((message) =>
          message.id === progressMsg.id
            ? { ...message, ...updates }
            : message
        );
        setMessages((prev) => applyUpdates(prev));
        setConversations((prev) => prev.map((conv) =>
          conv.id === convId
            ? { ...conv, messages: applyUpdates(conv.messages) }
            : conv
        ));
      };

      setMessages((prev) => [...prev, requestMsg, progressMsg]);
      setConversations((prev) => prev.map((conv) =>
        conv.id === convId
          ? { ...conv, messages: [...conv.messages, requestMsg, progressMsg] }
          : conv
      ));

      let statusIndex = 0;
      const statusTimer = setInterval(() => {
        statusIndex = Math.min(statusIndex + 1, INFOGRAPHIC_STATUS_STEPS.length - 1);
        replaceProgressMessage({ text: INFOGRAPHIC_STATUS_STEPS[statusIndex] });
        if (statusIndex === INFOGRAPHIC_STATUS_STEPS.length - 1) {
          clearInterval(statusTimer);
        }
      }, 15000);

      try {
        const result = await generateInfographic(
          token,
          pdfFile,
          focus || '',
          detail || 'standard',
          convId
        );

        replaceProgressMessage({
          text: `Infographic generated from **${pdfFile.name}**.`,
          infographicPending: false,
          // Prefer the persisted URL so the multi-megabyte base64 image does not
          // exceed localStorage quota. Fall back to inline data if storage failed.
          generated_image_url: result.image_url || undefined,
          generated_image_data: result.image_url ? undefined : result.image_base64,
          generated_image_filename: result.filename || 'infographic.png',
        });
      } catch (err) {
        if (err.message === 'SESSION_EXPIRED') {
          replaceProgressMessage({
            text: 'Infographic generation stopped because your session expired.',
            infographicPending: false,
          });
          onSessionExpired?.();
          return;
        }
        replaceProgressMessage({
          text: `Infographic generation failed: ${err.message || 'Unknown error. Please try again.'}`,
          infographicPending: false,
        });
      } finally {
        clearInterval(statusTimer);
        setIsInfographicLoading(false);
      }
    },
    [token, activeConversationId]
  );

  const pollDeepResearchJobToMessage = useCallback(async (jobRecord) => {
    const { jobId, conversationId, messageId, onSessionExpired } = jobRecord;
    if (deepResearchPollersRef.current[jobId]) return;

    const poll = async () => {
      try {
        const status = await getDeepResearchStatus(token, jobId);
        const progressText = formatDeepResearchProgress(status);
        setDeepResearchProgress({
          message: progressText,
          step: status.step || status.progress?.step || '',
        });

        if (status.status === 'ready') {
          deepResearchSseCancelledRef.current[jobId] = true;
          const reportText = status.response || status.report_markdown || 'Report ready.';
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: reportText,
            deepResearchJobStatus: 'ready',
            deepResearchJobStep: status.step || status.progress?.step,
            deepResearchCitations: status.citations || [],
            usedDeepResearch: true,
          });
          removeActiveDeepResearchJob(jobId);
          delete deepResearchPollersRef.current[jobId];
          return;
        }

        if (status.status === 'failed') {
          deepResearchSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'failed',
            deepResearchJobStep: status.step || status.progress?.step,
          });
          removeActiveDeepResearchJob(jobId);
          delete deepResearchPollersRef.current[jobId];
          return;
        }

        if (status.status === 'cancelled') {
          deepResearchSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: 'Deep research job was cancelled.',
            deepResearchJobStatus: 'cancelled',
          });
          removeActiveDeepResearchJob(jobId);
          delete deepResearchPollersRef.current[jobId];
          return;
        }

        updateDeepResearchJobMessage(conversationId, messageId, {
          text: progressText,
          deepResearchJobStatus: status.status || 'running',
          deepResearchJobStep: status.step || status.progress?.step,
          deepResearchCitations: status.citations || [],
        });
        saveActiveDeepResearchJob({ ...jobRecord, conversationId, messageId });

        deepResearchPollersRef.current[jobId] = setTimeout(poll, DEEP_RESEARCH_POLL_INTERVAL_MS);
      } catch (error) {
        if (error.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          delete deepResearchPollersRef.current[jobId];
          return;
        }
        deepResearchPollersRef.current[jobId] = setTimeout(poll, DEEP_RESEARCH_POLL_INTERVAL_MS);
      }
    };

    poll();
  }, [
    token,
    updateDeepResearchJobMessage,
    saveActiveDeepResearchJob,
    removeActiveDeepResearchJob,
  ]);

  const pollGmsJobToMessage = useCallback(async (jobRecord) => {
    const { jobId, conversationId, messageId, onSessionExpired } = jobRecord;
    if (gmsPollersRef.current[jobId]) return;

    const poll = async () => {
      try {
        const status = await getGmsReportStatus(token, jobId);
        const step = status?.step || status?.progress?.step || '';
        const message = status?.message || '';
        const percent = status?.progress?.percent ?? '';
        const progressText =
          status?.status === 'ready'
            ? 'GMS Daily Intelligence Brief ready.'
            : status?.status === 'failed'
            ? `GMS report failed: ${status.detail || status.error || 'Unknown error'}`
            : [step && `Stage: ${step}${percent !== '' ? ` (${percent}%)` : ''}`, message]
                .filter(Boolean)
                .join('\n') || 'Generating GMS report…';

        setDeepResearchProgress({ message: progressText, step });

        if (status.status === 'ready') {
          gmsSseCancelledRef.current[jobId] = true;
          const reportText = status.response || status.report_markdown || 'Report ready.';
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: reportText,
            deepResearchJobStatus: 'ready',
            deepResearchJobStep: step,
            usedDeepResearch: true,
            aviationArtifactStatus: status.artifact_status || 'pending',
            aviationArtifactError: status.artifact_error || undefined,
            generated_image_url: status.infographic_url || undefined,
            generated_image_filename: status.infographic_url ? 'aviation-infographic.png' : undefined,
            aviation_pptx_url: status.pptx_url || undefined,
            aviation_pptx_filename: status.pptx_filename || 'aviation-executive-brief.pptx',
          });
          removeActiveGmsJob(jobId);
          delete gmsPollersRef.current[jobId];
          return;
        }

        if (status.status === 'failed') {
          gmsSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'failed',
            deepResearchJobStep: step,
          });
          removeActiveGmsJob(jobId);
          delete gmsPollersRef.current[jobId];
          return;
        }

        if (status.status === 'cancelled') {
          gmsSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: 'GMS report job was cancelled.',
            deepResearchJobStatus: 'cancelled',
          });
          removeActiveGmsJob(jobId);
          delete gmsPollersRef.current[jobId];
          return;
        }

        updateDeepResearchJobMessage(conversationId, messageId, {
          text: progressText,
          deepResearchJobStatus: status.status || 'running',
          deepResearchJobStep: step,
        });
        saveActiveGmsJob({ ...jobRecord, conversationId, messageId });

        gmsPollersRef.current[jobId] = setTimeout(poll, GMS_REPORT_POLL_INTERVAL_MS);
      } catch (error) {
        if (error.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          delete gmsPollersRef.current[jobId];
          return;
        }
        gmsPollersRef.current[jobId] = setTimeout(poll, GMS_REPORT_POLL_INTERVAL_MS);
      }
    };

    poll();
  }, [
    token,
    updateDeepResearchJobMessage,
    saveActiveGmsJob,
    removeActiveGmsJob,
  ]);

  const submitDeepResearch = useCallback(async (params, onSessionExpired) => {
    if (!params?.query || !token) return;

    setIsDeepResearchLoading(true);
    setDeepResearchProgress({ message: 'Starting deep research job…', step: 'PLANNING' });

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((prev) => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const sourceParts = [];
    if (params.sources?.web) sourceParts.push('web');
    if (params.sources?.communications) sourceParts.push('communications');
    if (params.sources?.attachments) sourceParts.push('attachments');

    const isAviationReport = params.report_type === 'aviation';
    const isGmsReport = params.report_type === 'gms';
    const jobLabel = isAviationReport ? 'Aviation Report' : isGmsReport ? 'GMS Report' : 'Deep Research';
    const msgBody = isAviationReport
      ? 'Generating Aviation Sector Executive Daily Brief...'
      : isGmsReport
      ? 'Generating GMS Daily Intelligence Brief...'
      : `${params.query}\n\nSources: ${sourceParts.join(', ') || 'none'}\nDepth: ${params.depth || 'standard'}`;
    const userMessage = {
      type: 'user',
      text: `[${jobLabel}]\n\n${msgBody}`,
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const { jobId } = await startDeepResearchJob(token, {
        ...params,
        conversationId: currentConversationId,
        userMessage: userMessage.text,
        pdfData: selectedPdf,
        pdfFilename: selectedPdfName,
        excelData: selectedExcel,
        excelFilename: selectedExcelName,
      });

      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: 'Deep research job accepted. This may take several minutes.\n\nRunning deep research…',
        usedDeepResearch: true,
        deepResearchJobId: jobId,
        deepResearchJobStatus: 'pending',
        deepResearchJobStep: 'PLANNING',
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        onSessionExpired,
        query: params.query,
        startedAt: Date.now(),
      };
      saveActiveDeepResearchJob(jobRecord);
      deepResearchSseCancelledRef.current[jobId] = false;
      pollDeepResearchJobToMessage(jobRecord);

      streamDeepResearchEvents(
        token,
        jobId,
        (event) => {
          const progressText = formatDeepResearchProgress(event);
          setDeepResearchProgress({
            message: progressText,
            step: event.step || '',
          });
          updateDeepResearchJobMessage(currentConversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'running',
            deepResearchJobStep: event.step,
          });
        },
        () => deepResearchSseCancelledRef.current[jobId] === true,
      ).catch(() => {
        // polling remains the fallback
      });

      setIsDeepResearchModalOpen(false);
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to start deep research.'}`,
      };
      setMessages([...updatedMessages, errorMessage]);
      updateConversationMessages(currentConversationId, [...updatedMessages, errorMessage]);
    } finally {
      setIsDeepResearchLoading(false);
    }
  }, [
    activeConversationId,
    messages,
    pollDeepResearchJobToMessage,
    saveActiveDeepResearchJob,
    updateDeepResearchJobMessage,
    selectedExcel,
    selectedExcelName,
    selectedPdf,
    selectedPdfName,
    token,
    updateConversationMessages,
  ]);

  const submitGmsReport = useCallback(async (onSessionExpired) => {
    if (!token) return;

    setIsDeepResearchLoading(true);
    setDeepResearchProgress({ message: 'Starting GMS report job…', step: 'STARTING' });

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((prev) => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = {
      type: 'user',
      text: '[GMS Report]\n\nGenerating GMS Daily Intelligence Brief...',
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const { jobId } = await startGmsReportJob(token, { conversationId: currentConversationId });

      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: 'GMS report job accepted. This may take several minutes.\n\nPhase 1: Fetching from advisory alert sources…',
        usedDeepResearch: true,
        deepResearchJobId: jobId,
        deepResearchJobStatus: 'pending',
        deepResearchJobStep: 'STARTING',
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        onSessionExpired,
        startedAt: Date.now(),
      };
      saveActiveGmsJob(jobRecord);
      gmsSseCancelledRef.current[jobId] = false;
      pollGmsJobToMessage(jobRecord);

      streamGmsReportEvents(
        token,
        jobId,
        (event) => {
          if (gmsSseCancelledRef.current[jobId]) return;
          const step = event?.step || '';
          const message = event?.message || '';

          // On terminal SSE events, trigger an immediate poll to fetch the actual
          // report content instead of showing the progress text.
          if (step === 'COMPLETED' || step === 'FAILED' || step === 'CANCELLED') {
            const existingTimer = gmsPollersRef.current[jobId];
            if (existingTimer) {
              clearTimeout(existingTimer);
              delete gmsPollersRef.current[jobId];
            }
            pollGmsJobToMessage(jobRecord);
            return;
          }

          const progressText = [step && `Stage: ${step}`, message].filter(Boolean).join('\n') || 'Generating GMS report…';
          setDeepResearchProgress({ message: progressText, step });
          updateDeepResearchJobMessage(currentConversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'running',
            deepResearchJobStep: step,
          });
        },
        () => gmsSseCancelledRef.current[jobId] === true,
      ).catch(() => {
        // polling remains the fallback
      });
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to start GMS report.'}`,
      };
      setMessages([...updatedMessages, errorMessage]);
      updateConversationMessages(currentConversationId, [...updatedMessages, errorMessage]);
    } finally {
      setIsDeepResearchLoading(false);
    }
  }, [
    activeConversationId,
    messages,
    pollGmsJobToMessage,
    saveActiveGmsJob,
    updateDeepResearchJobMessage,
    token,
    updateConversationMessages,
  ]);

  const pollAviationJobToMessage = useCallback(async (jobRecord) => {
    const { jobId, conversationId, messageId, onSessionExpired } = jobRecord;
    if (aviationPollersRef.current[jobId]) return;

    const poll = async () => {
      try {
        const status = await getAviationReportStatus(token, jobId);
        const step = status?.step || status?.progress?.step || '';
        const message = status?.message || '';
        const percent = status?.progress?.percent ?? '';
        const progressText =
          status?.status === 'ready'
            ? 'Aviation report ready.'
            : status?.status === 'failed'
            ? `Aviation report failed: ${status.detail || status.error || 'Unknown error'}`
            : [step && `Stage: ${step}${percent !== '' ? ` (${percent}%)` : ''}`, message]
                .filter(Boolean)
                .join('\n') || 'Generating Aviation report…';

        setDeepResearchProgress({ message: progressText, step });

        if (status.status === 'ready') {
          aviationSseCancelledRef.current[jobId] = true;
          const reportText = status.response || status.report_markdown || 'Report ready.';
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: reportText,
            deepResearchJobStatus: 'ready',
            deepResearchJobStep: step,
            usedDeepResearch: true,
            aviationArtifactStatus: status.artifact_status || 'pending',
            aviationArtifactError: status.artifact_error || undefined,
            generated_image_url: status.infographic_url || undefined,
            generated_image_filename: status.infographic_url ? 'aviation-infographic.png' : undefined,
            aviationPptxAvailable: Boolean(status.pptx_ready),
            aviation_pptx_filename: status.pptx_filename || 'aviation-executive-brief.pptx',
          });
          removeActiveAviationJob(jobId);
          delete aviationPollersRef.current[jobId];
          return;
        }

        if (status.status === 'failed') {
          aviationSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'failed',
            deepResearchJobStep: step,
          });
          removeActiveAviationJob(jobId);
          delete aviationPollersRef.current[jobId];
          return;
        }

        if (status.status === 'cancelled') {
          aviationSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: 'Aviation report job was cancelled.',
            deepResearchJobStatus: 'cancelled',
          });
          removeActiveAviationJob(jobId);
          delete aviationPollersRef.current[jobId];
          return;
        }

        updateDeepResearchJobMessage(conversationId, messageId, {
          text: progressText,
          deepResearchJobStatus: status.status || 'running',
          deepResearchJobStep: step,
        });
        saveActiveAviationJob({ ...jobRecord, conversationId, messageId });

        aviationPollersRef.current[jobId] = setTimeout(poll, AVIATION_REPORT_POLL_INTERVAL_MS);
      } catch (error) {
        if (error.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          delete aviationPollersRef.current[jobId];
          return;
        }
        aviationPollersRef.current[jobId] = setTimeout(poll, AVIATION_REPORT_POLL_INTERVAL_MS);
      }
    };

    aviationPollersRef.current[jobId] = setTimeout(poll, AVIATION_REPORT_POLL_INTERVAL_MS);
  }, [
    token,
    updateDeepResearchJobMessage,
    saveActiveAviationJob,
    removeActiveAviationJob,
  ]);

  const retryAviationArtifacts = useCallback(async (msg) => {
    const jobId = msg?.deepResearchJobId;
    const messageId = msg?.id;
    const conversationId = activeConversationId;
    if (!jobId || !messageId || !conversationId || !token) return;

    const pollerKey = `artifacts:${jobId}`;
    const pollArtifacts = async () => {
      try {
        const status = await getAviationReportStatus(token, jobId);
        updateDeepResearchJobMessage(conversationId, messageId, {
          aviationArtifactStatus: status.artifact_status || 'pending',
          aviationArtifactError: status.artifact_error || undefined,
          generated_image_url: status.infographic_url || undefined,
          generated_image_filename: status.infographic_url ? 'aviation-infographic.png' : undefined,
          aviationPptxAvailable: Boolean(status.pptx_ready),
          aviation_pptx_filename: status.pptx_filename || 'aviation-executive-brief.pptx',
        });
        if (status.artifact_status === 'generating') {
          aviationPollersRef.current[pollerKey] = setTimeout(
            pollArtifacts,
            AVIATION_REPORT_POLL_INTERVAL_MS
          );
        } else {
          delete aviationPollersRef.current[pollerKey];
        }
      } catch (error) {
        updateDeepResearchJobMessage(conversationId, messageId, {
          aviationArtifactStatus: 'failed',
          aviationArtifactError: error.message || 'Artifact generation failed.',
        });
        delete aviationPollersRef.current[pollerKey];
      }
    };

    updateDeepResearchJobMessage(conversationId, messageId, {
      aviationArtifactStatus: 'generating',
      aviationArtifactError: undefined,
    });
    try {
      await retryAviationReportArtifacts(token, jobId);
      await pollArtifacts();
    } catch (error) {
      updateDeepResearchJobMessage(conversationId, messageId, {
        aviationArtifactStatus: 'failed',
        aviationArtifactError: error.message || 'Artifact retry failed.',
      });
    }
  }, [activeConversationId, token, updateDeepResearchJobMessage]);

  const downloadAviationPptx = useCallback(async (msg) => {
    const jobId = msg?.deepResearchJobId;
    if (!jobId || !token) return;
    const { blob, filename } = await downloadAviationReportPptx(token, jobId);
    const url = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename || msg.aviation_pptx_filename || 'aviation-executive-brief.pptx';
      anchor.click();
    } finally {
      URL.revokeObjectURL(url);
    }
  }, [token]);

  useEffect(() => {
    if (!token || !activeConversationId) return undefined;
    if (aviationArtifactRefreshRef.current.conversationId !== activeConversationId) {
      aviationArtifactRefreshRef.current = {
        conversationId: activeConversationId,
        jobIds: new Set(),
      };
    }

    const candidates = messages.filter((msg) => (
      msg?.type === 'assistant'
      && msg?.deepResearchJobId
      && (
        msg.aviationArtifactStatus
        || String(msg.text || '').includes('Aviation Sector Executive Daily Brief')
      )
      && !aviationArtifactRefreshRef.current.jobIds.has(msg.deepResearchJobId)
    ));
    if (!candidates.length) return undefined;

    candidates.forEach((msg) => {
      aviationArtifactRefreshRef.current.jobIds.add(msg.deepResearchJobId);
      getAviationReportStatus(token, msg.deepResearchJobId)
        .then((status) => {
          if (
            aviationArtifactRefreshRef.current.conversationId !== activeConversationId
            || status.status !== 'ready'
          ) return;
          updateDeepResearchJobMessage(activeConversationId, msg.id, {
            aviationArtifactStatus: status.artifact_status || 'pending',
            aviationArtifactError: status.artifact_error || undefined,
            generated_image_url: status.infographic_url || undefined,
            generated_image_filename: status.infographic_url ? 'aviation-infographic.png' : undefined,
            aviationPptxAvailable: Boolean(status.pptx_ready),
            aviation_pptx_filename: status.pptx_filename || 'aviation-executive-brief.pptx',
          });
        })
        .catch(() => {
          // The Markdown report remains usable if artifact metadata cannot refresh.
        });
    });
    return undefined;
  }, [
    activeConversationId,
    messages,
    token,
    updateDeepResearchJobMessage,
  ]);

  const submitAviationReport = useCallback(async (onSessionExpired) => {
    if (!token) return;

    setIsDeepResearchLoading(true);
    setDeepResearchProgress({ message: 'Starting Aviation Report job…', step: 'STARTING' });

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((prev) => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = {
      type: 'user',
      text: '[Aviation Report]\n\nGenerating Aviation Sector Executive Daily Brief...',
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const { jobId } = await startAviationReportJob(token, { conversationId: currentConversationId });

      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: 'Aviation report job accepted. This may take several minutes.\n\nPhase 1: Fetching from aviation news sources…',
        usedDeepResearch: true,
        deepResearchJobId: jobId,
        deepResearchJobStatus: 'pending',
        deepResearchJobStep: 'STARTING',
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        onSessionExpired,
        startedAt: Date.now(),
      };
      saveActiveAviationJob(jobRecord);
      aviationSseCancelledRef.current[jobId] = false;
      pollAviationJobToMessage(jobRecord);

      streamAviationReportEvents(
        token,
        jobId,
        (event) => {
          // If polling already marked the job as complete, ignore late SSE events
          // to prevent overwriting the actual report content.
          if (aviationSseCancelledRef.current[jobId]) return;
          const step = event?.step || '';
          const message = event?.message || '';

          // On terminal SSE events, skip the progress update and instead trigger
          // an immediate poll so the actual report content is fetched right away.
          if (step === 'COMPLETED' || step === 'FAILED' || step === 'CANCELLED') {
            const existingTimer = aviationPollersRef.current[jobId];
            if (existingTimer) {
              clearTimeout(existingTimer);
              delete aviationPollersRef.current[jobId];
            }
            pollAviationJobToMessage(jobRecord);
            return;
          }

          const progressText = [step && `Stage: ${step}`, message].filter(Boolean).join('\n') || 'Generating Aviation report…';
          setDeepResearchProgress({ message: progressText, step });
          updateDeepResearchJobMessage(currentConversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'running',
            deepResearchJobStep: step,
          });
        },
        () => aviationSseCancelledRef.current[jobId] === true,
      ).catch(() => {
        // polling remains the fallback
      });
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to start Aviation report.'}`,
      };
      setMessages([...updatedMessages, errorMessage]);
      updateConversationMessages(currentConversationId, [...updatedMessages, errorMessage]);
    } finally {
      setIsDeepResearchLoading(false);
    }
  }, [
    activeConversationId,
    messages,
    pollAviationJobToMessage,
    saveActiveAviationJob,
    updateDeepResearchJobMessage,
    token,
    updateConversationMessages,
  ]);

  const pollEnergyJobToMessage = useCallback(async (jobRecord) => {
    const { jobId, conversationId, messageId, onSessionExpired } = jobRecord;
    if (energyPollersRef.current[jobId]) return;

    const poll = async () => {
      try {
        const status = await getEnergyReportStatus(token, jobId);
        const step = status?.step || status?.progress?.step || '';
        const message = status?.message || '';
        const percent = status?.progress?.percent ?? '';
        const progressText =
          status?.status === 'ready'
            ? 'Energy report ready.'
            : status?.status === 'failed'
            ? `Energy report failed: ${status.detail || status.error || 'Unknown error'}`
            : [step && `Stage: ${step}${percent !== '' ? ` (${percent}%)` : ''}`, message]
                .filter(Boolean)
                .join('\n') || 'Generating Energy report…';

        setDeepResearchProgress({ message: progressText, step });

        if (status.status === 'ready') {
          energySseCancelledRef.current[jobId] = true;
          const reportText = status.response || status.report_markdown || 'Report ready.';
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: reportText,
            deepResearchJobStatus: 'ready',
            deepResearchJobStep: step,
            usedDeepResearch: true,
          });
          removeActiveEnergyJob(jobId);
          delete energyPollersRef.current[jobId];
          return;
        }

        if (status.status === 'failed') {
          energySseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'failed',
            deepResearchJobStep: step,
          });
          removeActiveEnergyJob(jobId);
          delete energyPollersRef.current[jobId];
          return;
        }

        if (status.status === 'cancelled') {
          energySseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: 'Energy report job was cancelled.',
            deepResearchJobStatus: 'cancelled',
          });
          removeActiveEnergyJob(jobId);
          delete energyPollersRef.current[jobId];
          return;
        }

        updateDeepResearchJobMessage(conversationId, messageId, {
          text: progressText,
          deepResearchJobStatus: status.status || 'running',
          deepResearchJobStep: step,
        });
        saveActiveEnergyJob({ ...jobRecord, conversationId, messageId });

        energyPollersRef.current[jobId] = setTimeout(poll, ENERGY_REPORT_POLL_INTERVAL_MS);
      } catch (error) {
        if (error.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          delete energyPollersRef.current[jobId];
          return;
        }
        energyPollersRef.current[jobId] = setTimeout(poll, ENERGY_REPORT_POLL_INTERVAL_MS);
      }
    };

    energyPollersRef.current[jobId] = setTimeout(poll, ENERGY_REPORT_POLL_INTERVAL_MS);
  }, [
    token,
    updateDeepResearchJobMessage,
    saveActiveEnergyJob,
    removeActiveEnergyJob,
  ]);

  const submitEnergyReport = useCallback(async (onSessionExpired) => {
    if (!token) return;

    setIsDeepResearchLoading(true);
    setDeepResearchProgress({ message: 'Starting Energy Report job…', step: 'STARTING' });

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((prev) => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = {
      type: 'user',
      text: '[Energy Report]\n\nGenerating Energy Sector Executive Daily Brief...',
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const { jobId } = await startEnergyReportJob(token, { conversationId: currentConversationId });

      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: 'Energy report job accepted. This may take several minutes.\n\nPhase 1: Fetching from energy news sources…',
        usedDeepResearch: true,
        deepResearchJobId: jobId,
        deepResearchJobStatus: 'pending',
        deepResearchJobStep: 'STARTING',
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        onSessionExpired,
        startedAt: Date.now(),
      };
      saveActiveEnergyJob(jobRecord);
      energySseCancelledRef.current[jobId] = false;
      pollEnergyJobToMessage(jobRecord);

      streamEnergyReportEvents(
        token,
        jobId,
        (event) => {
          if (energySseCancelledRef.current[jobId]) return;
          const step = event?.step || '';
          const message = event?.message || '';

          // COMPLETE (from update_progress) and COMPLETED (from mark_ready) both
          // signal the job is done.  Immediately poll for the final report rather
          // than waiting the full 8-second interval, because the SSE stream may
          // end before the COMPLETED event is appended (mark_ready race condition).
          if (step === 'COMPLETE' || step === 'COMPLETED' || step === 'FAILED' || step === 'CANCELLED') {
            const existingTimer = energyPollersRef.current[jobId];
            if (existingTimer) {
              clearTimeout(existingTimer);
              delete energyPollersRef.current[jobId];
            }
            // Immediate poll: fetch status now, no delay
            getEnergyReportStatus(token, jobId).then((status) => {
              if (energySseCancelledRef.current[jobId]) return;
              if (status?.status === 'ready') {
                energySseCancelledRef.current[jobId] = true;
                const reportText = status.response || status.report_markdown || 'Report ready.';
                updateDeepResearchJobMessage(currentConversationId, messageId, {
                  text: reportText,
                  deepResearchJobStatus: 'ready',
                  deepResearchJobStep: step,
                  usedDeepResearch: true,
                });
                removeActiveEnergyJob(jobId);
              } else {
                // Not ready yet (e.g., COMPLETE fired but mark_ready not done) — fall back to poll
                pollEnergyJobToMessage(jobRecord);
              }
            }).catch(() => {
              pollEnergyJobToMessage(jobRecord);
            });
            return;
          }

          const progressText = [step && `Stage: ${step}`, message].filter(Boolean).join('\n') || 'Generating Energy report…';
          setDeepResearchProgress({ message: progressText, step });
          updateDeepResearchJobMessage(currentConversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'running',
            deepResearchJobStep: step,
          });
        },
        () => energySseCancelledRef.current[jobId] === true,
      ).catch(() => {
        // polling remains the fallback
      });
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to start Energy report.'}`,
      };
      setMessages([...updatedMessages, errorMessage]);
      updateConversationMessages(currentConversationId, [...updatedMessages, errorMessage]);
    } finally {
      setIsDeepResearchLoading(false);
    }
  }, [
    activeConversationId,
    messages,
    pollEnergyJobToMessage,
    saveActiveEnergyJob,
    removeActiveEnergyJob,
    updateDeepResearchJobMessage,
    token,
    updateConversationMessages,
  ]);

  const pollDataCenterJobToMessage = useCallback(async (jobRecord) => {
    const { jobId, conversationId, messageId, onSessionExpired } = jobRecord;
    if (dataCenterPollersRef.current[jobId]) return;

    const poll = async () => {
      try {
        const status = await getDataCenterReportStatus(token, jobId);
        const step = status?.step || status?.progress?.step || '';
        const message = status?.message || '';
        const percent = status?.progress?.percent ?? '';
        const progressText =
          status?.status === 'ready'
            ? 'Data Centers report ready.'
            : status?.status === 'failed'
            ? `Data Centers report failed: ${status.detail || status.error || 'Unknown error'}`
            : [step && `Stage: ${step}${percent !== '' ? ` (${percent}%)` : ''}`, message]
                .filter(Boolean)
                .join('\n') || 'Generating Data Centers report…';

        setDeepResearchProgress({ message: progressText, step });

        if (status.status === 'ready') {
          dataCenterSseCancelledRef.current[jobId] = true;
          const reportText = status.response || status.report_markdown || 'Report ready.';
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: reportText,
            deepResearchJobStatus: 'ready',
            deepResearchJobStep: step,
            usedDeepResearch: true,
          });
          removeActiveDataCenterJob(jobId);
          delete dataCenterPollersRef.current[jobId];
          return;
        }

        if (status.status === 'failed') {
          dataCenterSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'failed',
            deepResearchJobStep: step,
          });
          removeActiveDataCenterJob(jobId);
          delete dataCenterPollersRef.current[jobId];
          return;
        }

        if (status.status === 'cancelled') {
          dataCenterSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: 'Data Centers report job was cancelled.',
            deepResearchJobStatus: 'cancelled',
            deepResearchJobStep: step,
          });
          removeActiveDataCenterJob(jobId);
          delete dataCenterPollersRef.current[jobId];
          return;
        }

        updateDeepResearchJobMessage(conversationId, messageId, {
          text: progressText,
          deepResearchJobStatus: status.status || 'running',
          deepResearchJobStep: step,
        });
        saveActiveDataCenterJob({ ...jobRecord, conversationId, messageId });
        dataCenterPollersRef.current[jobId] = setTimeout(
          poll,
          DATA_CENTER_REPORT_POLL_INTERVAL_MS
        );
      } catch (error) {
        if (error.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          delete dataCenterPollersRef.current[jobId];
          return;
        }
        dataCenterPollersRef.current[jobId] = setTimeout(
          poll,
          DATA_CENTER_REPORT_POLL_INTERVAL_MS
        );
      }
    };

    dataCenterPollersRef.current[jobId] = setTimeout(
      poll,
      DATA_CENTER_REPORT_POLL_INTERVAL_MS
    );
  }, [
    token,
    updateDeepResearchJobMessage,
    saveActiveDataCenterJob,
    removeActiveDataCenterJob,
  ]);

  const submitDataCenterReport = useCallback(async (onSessionExpired) => {
    if (!token) return;

    setIsDeepResearchLoading(true);
    setDeepResearchProgress({ message: 'Starting Data Centers Report job…', step: 'STARTING' });

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((prev) => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = {
      type: 'user',
      text: '[Data Centers Report]\n\nGenerating Data Centers Executive Daily Brief...',
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const { jobId } = await startDataCenterReportJob(token, {
        conversationId: currentConversationId,
      });

      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: 'Data Centers report job accepted. This may take several minutes.',
        usedDeepResearch: true,
        deepResearchJobId: jobId,
        deepResearchJobStatus: 'pending',
        deepResearchJobStep: 'STARTING',
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        onSessionExpired,
        startedAt: Date.now(),
      };
      saveActiveDataCenterJob(jobRecord);
      dataCenterSseCancelledRef.current[jobId] = false;
      pollDataCenterJobToMessage(jobRecord);

      streamDataCenterReportEvents(
        token,
        jobId,
        (event) => {
          if (dataCenterSseCancelledRef.current[jobId]) return;
          const step = event?.step || '';
          const message = event?.message || '';

          if (
            step === 'COMPLETE'
            || step === 'COMPLETED'
            || step === 'FAILED'
            || step === 'CANCELLED'
          ) {
            const existingTimer = dataCenterPollersRef.current[jobId];
            if (existingTimer) {
              clearTimeout(existingTimer);
              delete dataCenterPollersRef.current[jobId];
            }
            getDataCenterReportStatus(token, jobId).then((status) => {
              if (dataCenterSseCancelledRef.current[jobId]) return;
              if (status?.status === 'ready') {
                dataCenterSseCancelledRef.current[jobId] = true;
                const reportText = status.response || status.report_markdown || 'Report ready.';
                updateDeepResearchJobMessage(currentConversationId, messageId, {
                  text: reportText,
                  deepResearchJobStatus: 'ready',
                  deepResearchJobStep: step,
                  usedDeepResearch: true,
                });
                removeActiveDataCenterJob(jobId);
              } else {
                pollDataCenterJobToMessage(jobRecord);
              }
            }).catch(() => {
              pollDataCenterJobToMessage(jobRecord);
            });
            return;
          }

          const progressText =
            [step && `Stage: ${step}`, message].filter(Boolean).join('\n')
            || 'Generating Data Centers report…';
          setDeepResearchProgress({ message: progressText, step });
          updateDeepResearchJobMessage(currentConversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'running',
            deepResearchJobStep: step,
          });
        },
        () => dataCenterSseCancelledRef.current[jobId] === true,
      ).catch(() => {
        // polling remains the fallback
      });
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to start Data Centers report.'}`,
      };
      setMessages([...updatedMessages, errorMessage]);
      updateConversationMessages(currentConversationId, [...updatedMessages, errorMessage]);
    } finally {
      setIsDeepResearchLoading(false);
    }
  }, [
    activeConversationId,
    messages,
    pollDataCenterJobToMessage,
    saveActiveDataCenterJob,
    removeActiveDataCenterJob,
    updateDeepResearchJobMessage,
    token,
    updateConversationMessages,
  ]);

  const pollGamingJobToMessage = useCallback(async (jobRecord) => {
    const { jobId, conversationId, messageId, onSessionExpired } = jobRecord;
    if (gamingPollersRef.current[jobId]) return;

    const poll = async () => {
      try {
        const status = await getGamingReportStatus(token, jobId);
        const step = status?.step || status?.progress?.step || '';
        const message = status?.message || '';
        const percent = status?.progress?.percent ?? '';
        const progressText =
          status?.status === 'ready'
            ? 'Gaming report ready.'
            : status?.status === 'failed'
            ? `Gaming report failed: ${status.detail || status.error || 'Unknown error'}`
            : [step && `Stage: ${step}${percent !== '' ? ` (${percent}%)` : ''}`, message]
                .filter(Boolean)
                .join('\n') || 'Generating Gaming report…';

        setDeepResearchProgress({ message: progressText, step });

        if (status.status === 'ready') {
          gamingSseCancelledRef.current[jobId] = true;
          const reportText = status.response || status.report_markdown || 'Report ready.';
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: reportText,
            deepResearchJobStatus: 'ready',
            deepResearchJobStep: step,
            usedDeepResearch: true,
          });
          removeActiveGamingJob(jobId);
          delete gamingPollersRef.current[jobId];
          return;
        }

        if (status.status === 'failed') {
          gamingSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'failed',
            deepResearchJobStep: step,
          });
          removeActiveGamingJob(jobId);
          delete gamingPollersRef.current[jobId];
          return;
        }

        if (status.status === 'cancelled') {
          gamingSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: 'Gaming report job was cancelled.',
            deepResearchJobStatus: 'cancelled',
            deepResearchJobStep: step,
          });
          removeActiveGamingJob(jobId);
          delete gamingPollersRef.current[jobId];
          return;
        }

        updateDeepResearchJobMessage(conversationId, messageId, {
          text: progressText,
          deepResearchJobStatus: status.status || 'running',
          deepResearchJobStep: step,
        });
        saveActiveGamingJob({ ...jobRecord, conversationId, messageId });
        gamingPollersRef.current[jobId] = setTimeout(
          poll,
          GAMING_REPORT_POLL_INTERVAL_MS
        );
      } catch (error) {
        if (error.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          delete gamingPollersRef.current[jobId];
          return;
        }
        gamingPollersRef.current[jobId] = setTimeout(
          poll,
          GAMING_REPORT_POLL_INTERVAL_MS
        );
      }
    };

    gamingPollersRef.current[jobId] = setTimeout(
      poll,
      GAMING_REPORT_POLL_INTERVAL_MS
    );
  }, [
    token,
    updateDeepResearchJobMessage,
    saveActiveGamingJob,
    removeActiveGamingJob,
  ]);

  const submitGamingReport = useCallback(async (onSessionExpired) => {
    if (!token) return;

    setIsDeepResearchLoading(true);
    setDeepResearchProgress({ message: 'Starting Gaming Report job…', step: 'STARTING' });

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((prev) => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = {
      type: 'user',
      text: '[Gaming Report]\n\nGenerating Gaming Sector Weekly Executive Briefing...',
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const { jobId } = await startGamingReportJob(token, {
        conversationId: currentConversationId,
      });

      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: 'Gaming report job accepted. This may take several minutes.',
        usedDeepResearch: true,
        deepResearchJobId: jobId,
        deepResearchJobStatus: 'pending',
        deepResearchJobStep: 'STARTING',
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        onSessionExpired,
        startedAt: Date.now(),
      };
      saveActiveGamingJob(jobRecord);
      gamingSseCancelledRef.current[jobId] = false;
      pollGamingJobToMessage(jobRecord);

      streamGamingReportEvents(
        token,
        jobId,
        (event) => {
          if (gamingSseCancelledRef.current[jobId]) return;
          const step = event?.step || '';
          const message = event?.message || '';

          if (
            step === 'COMPLETE'
            || step === 'COMPLETED'
            || step === 'FAILED'
            || step === 'CANCELLED'
          ) {
            const existingTimer = gamingPollersRef.current[jobId];
            if (existingTimer) {
              clearTimeout(existingTimer);
              delete gamingPollersRef.current[jobId];
            }
            getGamingReportStatus(token, jobId).then((status) => {
              if (gamingSseCancelledRef.current[jobId]) return;
              if (status?.status === 'ready') {
                gamingSseCancelledRef.current[jobId] = true;
                const reportText = status.response || status.report_markdown || 'Report ready.';
                updateDeepResearchJobMessage(currentConversationId, messageId, {
                  text: reportText,
                  deepResearchJobStatus: 'ready',
                  deepResearchJobStep: step,
                  usedDeepResearch: true,
                });
                removeActiveGamingJob(jobId);
              } else {
                pollGamingJobToMessage(jobRecord);
              }
            }).catch(() => {
              pollGamingJobToMessage(jobRecord);
            });
            return;
          }

          const progressText =
            [step && `Stage: ${step}`, message].filter(Boolean).join('\n')
            || 'Generating Gaming report…';
          setDeepResearchProgress({ message: progressText, step });
          updateDeepResearchJobMessage(currentConversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'running',
            deepResearchJobStep: step,
          });
        },
        () => gamingSseCancelledRef.current[jobId] === true,
      ).catch(() => {});
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to start Gaming report.'}`,
      };
      setMessages([...updatedMessages, errorMessage]);
      updateConversationMessages(currentConversationId, [...updatedMessages, errorMessage]);
    } finally {
      setIsDeepResearchLoading(false);
    }
  }, [
    activeConversationId,
    messages,
    pollGamingJobToMessage,
    saveActiveGamingJob,
    removeActiveGamingJob,
    updateDeepResearchJobMessage,
    token,
    updateConversationMessages,
  ]);

  const pollUsChinaTradeJobToMessage = useCallback(async (jobRecord) => {
    const { jobId, conversationId, messageId, onSessionExpired } = jobRecord;
    if (usChinaTradePollersRef.current[jobId]) return;

    const poll = async () => {
      try {
        const status = await getUsChinaTradeReportStatus(token, jobId);
        const progressText =
          status?.status === 'ready'
            ? 'US-China trade report ready.'
            : status?.status === 'failed'
              ? `US-China trade report failed: ${status.detail || status.error || 'Unknown error'}`
              : [status?.step && `Stage: ${status.step}`, status?.message]
                .filter(Boolean)
                .join('\n') || 'Generating US-China trade report…';

        if (status?.status === 'ready') {
          usChinaTradeSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: status.response || status.report_markdown || 'Report ready.',
            deepResearchJobStatus: 'ready',
            deepResearchJobStep: 'COMPLETED',
            usedDeepResearch: true,
          });
          removeActiveUsChinaTradeJob(jobId);
          delete usChinaTradePollersRef.current[jobId];
          return;
        }
        if (status?.status === 'failed') {
          usChinaTradeSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'failed',
          });
          removeActiveUsChinaTradeJob(jobId);
          delete usChinaTradePollersRef.current[jobId];
          return;
        }
        if (status?.status === 'cancelled') {
          usChinaTradeSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: 'US-China trade report job was cancelled.',
            deepResearchJobStatus: 'cancelled',
          });
          removeActiveUsChinaTradeJob(jobId);
          delete usChinaTradePollersRef.current[jobId];
          return;
        }

        setDeepResearchProgress({ message: progressText, step: status?.step });
        updateDeepResearchJobMessage(conversationId, messageId, {
          text: progressText,
          deepResearchJobStatus: 'running',
          deepResearchJobStep: status?.step,
        });
        saveActiveUsChinaTradeJob({ ...jobRecord, conversationId, messageId });
        usChinaTradePollersRef.current[jobId] = setTimeout(
          poll,
          US_CHINA_TRADE_REPORT_POLL_INTERVAL_MS
        );
      } catch (error) {
        if (error.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          delete usChinaTradePollersRef.current[jobId];
          return;
        }
        usChinaTradePollersRef.current[jobId] = setTimeout(
          poll,
          US_CHINA_TRADE_REPORT_POLL_INTERVAL_MS
        );
      }
    };

    usChinaTradePollersRef.current[jobId] = setTimeout(
      poll,
      US_CHINA_TRADE_REPORT_POLL_INTERVAL_MS
    );
  }, [
    token,
    updateDeepResearchJobMessage,
    saveActiveUsChinaTradeJob,
    removeActiveUsChinaTradeJob,
  ]);

  const submitUsChinaTradeReport = useCallback(async (onSessionExpired) => {
    if (!token) return;

    setIsDeepResearchLoading(true);
    setDeepResearchProgress({ message: 'Starting US-China Trade Report job…', step: 'STARTING' });

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((prev) => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = {
      type: 'user',
      text: '[US-China Trade Report]\n\nGenerating US-China Trade Dispute update...',
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const { jobId } = await startUsChinaTradeReportJob(token, {
        conversationId: currentConversationId,
      });

      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: 'US-China trade report job accepted. This may take several minutes.',
        usedDeepResearch: true,
        deepResearchJobId: jobId,
        deepResearchJobStatus: 'pending',
        deepResearchJobStep: 'STARTING',
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        onSessionExpired,
        startedAt: Date.now(),
      };
      saveActiveUsChinaTradeJob(jobRecord);
      usChinaTradeSseCancelledRef.current[jobId] = false;
      pollUsChinaTradeJobToMessage(jobRecord);

      streamUsChinaTradeReportEvents(
        token,
        jobId,
        (event) => {
          if (usChinaTradeSseCancelledRef.current[jobId]) return;
          const step = event?.step || '';
          const message = event?.message || '';

          if (
            step === 'COMPLETE'
            || step === 'COMPLETED'
            || step === 'FAILED'
            || step === 'CANCELLED'
          ) {
            const existingTimer = usChinaTradePollersRef.current[jobId];
            if (existingTimer) {
              clearTimeout(existingTimer);
              delete usChinaTradePollersRef.current[jobId];
            }
            getUsChinaTradeReportStatus(token, jobId).then((status) => {
              if (usChinaTradeSseCancelledRef.current[jobId]) return;
              if (status?.status === 'ready') {
                usChinaTradeSseCancelledRef.current[jobId] = true;
                const reportText = status.response || status.report_markdown || 'Report ready.';
                updateDeepResearchJobMessage(currentConversationId, messageId, {
                  text: reportText,
                  deepResearchJobStatus: 'ready',
                  deepResearchJobStep: step,
                  usedDeepResearch: true,
                });
                removeActiveUsChinaTradeJob(jobId);
              } else {
                pollUsChinaTradeJobToMessage(jobRecord);
              }
            }).catch(() => {
              pollUsChinaTradeJobToMessage(jobRecord);
            });
            return;
          }

          const progressText =
            [step && `Stage: ${step}`, message].filter(Boolean).join('\n')
            || 'Generating US-China trade report…';
          setDeepResearchProgress({ message: progressText, step });
          updateDeepResearchJobMessage(currentConversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'running',
            deepResearchJobStep: step,
          });
        },
        () => usChinaTradeSseCancelledRef.current[jobId] === true,
      ).catch(() => {});
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to start US-China trade report.'}`,
      };
      setMessages([...updatedMessages, errorMessage]);
      updateConversationMessages(currentConversationId, [...updatedMessages, errorMessage]);
    } finally {
      setIsDeepResearchLoading(false);
    }
  }, [
    activeConversationId,
    messages,
    pollUsChinaTradeJobToMessage,
    saveActiveUsChinaTradeJob,
    removeActiveUsChinaTradeJob,
    updateDeepResearchJobMessage,
    token,
    updateConversationMessages,
  ]);

  const pollLatamJobToMessage = useCallback(async (jobRecord) => {
    const { jobId, conversationId, messageId, onSessionExpired } = jobRecord;
    if (latamPollersRef.current[jobId]) return;

    const poll = async () => {
      try {
        const status = await getLatamSummaryStatus(token, jobId);
        const step = status?.step || status?.progress?.step || '';
        const message = status?.message || '';
        const percent = status?.progress?.percent ?? '';
        const progressText =
          status?.status === 'ready'
            ? 'LATAM summary ready.'
            : status?.status === 'failed'
            ? `LATAM summary failed: ${status.detail || status.error || 'Unknown error'}`
            : [step && `Stage: ${step}${percent !== '' ? ` (${percent}%)` : ''}`, message]
                .filter(Boolean)
                .join('\n') || 'Generating LATAM summary…';

        setDeepResearchProgress({ message: progressText, step });

        if (status.status === 'ready') {
          latamSseCancelledRef.current[jobId] = true;
          const reportText = status.response || status.report_markdown || 'Summary ready.';
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: reportText,
            deepResearchJobStatus: 'ready',
            deepResearchJobStep: step,
            usedDeepResearch: true,
          });
          removeActiveLatamJob(jobId);
          delete latamPollersRef.current[jobId];
          return;
        }

        if (status.status === 'failed') {
          latamSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'failed',
            deepResearchJobStep: step,
          });
          removeActiveLatamJob(jobId);
          delete latamPollersRef.current[jobId];
          return;
        }

        if (status.status === 'cancelled') {
          latamSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: 'LATAM summary job was cancelled.',
            deepResearchJobStatus: 'cancelled',
          });
          removeActiveLatamJob(jobId);
          delete latamPollersRef.current[jobId];
          return;
        }

        updateDeepResearchJobMessage(conversationId, messageId, {
          text: progressText,
          deepResearchJobStatus: status.status || 'running',
          deepResearchJobStep: step,
        });
        saveActiveLatamJob({ ...jobRecord, conversationId, messageId });

        latamPollersRef.current[jobId] = setTimeout(poll, LATAM_SUMMARY_POLL_INTERVAL_MS);
      } catch (error) {
        if (error.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          delete latamPollersRef.current[jobId];
          return;
        }
        latamPollersRef.current[jobId] = setTimeout(poll, LATAM_SUMMARY_POLL_INTERVAL_MS);
      }
    };

    poll();
  }, [
    token,
    updateDeepResearchJobMessage,
    saveActiveLatamJob,
    removeActiveLatamJob,
  ]);

  const submitLatamSummary = useCallback(async (urls, onSessionExpired) => {
    if (!token) return;

    const urlList = Array.isArray(urls) ? urls.filter(Boolean) : [];
    if (!urlList.length) {
      const errorMessage = {
        type: 'error',
        text: 'Error: No URLs found in the message. Please include article URLs to summarize.',
      };
      setMessages((prev) => [...prev, errorMessage]);
      return;
    }

    setIsDeepResearchLoading(true);
    setDeepResearchProgress({ message: 'Starting LATAM summary job…', step: 'STARTING' });

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((prev) => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = {
      type: 'user',
      text: `[LATAM Summary]\n\nGenerating LATAM crime incident summary for ${urlList.length} article(s)...`,
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const { jobId } = await startLatamSummaryJob(token, {
        urls: urlList,
        conversationId: currentConversationId,
      });

      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: `LATAM summary job accepted. Processing ${urlList.length} article(s)…`,
        usedDeepResearch: true,
        deepResearchJobId: jobId,
        deepResearchJobStatus: 'pending',
        deepResearchJobStep: 'STARTING',
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        onSessionExpired,
        startedAt: Date.now(),
      };
      saveActiveLatamJob(jobRecord);
      latamSseCancelledRef.current[jobId] = false;
      pollLatamJobToMessage(jobRecord);

      streamLatamSummaryEvents(
        token,
        jobId,
        (event) => {
          if (latamSseCancelledRef.current[jobId]) return;
          const step = event?.step || '';
          const message = event?.message || '';

          // When the job is done, skip updating the progress card and instead
          // trigger an immediate poll so the actual report text is fetched
          // without waiting for the next 8-second interval.
          if (step === 'COMPLETED' || step === 'FAILED' || step === 'CANCELLED') {
            const existingTimer = latamPollersRef.current[jobId];
            if (existingTimer) {
              clearTimeout(existingTimer);
              delete latamPollersRef.current[jobId];
            }
            pollLatamJobToMessage(jobRecord);
            return;
          }

          const progressText =
            [step && `Stage: ${step}`, message].filter(Boolean).join('\n') ||
            'Generating LATAM summary…';
          setDeepResearchProgress({ message: progressText, step });
          updateDeepResearchJobMessage(currentConversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'running',
            deepResearchJobStep: step,
          });
        },
        () => latamSseCancelledRef.current[jobId] === true,
      ).catch(() => {
        // polling remains the fallback
      });
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to start LATAM summary.'}`,
      };
      setMessages([...updatedMessages, errorMessage]);
      updateConversationMessages(currentConversationId, [...updatedMessages, errorMessage]);
    } finally {
      setIsDeepResearchLoading(false);
    }
  }, [
    activeConversationId,
    messages,
    pollLatamJobToMessage,
    saveActiveLatamJob,
    updateDeepResearchJobMessage,
    token,
    updateConversationMessages,
  ]);

  const pollLatamCsvJobToMessage = useCallback(async (jobRecord) => {
    const { jobId, conversationId, messageId, onSessionExpired } = jobRecord;
    if (latamCsvPollersRef.current[jobId]) return;

    const poll = async () => {
      try {
        const status = await getLatamToCsvStatus(token, jobId);
        const step = status?.step || status?.progress?.step || '';
        const message = status?.message || '';
        const percent = status?.progress?.percent;
        const progressText = status?.status === 'ready'
          ? 'LATAM CSV ready.'
          : status?.status === 'failed'
            ? `LATAM-to-CSV conversion failed: ${status.detail || status.error || 'Unknown error'}`
            : [step && `Stage: ${step}${percent != null ? ` (${percent}%)` : ''}`, message]
                .filter(Boolean)
                .join('\n') || 'Converting LATAM report to TSV…';

        setDeepResearchProgress({ message: progressText, step });
        if (status.status === 'ready') {
          latamCsvSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: status.response || status.report_markdown || 'LATAM CSV ready.',
            deepResearchJobStatus: 'ready',
            deepResearchJobStep: step,
            usedDeepResearch: true,
          });
          removeActiveLatamCsvJob(jobId);
          delete latamCsvPollersRef.current[jobId];
          return;
        }
        if (status.status === 'failed' || status.status === 'cancelled') {
          latamCsvSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: status.status === 'cancelled' ? 'LATAM-to-CSV job was cancelled.' : progressText,
            deepResearchJobStatus: status.status,
            deepResearchJobStep: step,
          });
          removeActiveLatamCsvJob(jobId);
          delete latamCsvPollersRef.current[jobId];
          return;
        }
        updateDeepResearchJobMessage(conversationId, messageId, {
          text: progressText,
          deepResearchJobStatus: status.status || 'running',
          deepResearchJobStep: step,
        });
        saveActiveLatamCsvJob(jobRecord);
        latamCsvPollersRef.current[jobId] = setTimeout(poll, LATAM_TO_CSV_POLL_INTERVAL_MS);
      } catch (error) {
        if (error.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          delete latamCsvPollersRef.current[jobId];
          return;
        }
        latamCsvPollersRef.current[jobId] = setTimeout(poll, LATAM_TO_CSV_POLL_INTERVAL_MS);
      }
    };
    poll();
  }, [
    token,
    updateDeepResearchJobMessage,
    saveActiveLatamCsvJob,
    removeActiveLatamCsvJob,
  ]);

  const submitLatamToCsv = useCallback(async (reportText, onSessionExpired) => {
    if (!token) return;
    const pastedReport = (reportText || '').trim();
    if (pastedReport.length < 80) {
      setMessages((previous) => [...previous, {
        type: 'error',
        text: 'Please paste the incident report after “do latam to csv” in the same message.',
      }]);
      return;
    }

    setIsDeepResearchLoading(true);
    setDeepResearchProgress({ message: 'Starting LATAM-to-CSV conversion…', step: 'STARTING' });
    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((previous) => [newConversation, ...previous]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = { type: 'user', text: `do latam to csv\n\n${pastedReport}` };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const { jobId } = await startLatamToCsvJob(token, {
        reportText: pastedReport,
        conversationId: currentConversationId,
      });
      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: 'LATAM-to-CSV job accepted. Extracting spreadsheet rows…',
        usedDeepResearch: true,
        deepResearchJobId: jobId,
        deepResearchJobStatus: 'pending',
        deepResearchJobStep: 'STARTING',
      };
      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        onSessionExpired,
        startedAt: Date.now(),
      };
      saveActiveLatamCsvJob(jobRecord);
      latamCsvSseCancelledRef.current[jobId] = false;
      pollLatamCsvJobToMessage(jobRecord);
      streamLatamToCsvEvents(
        token,
        jobId,
        (event) => {
          if (latamCsvSseCancelledRef.current[jobId]) return;
          const step = event?.step || '';
          if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(step)) {
            const timer = latamCsvPollersRef.current[jobId];
            if (timer) clearTimeout(timer);
            delete latamCsvPollersRef.current[jobId];
            pollLatamCsvJobToMessage(jobRecord);
            return;
          }
          const progressText = [step && `Stage: ${step}`, event?.message]
            .filter(Boolean)
            .join('\n');
          updateDeepResearchJobMessage(currentConversationId, messageId, {
            text: progressText || 'Converting LATAM report to TSV…',
            deepResearchJobStatus: 'running',
            deepResearchJobStep: step,
          });
        },
        () => latamCsvSseCancelledRef.current[jobId] === true,
      ).catch(() => {});
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        return;
      }
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to start LATAM-to-CSV conversion.'}`,
      };
      setMessages([...updatedMessages, errorMessage]);
      updateConversationMessages(currentConversationId, [...updatedMessages, errorMessage]);
    } finally {
      setIsDeepResearchLoading(false);
    }
  }, [
    token,
    activeConversationId,
    messages,
    updateConversationMessages,
    saveActiveLatamCsvJob,
    pollLatamCsvJobToMessage,
    updateDeepResearchJobMessage,
  ]);

  useEffect(() => {
    if (!token || !user?.id || conversations.length === 0) return;
    if (latamCsvResumeCheckedRef.current === user.id) return;
    latamCsvResumeCheckedRef.current = user.id;

    const resumeLatamCsvJobs = async () => {
      const storageKey = getLatamCsvJobsStorageKey(user.id);
      let savedJobs = [];
      try {
        savedJobs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (_) {
        savedJobs = [];
      }
      let backendJobs = [];
      try {
        backendJobs = await getActiveLatamToCsvJobs(token);
      } catch (error) {
        logger.error('Failed to load active LATAM-to-CSV jobs', error);
      }
      const candidates = [...savedJobs];
      for (const backendJob of backendJobs) {
        if (!candidates.some((job) => job.jobId === backendJob.job_id)) {
          candidates.push({
            jobId: backendJob.job_id,
            conversationId: backendJob.conversation_id,
            messageId: null,
            startedAt: Date.now(),
          });
        }
      }

      for (const saved of candidates) {
        try {
          const status = await getLatamToCsvStatus(token, saved.jobId);
          let conversationId = saved.conversationId;
          let conversation = conversations.find((item) => item.id === conversationId);
          if (!conversation) {
            conversation = conversations.find((item) => item.id === activeConversationId) || conversations[0];
            conversationId = conversation?.id;
          }
          if (!conversationId) continue;

          let messageId = saved.messageId;
          const hasPlaceholder = (conversation?.messages || []).some((message) => message.id === messageId);
          if (status.status === 'ready') {
            const reportText = status.response || status.report_markdown;
            if (!reportText) continue;
            if (hasPlaceholder) {
              updateDeepResearchJobMessage(conversationId, messageId, {
                text: reportText,
                deepResearchJobStatus: 'ready',
                usedDeepResearch: true,
              });
            } else {
              const completedMessage = {
                id: generateId(),
                type: 'assistant',
                text: reportText,
                usedDeepResearch: true,
                deepResearchJobId: saved.jobId,
                deepResearchJobStatus: 'ready',
              };
              setConversations((previous) => previous.map((item) => (
                item.id === conversationId
                  ? { ...item, messages: [...(item.messages || []), completedMessage] }
                  : item
              )));
              if (conversationId === activeConversationId) {
                setMessages((previous) => [...previous, completedMessage]);
              }
            }
            removeActiveLatamCsvJob(saved.jobId);
            continue;
          }
          if (status.status !== 'running') {
            removeActiveLatamCsvJob(saved.jobId);
            continue;
          }
          if (!hasPlaceholder) {
            messageId = generateId();
            const placeholder = {
              id: messageId,
              type: 'assistant',
              text: 'Resumed LATAM-to-CSV conversion…',
              usedDeepResearch: true,
              deepResearchJobId: saved.jobId,
              deepResearchJobStatus: 'running',
            };
            setConversations((previous) => previous.map((item) => (
              item.id === conversationId
                ? { ...item, messages: [...(item.messages || []), placeholder] }
                : item
            )));
            if (conversationId === activeConversationId) {
              setMessages((previous) => [...previous, placeholder]);
            }
          }
          const record = { ...saved, conversationId, messageId };
          saveActiveLatamCsvJob(record);
          latamCsvSseCancelledRef.current[saved.jobId] = false;
          pollLatamCsvJobToMessage(record);
          streamLatamToCsvEvents(
            token,
            saved.jobId,
            (event) => {
              if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(event?.step)) {
                const timer = latamCsvPollersRef.current[saved.jobId];
                if (timer) clearTimeout(timer);
                delete latamCsvPollersRef.current[saved.jobId];
                pollLatamCsvJobToMessage(record);
              }
            },
            () => latamCsvSseCancelledRef.current[saved.jobId] === true,
          ).catch(() => {});
        } catch (_) {
          removeActiveLatamCsvJob(saved.jobId);
        }
      }
    };
    resumeLatamCsvJobs();
  }, [
    token,
    user,
    conversations,
    activeConversationId,
    pollLatamCsvJobToMessage,
    saveActiveLatamCsvJob,
    removeActiveLatamCsvJob,
    updateDeepResearchJobMessage,
  ]);

  const pollSequoiaStructureJobToMessage = useCallback(async (jobRecord) => {
    const { jobId, conversationId, messageId, onSessionExpired } = jobRecord;
    if (sequoiaStructurePollersRef.current[jobId]) return;

    const poll = async () => {
      try {
        const status = await getSequoiaStructureStatus(token, jobId);
        const step = status?.step || status?.progress?.step || '';
        const message = status?.message || '';
        const percent = status?.progress?.percent;
        const progressText = status?.status === 'ready'
          ? 'Sequoia Structure brief ready.'
          : status?.status === 'failed'
            ? `Sequoia Structure conversion failed: ${status.detail || status.error || 'Unknown error'}`
            : [step && `Stage: ${step}${percent != null ? ` (${percent}%)` : ''}`, message]
                .filter(Boolean)
                .join('\n') || 'Rewriting the source report into a GMS brief…';

        setDeepResearchProgress({ message: progressText, step });
        if (status.status === 'ready') {
          sequoiaStructureSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: status.response || status.report_markdown || 'Sequoia Structure brief ready.',
            deepResearchJobStatus: 'ready',
            deepResearchJobStep: step,
            usedSequoiaStructure: true,
          });
          removeActiveSequoiaStructureJob(jobId);
          delete sequoiaStructurePollersRef.current[jobId];
          return;
        }
        if (status.status === 'failed' || status.status === 'cancelled') {
          sequoiaStructureSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: status.status === 'cancelled' ? 'Sequoia Structure job was cancelled.' : progressText,
            deepResearchJobStatus: status.status,
            deepResearchJobStep: step,
          });
          removeActiveSequoiaStructureJob(jobId);
          delete sequoiaStructurePollersRef.current[jobId];
          return;
        }
        updateDeepResearchJobMessage(conversationId, messageId, {
          text: progressText,
          deepResearchJobStatus: status.status || 'running',
          deepResearchJobStep: step,
        });
        saveActiveSequoiaStructureJob(jobRecord);
        sequoiaStructurePollersRef.current[jobId] = setTimeout(poll, SEQUOIA_STRUCTURE_POLL_INTERVAL_MS);
      } catch (error) {
        if (error.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          delete sequoiaStructurePollersRef.current[jobId];
          return;
        }
        sequoiaStructurePollersRef.current[jobId] = setTimeout(poll, SEQUOIA_STRUCTURE_POLL_INTERVAL_MS);
      }
    };
    poll();
  }, [
    token,
    updateDeepResearchJobMessage,
    saveActiveSequoiaStructureJob,
    removeActiveSequoiaStructureJob,
  ]);

  const submitSequoiaStructure = useCallback(async (reportText, onSessionExpired) => {
    if (!token) return;
    const pastedReport = (reportText || '').trim();
    if (pastedReport.length < 80) {
      setMessages((previous) => [...previous, {
        type: 'error',
        text: 'Please paste the source report after “do sequoia structure” in the same message.',
      }]);
      return;
    }

    setIsDeepResearchLoading(true);
    setDeepResearchProgress({ message: 'Starting Sequoia Structure conversion…', step: 'STARTING' });
    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((previous) => [newConversation, ...previous]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const userMessage = { type: 'user', text: `do sequoia structure\n\n${pastedReport}` };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const { jobId } = await startSequoiaStructureJob(token, {
        reportText: pastedReport,
        conversationId: currentConversationId,
      });
      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: 'Rewriting the source report into a GMS brief…',
        usedSequoiaStructure: true,
        deepResearchJobId: jobId,
        deepResearchJobStatus: 'pending',
        deepResearchJobStep: 'STARTING',
      };
      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);
      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        onSessionExpired,
        startedAt: Date.now(),
      };
      saveActiveSequoiaStructureJob(jobRecord);
      sequoiaStructureSseCancelledRef.current[jobId] = false;
      pollSequoiaStructureJobToMessage(jobRecord);
      streamSequoiaStructureEvents(
        token,
        jobId,
        (event) => {
          if (sequoiaStructureSseCancelledRef.current[jobId]) return;
          const step = event?.step || '';
          if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(step)) {
            const timer = sequoiaStructurePollersRef.current[jobId];
            if (timer) clearTimeout(timer);
            delete sequoiaStructurePollersRef.current[jobId];
            pollSequoiaStructureJobToMessage(jobRecord);
            return;
          }
          const progressText = [step && `Stage: ${step}`, event?.message]
            .filter(Boolean)
            .join('\n');
          updateDeepResearchJobMessage(currentConversationId, messageId, {
            text: progressText || 'Rewriting the source report into a GMS brief…',
            deepResearchJobStatus: 'running',
            deepResearchJobStep: step,
          });
        },
        () => sequoiaStructureSseCancelledRef.current[jobId] === true,
      ).catch(() => {});
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        return;
      }
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to start Sequoia Structure conversion.'}`,
      };
      setMessages([...updatedMessages, errorMessage]);
      updateConversationMessages(currentConversationId, [...updatedMessages, errorMessage]);
    } finally {
      setIsDeepResearchLoading(false);
    }
  }, [
    token,
    activeConversationId,
    messages,
    updateConversationMessages,
    saveActiveSequoiaStructureJob,
    pollSequoiaStructureJobToMessage,
    updateDeepResearchJobMessage,
  ]);

  useEffect(() => {
    if (!token || !user?.id || conversations.length === 0) return;
    if (sequoiaStructureResumeCheckedRef.current === user.id) return;
    sequoiaStructureResumeCheckedRef.current = user.id;

    const resumeSequoiaStructureJobs = async () => {
      const storageKey = getSequoiaStructureJobsStorageKey(user.id);
      let savedJobs = [];
      try {
        savedJobs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (_) {
        savedJobs = [];
      }
      let backendJobs = [];
      try {
        backendJobs = await getActiveSequoiaStructureJobs(token);
      } catch (error) {
        logger.error('Failed to load active Sequoia Structure jobs', error);
      }
      const candidates = [...savedJobs];
      for (const backendJob of backendJobs) {
        if (!candidates.some((job) => job.jobId === backendJob.job_id)) {
          candidates.push({
            jobId: backendJob.job_id,
            conversationId: backendJob.conversation_id,
            messageId: null,
            startedAt: Date.now(),
          });
        }
      }

      for (const saved of candidates) {
        try {
          const status = await getSequoiaStructureStatus(token, saved.jobId);
          let conversationId = saved.conversationId;
          let conversation = conversations.find((item) => item.id === conversationId);
          if (!conversation) {
            conversation = conversations.find((item) => item.id === activeConversationId) || conversations[0];
            conversationId = conversation?.id;
          }
          if (!conversationId) continue;

          let messageId = saved.messageId;
          const hasPlaceholder = (conversation?.messages || []).some((message) => message.id === messageId);
          if (status.status === 'ready') {
            const reportText = status.response || status.report_markdown;
            if (!reportText) continue;
            if (hasPlaceholder) {
              updateDeepResearchJobMessage(conversationId, messageId, {
                text: reportText,
                deepResearchJobStatus: 'ready',
                usedSequoiaStructure: true,
              });
            } else {
              const completedMessage = {
                id: generateId(),
                type: 'assistant',
                text: reportText,
                usedSequoiaStructure: true,
                deepResearchJobId: saved.jobId,
                deepResearchJobStatus: 'ready',
              };
              setConversations((previous) => previous.map((item) => (
                item.id === conversationId
                  ? { ...item, messages: [...(item.messages || []), completedMessage] }
                  : item
              )));
              if (conversationId === activeConversationId) {
                setMessages((previous) => [...previous, completedMessage]);
              }
            }
            removeActiveSequoiaStructureJob(saved.jobId);
            continue;
          }
          if (status.status !== 'running') {
            removeActiveSequoiaStructureJob(saved.jobId);
            continue;
          }
          if (!hasPlaceholder) {
            messageId = generateId();
            const placeholder = {
              id: messageId,
              type: 'assistant',
              text: 'Resumed Sequoia Structure conversion…',
              usedSequoiaStructure: true,
              deepResearchJobId: saved.jobId,
              deepResearchJobStatus: 'running',
            };
            setConversations((previous) => previous.map((item) => (
              item.id === conversationId
                ? { ...item, messages: [...(item.messages || []), placeholder] }
                : item
            )));
            if (conversationId === activeConversationId) {
              setMessages((previous) => [...previous, placeholder]);
            }
          }
          const record = { ...saved, conversationId, messageId };
          saveActiveSequoiaStructureJob(record);
          sequoiaStructureSseCancelledRef.current[saved.jobId] = false;
          pollSequoiaStructureJobToMessage(record);
          streamSequoiaStructureEvents(
            token,
            saved.jobId,
            (event) => {
              if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(event?.step)) {
                const timer = sequoiaStructurePollersRef.current[saved.jobId];
                if (timer) clearTimeout(timer);
                delete sequoiaStructurePollersRef.current[saved.jobId];
                pollSequoiaStructureJobToMessage(record);
              }
            },
            () => sequoiaStructureSseCancelledRef.current[saved.jobId] === true,
          ).catch(() => {});
        } catch (_) {
          removeActiveSequoiaStructureJob(saved.jobId);
        }
      }
    };
    resumeSequoiaStructureJobs();
  }, [
    token,
    user,
    conversations,
    activeConversationId,
    pollSequoiaStructureJobToMessage,
    saveActiveSequoiaStructureJob,
    removeActiveSequoiaStructureJob,
    updateDeepResearchJobMessage,
  ]);

  useEffect(() => {
    if (!token || !user?.id || conversations.length === 0) return;
    if (deepResearchResumeCheckedRef.current === user.id) return;
    deepResearchResumeCheckedRef.current = user.id;

    const resumeJobs = async () => {
      const storageKey = getDeepResearchJobsStorageKey(user.id);
      let savedJobs = [];
      try {
        savedJobs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (_) {
        savedJobs = [];
      }

      let backendJobs = [];
      let loadedBackendJobs = false;
      try {
        backendJobs = await getActiveDeepResearchJobs(token);
        loadedBackendJobs = true;
      } catch (error) {
        logger.error('Failed to load active deep research jobs', error);
        backendJobs = [];
      }

      const backendJobIds = new Set(backendJobs.map((j) => j.job_id));
      const backendJobById = new Map(backendJobs.map((j) => [j.job_id, j]));
      const knownJobs = savedJobs.filter((j) => backendJobIds.has(j.jobId));
      if (loadedBackendJobs && knownJobs.length !== savedJobs.length) {
        localStorage.setItem(storageKey, JSON.stringify(knownJobs));
      }

      // Jobs saved locally but no longer active may have completed while the
      // SSE stream was disconnected (e.g. page refreshed mid-run).
      // Check each and inject the result into the conversation if ready.
      const orphanedJobs = loadedBackendJobs
        ? savedJobs.filter((j) => !backendJobIds.has(j.jobId))
        : [];
      for (const job of orphanedJobs) {
        try {
          const status = await getDeepResearchStatus(token, job.jobId);
          if (status?.status !== 'ready') continue;
          const reportText = status.response || status.report_markdown;
          if (!reportText) continue;

          let conversationId = job.conversationId;
          let conversation = conversations.find((conv) => conv.id === conversationId);
          if (!conversation) {
            conversation =
              conversations.find((conv) => conv.id === activeConversationId) ||
              conversations[0];
            conversationId = conversation?.id;
          }
          if (!conversationId) continue;

          const hasPlaceholder = (conversation?.messages || []).some(
            (msg) => msg.id === job.messageId
          );

          if (hasPlaceholder) {
            updateDeepResearchJobMessage(conversationId, job.messageId, {
              text: reportText,
              deepResearchJobStatus: 'ready',
              deepResearchCitations: status.citations || [],
              usedDeepResearch: true,
            });
          } else {
            const newMessageId = generateId();
            const completedMsg = {
              id: newMessageId,
              type: 'assistant',
              text: reportText,
              usedDeepResearch: true,
              deepResearchJobId: job.jobId,
              deepResearchJobStatus: 'ready',
              deepResearchCitations: status.citations || [],
            };
            setConversations((prev) =>
              prev.map((conv) =>
                conv.id === conversationId
                  ? { ...conv, messages: [...(conv.messages || []), completedMsg] }
                  : conv
              )
            );
            if (conversationId === activeConversationId) {
              setMessages((prev) => [...prev, completedMsg]);
            }
          }
        } catch (_) {
          // Job expired or permanently lost — skip silently
        }
      }

      const startPolling = (record) => {
        saveActiveDeepResearchJob(record);
        deepResearchSseCancelledRef.current[record.jobId] = false;
        pollDeepResearchJobToMessage(record);
        streamDeepResearchEvents(
          token,
          record.jobId,
          (event) => {
            const progressText = formatDeepResearchProgress(event);
            updateDeepResearchJobMessage(record.conversationId, record.messageId, {
              text: progressText,
              deepResearchJobStatus: 'running',
              deepResearchJobStep: event.step,
            });
          },
          () => deepResearchSseCancelledRef.current[record.jobId] === true,
        ).catch(() => {});
      };

      for (const job of knownJobs) {
        if (deepResearchPollersRef.current[job.jobId]) continue;

        const backendJob = backendJobById.get(job.jobId);
        let conversationId = job.conversationId;
        let conversation = conversations.find((conv) => conv.id === conversationId);
        if (!conversation) {
          conversation = conversations.find((conv) => conv.id === activeConversationId) || conversations[0];
          conversationId = conversation?.id;
        }
        if (!conversationId) continue;

        let messageId = job.messageId;
        const hasPlaceholder = (conversation?.messages || []).some((msg) => msg.id === messageId);
        if (!hasPlaceholder) {
          messageId = generateId();
          const placeholder = {
            id: messageId,
            type: 'assistant',
            text: `${formatDeepResearchProgress(backendJob)}\n\nResumed deep research job.\nJob ID: ${job.jobId}`,
            usedDeepResearch: true,
            deepResearchJobId: job.jobId,
            deepResearchJobStatus: 'running',
            deepResearchJobStep: backendJob?.step || backendJob?.progress?.step,
          };

          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversationId
                ? { ...conv, messages: [...(conv.messages || []), placeholder] }
                : conv
            )
          );
          if (conversationId === activeConversationId) {
            setMessages((prev) => [...prev, placeholder]);
          }
        }

        startPolling({
          ...job,
          conversationId,
          messageId,
          query: job.query || 'Resumed deep research',
        });
      }

      for (const backendJob of backendJobs) {
        const jobId = backendJob.job_id;
        if (savedJobs.some((j) => j.jobId === jobId) || deepResearchPollersRef.current[jobId]) {
          continue;
        }

        const conversationId = activeConversationId || conversations[0]?.id;
        if (!conversationId) continue;

        const messageId = generateId();
        const placeholder = {
          id: messageId,
          type: 'assistant',
          text: `${formatDeepResearchProgress(backendJob)}\n\nResumed deep research job.\nJob ID: ${jobId}`,
          usedDeepResearch: true,
          deepResearchJobId: jobId,
          deepResearchJobStatus: 'running',
          deepResearchJobStep: backendJob.step || backendJob.progress?.step,
        };

        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId
              ? { ...conv, messages: [...(conv.messages || []), placeholder] }
              : conv
          )
        );
        if (conversationId === activeConversationId) {
          setMessages((prev) => [...prev, placeholder]);
        }

        startPolling({
          jobId,
          conversationId,
          messageId,
          query: 'Resumed deep research',
          startedAt: Date.now(),
        });
      }
    };

    resumeJobs();
  }, [
    token,
    user,
    conversations,
    activeConversationId,
    pollDeepResearchJobToMessage,
    saveActiveDeepResearchJob,
    updateDeepResearchJobMessage,
  ]);

  // Resume active GMS report jobs after page reload
  useEffect(() => {
    if (!token || !user?.id || conversations.length === 0) return;
    if (gmsResumeCheckedRef.current === user.id) return;
    gmsResumeCheckedRef.current = user.id;

    const resumeGmsJobs = async () => {
      const storageKey = getGmsJobsStorageKey(user.id);
      let savedJobs = [];
      try {
        savedJobs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (_) {
        savedJobs = [];
      }

      let backendJobs = [];
      let loadedBackendJobs = false;
      try {
        backendJobs = await getActiveGmsReportJobs(token);
        loadedBackendJobs = true;
      } catch (error) {
        logger.error('Failed to load active GMS report jobs', error);
      }

      const backendJobIds = new Set(backendJobs.map((j) => j.job_id));
      const savedBackendJobs = savedJobs.filter((j) => backendJobIds.has(j.jobId));
      const savedBackendJobIds = new Set(savedBackendJobs.map((j) => j.jobId));
      const recoveredBackendJobs = backendJobs
        .filter((job) => !savedBackendJobIds.has(job.job_id))
        .map((job) => {
          for (const conversation of conversations) {
            const message = (conversation.messages || []).find(
              (entry) => entry.deepResearchJobId === job.job_id
            );
            if (message) {
              return {
                jobId: job.job_id,
                conversationId: conversation.id,
                messageId: message.id,
              };
            }
          }
          return {
            jobId: job.job_id,
            conversationId: activeConversationId || conversations[0]?.id,
            messageId: null,
          };
        });
      const knownJobs = [...savedBackendJobs, ...recoveredBackendJobs];
      if (loadedBackendJobs) {
        localStorage.setItem(storageKey, JSON.stringify(knownJobs));
      }

      // Check orphaned jobs that may have completed while offline
      const orphanedJobs = loadedBackendJobs
        ? savedJobs.filter((j) => !backendJobIds.has(j.jobId))
        : [];
      for (const job of orphanedJobs) {
        try {
          const status = await getGmsReportStatus(token, job.jobId);
          if (status?.status !== 'ready') continue;
          const reportText = status.response || status.report_markdown;
          if (!reportText) continue;

          let conversationId = job.conversationId;
          let conversation = conversations.find((conv) => conv.id === conversationId);
          if (!conversation) {
            conversation =
              conversations.find((conv) => conv.id === activeConversationId) ||
              conversations[0];
            conversationId = conversation?.id;
          }
          if (!conversationId) continue;

          const hasPlaceholder = (conversation?.messages || []).some(
            (msg) => msg.id === job.messageId
          );
          if (hasPlaceholder) {
            updateDeepResearchJobMessage(conversationId, job.messageId, {
              text: reportText,
              deepResearchJobStatus: 'ready',
              usedDeepResearch: true,
            });
          } else {
            const newMsgId = generateId();
            const completedMsg = {
              id: newMsgId,
              type: 'assistant',
              text: reportText,
              usedDeepResearch: true,
              deepResearchJobId: job.jobId,
              deepResearchJobStatus: 'ready',
            };
            setConversations((prev) =>
              prev.map((conv) =>
                conv.id === conversationId
                  ? { ...conv, messages: [...(conv.messages || []), completedMsg] }
                  : conv
              )
            );
            if (conversationId === activeConversationId) {
              setMessages((prev) => [...prev, completedMsg]);
            }
          }
        } catch (_) {
          // Job expired or permanently lost — skip silently
        }
      }

      const startGmsPolling = (record) => {
        saveActiveGmsJob(record);
        gmsSseCancelledRef.current[record.jobId] = false;
        pollGmsJobToMessage(record);
        streamGmsReportEvents(
          token,
          record.jobId,
          (event) => {
            if (gmsSseCancelledRef.current[record.jobId]) return;
            const step = event?.step || '';
            const message = event?.message || '';

            if (step === 'COMPLETED' || step === 'FAILED' || step === 'CANCELLED') {
              const existingTimer = gmsPollersRef.current[record.jobId];
              if (existingTimer) {
                clearTimeout(existingTimer);
                delete gmsPollersRef.current[record.jobId];
              }
              pollGmsJobToMessage(record);
              return;
            }

            const progressText =
              [step && `Stage: ${step}`, message].filter(Boolean).join('\n') ||
              'Generating GMS report…';
            updateDeepResearchJobMessage(record.conversationId, record.messageId, {
              text: progressText,
              deepResearchJobStatus: 'running',
              deepResearchJobStep: step,
            });
          },
          () => gmsSseCancelledRef.current[record.jobId] === true,
        ).catch(() => {});
      };

      for (const job of knownJobs) {
        if (gmsPollersRef.current[job.jobId]) continue;

        let conversationId = job.conversationId;
        let conversation = conversations.find((conv) => conv.id === conversationId);
        if (!conversation) {
          conversation =
            conversations.find((conv) => conv.id === activeConversationId) ||
            conversations[0];
          conversationId = conversation?.id;
        }
        if (!conversationId) continue;

        let messageId = job.messageId;
        const hasPlaceholder = (conversation?.messages || []).some(
          (msg) => msg.id === messageId
        );
        if (!hasPlaceholder) {
          messageId = generateId();
          const placeholder = {
            id: messageId,
            type: 'assistant',
            text: `Resumed GMS report job.\nJob ID: ${job.jobId}`,
            usedDeepResearch: true,
            deepResearchJobId: job.jobId,
            deepResearchJobStatus: 'running',
          };
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversationId
                ? { ...conv, messages: [...(conv.messages || []), placeholder] }
                : conv
            )
          );
          if (conversationId === activeConversationId) {
            setMessages((prev) => [...prev, placeholder]);
          }
        }

        startGmsPolling({ ...job, conversationId, messageId });
      }
    };

    resumeGmsJobs();
  }, [
    token,
    user,
    conversations,
    activeConversationId,
    pollGmsJobToMessage,
    saveActiveGmsJob,
    updateDeepResearchJobMessage,
  ]);

  const pollTwaJobToMessage = useCallback(async (jobRecord) => {
    const { jobId, conversationId, messageId, onSessionExpired } = jobRecord;
    if (twaPollersRef.current[jobId]) return;

    const poll = async () => {
      try {
        const status = await getTwaSummaryStatus(token, jobId);
        const step = status?.step || status?.progress?.step || '';
        const message = status?.message || '';
        const percent = status?.progress?.percent ?? '';
        const progressText =
          status?.status === 'ready'
            ? 'TWA summary ready.'
            : status?.status === 'failed'
            ? `TWA summary failed: ${status.detail || status.error || 'Unknown error'}`
            : [step && `Stage: ${step}${percent !== '' ? ` (${percent}%)` : ''}`, message]
                .filter(Boolean)
                .join('\n') || 'Generating TWA summary…';

        setDeepResearchProgress({ message: progressText, step });

        if (status.status === 'ready') {
          twaSseCancelledRef.current[jobId] = true;
          const reportText = status.response || status.report_markdown || 'Summary ready.';
          const reportHtml = status.response_html || status.report_html || null;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: reportText,
            reportHtml,
            deepResearchJobStatus: 'ready',
            deepResearchJobStep: step,
            usedDeepResearch: true,
          });
          removeActiveTwaJob(jobId);
          delete twaPollersRef.current[jobId];
          return;
        }

        if (status.status === 'failed') {
          twaSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'failed',
            deepResearchJobStep: step,
          });
          removeActiveTwaJob(jobId);
          delete twaPollersRef.current[jobId];
          return;
        }

        if (status.status === 'cancelled') {
          twaSseCancelledRef.current[jobId] = true;
          updateDeepResearchJobMessage(conversationId, messageId, {
            text: 'TWA summary job was cancelled.',
            deepResearchJobStatus: 'cancelled',
          });
          removeActiveTwaJob(jobId);
          delete twaPollersRef.current[jobId];
          return;
        }

        updateDeepResearchJobMessage(conversationId, messageId, {
          text: progressText,
          deepResearchJobStatus: status.status || 'running',
          deepResearchJobStep: step,
        });
        saveActiveTwaJob({ ...jobRecord, conversationId, messageId });

        twaPollersRef.current[jobId] = setTimeout(poll, TWA_SUMMARY_POLL_INTERVAL_MS);
      } catch (error) {
        if (error.message === 'SESSION_EXPIRED') {
          onSessionExpired?.();
          delete twaPollersRef.current[jobId];
          return;
        }
        twaPollersRef.current[jobId] = setTimeout(poll, TWA_SUMMARY_POLL_INTERVAL_MS);
      }
    };

    poll();
  }, [
    token,
    updateDeepResearchJobMessage,
    saveActiveTwaJob,
    removeActiveTwaJob,
  ]);

  const submitTwaSummary = useCallback(async (urls, pastedTexts, template, onSessionExpired, pdfBase64) => {
    if (!token) return;

    const urlList = Array.isArray(urls) ? urls.filter(Boolean) : [];
    const pastedList = Array.isArray(pastedTexts) ? pastedTexts.filter(Boolean) : [];
    const pdfDocuments = pdfBase64 ? [pdfBase64] : [];

    if (!urlList.length && !pastedList.length && !pdfDocuments.length) {
      const errorMessage = {
        type: 'error',
        text: 'Error: No URLs, article text, or PDF found. Please include article URLs, paste the article text, or attach a PDF.',
      };
      setMessages((prev) => [...prev, errorMessage]);
      return;
    }

    setIsDeepResearchLoading(true);
    setDeepResearchProgress({ message: 'Starting TWA summary job…', step: 'STARTING' });

    let currentConversationId = activeConversationId;
    if (!currentConversationId) {
      const newConversation = {
        id: generateId(),
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setConversations((prev) => [newConversation, ...prev]);
      currentConversationId = newConversation.id;
      setActiveConversationId(newConversation.id);
    }

    const templateLabel = template ? ` (${template})` : '';
    const sourceSummary = [
      urlList.length ? `${urlList.length} URL(s)` : '',
      pdfDocuments.length ? `${pdfDocuments.length} PDF(s)` : '',
      pastedList.length ? `${pastedList.length} pasted text(s)` : '',
    ]
      .filter(Boolean)
      .join(', ');

    const userMessage = {
      type: 'user',
      text: `[Summary Tool${templateLabel}]\n\nGenerating intelligence article from ${sourceSummary}...`,
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateConversationMessages(currentConversationId, updatedMessages);

    try {
      const { jobId } = await startTwaSummaryJob(token, {
        urls: urlList,
        pastedTexts: pastedList,
        pdfDocuments,
        template: template || '',
        conversationId: currentConversationId,
      });

      const messageId = generateId();
      const assistantMessage = {
        id: messageId,
        type: 'assistant',
        text: `TWA summary job accepted. Processing ${sourceSummary}…`,
        usedDeepResearch: true,
        deepResearchJobId: jobId,
        deepResearchJobStatus: 'pending',
        deepResearchJobStep: 'STARTING',
      };

      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);
      updateConversationMessages(currentConversationId, finalMessages);

      const jobRecord = {
        jobId,
        conversationId: currentConversationId,
        messageId,
        onSessionExpired,
        startedAt: Date.now(),
      };
      saveActiveTwaJob(jobRecord);
      twaSseCancelledRef.current[jobId] = false;
      pollTwaJobToMessage(jobRecord);

      streamTwaSummaryEvents(
        token,
        jobId,
        (event) => {
          const step = event?.step || '';
          const message = event?.message || '';

          if (step === 'COMPLETED' || step === 'FAILED' || step === 'CANCELLED') {
            const existingTimer = twaPollersRef.current[jobId];
            if (existingTimer) {
              clearTimeout(existingTimer);
              delete twaPollersRef.current[jobId];
            }
            pollTwaJobToMessage(jobRecord);
            return;
          }

          const progressText =
            [step && `Stage: ${step}`, message].filter(Boolean).join('\n') ||
            'Generating TWA summary…';
          setDeepResearchProgress({ message: progressText, step });
          updateDeepResearchJobMessage(currentConversationId, messageId, {
            text: progressText,
            deepResearchJobStatus: 'running',
            deepResearchJobStep: step,
          });
        },
        () => twaSseCancelledRef.current[jobId] === true,
      ).catch(() => {
        // polling remains the fallback
      });
    } catch (error) {
      if (error.message === 'SESSION_EXPIRED') {
        onSessionExpired?.();
        alert('Session expired. Please login again.');
        return;
      }
      const errorMessage = {
        type: 'error',
        text: `Error: ${error.message || 'Failed to start TWA summary.'}`,
      };
      setMessages([...updatedMessages, errorMessage]);
      updateConversationMessages(currentConversationId, [...updatedMessages, errorMessage]);
    } finally {
      setIsDeepResearchLoading(false);
    }
  }, [
    activeConversationId,
    messages,
    pollTwaJobToMessage,
    saveActiveTwaJob,
    updateDeepResearchJobMessage,
    token,
    updateConversationMessages,
  ]);

  // Resume active LATAM summary jobs after page reload
  useEffect(() => {
    if (!token || !user?.id || conversations.length === 0) return;
    if (latamResumeCheckedRef.current === user.id) return;
    latamResumeCheckedRef.current = user.id;

    const resumeLatamJobs = async () => {
      const storageKey = getLatamJobsStorageKey(user.id);
      let savedJobs = [];
      try {
        savedJobs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (_) {
        savedJobs = [];
      }
      if (!savedJobs.length) return;

      let backendJobs = [];
      let loadedBackendJobs = false;
      try {
        backendJobs = await getActiveLatamSummaryJobs(token);
        loadedBackendJobs = true;
      } catch (error) {
        logger.error('Failed to load active LATAM summary jobs', error);
      }

      const backendJobIds = new Set(backendJobs.map((j) => j.job_id));
      const knownJobs = savedJobs.filter((j) => backendJobIds.has(j.jobId));
      if (loadedBackendJobs && knownJobs.length !== savedJobs.length) {
        localStorage.setItem(storageKey, JSON.stringify(knownJobs));
      }

      // Check orphaned jobs that may have completed while offline
      const orphanedJobs = loadedBackendJobs
        ? savedJobs.filter((j) => !backendJobIds.has(j.jobId))
        : [];
      for (const job of orphanedJobs) {
        try {
          const status = await getLatamSummaryStatus(token, job.jobId);
          if (status?.status !== 'ready') continue;
          const reportText = status.response || status.report_markdown;
          if (!reportText) continue;

          let conversationId = job.conversationId;
          let conversation = conversations.find((conv) => conv.id === conversationId);
          if (!conversation) {
            conversation =
              conversations.find((conv) => conv.id === activeConversationId) ||
              conversations[0];
            conversationId = conversation?.id;
          }
          if (!conversationId) continue;

          const hasPlaceholder = (conversation?.messages || []).some(
            (msg) => msg.id === job.messageId
          );
          if (hasPlaceholder) {
            updateDeepResearchJobMessage(conversationId, job.messageId, {
              text: reportText,
              deepResearchJobStatus: 'ready',
              usedDeepResearch: true,
            });
          } else {
            const newMsgId = generateId();
            const completedMsg = {
              id: newMsgId,
              type: 'assistant',
              text: reportText,
              usedDeepResearch: true,
              deepResearchJobId: job.jobId,
              deepResearchJobStatus: 'ready',
            };
            setConversations((prev) =>
              prev.map((conv) =>
                conv.id === conversationId
                  ? { ...conv, messages: [...(conv.messages || []), completedMsg] }
                  : conv
              )
            );
            if (conversationId === activeConversationId) {
              setMessages((prev) => [...prev, completedMsg]);
            }
          }
        } catch (_) {
          // Job expired or permanently lost — skip silently
        }
      }

      const startLatamPolling = (record) => {
        saveActiveLatamJob(record);
        latamSseCancelledRef.current[record.jobId] = false;
        pollLatamJobToMessage(record);
        streamLatamSummaryEvents(
          token,
          record.jobId,
          (event) => {
            if (latamSseCancelledRef.current[record.jobId]) return;
            const step = event?.step || '';
            const message = event?.message || '';

            if (step === 'COMPLETED' || step === 'FAILED' || step === 'CANCELLED') {
              const existingTimer = latamPollersRef.current[record.jobId];
              if (existingTimer) {
                clearTimeout(existingTimer);
                delete latamPollersRef.current[record.jobId];
              }
              pollLatamJobToMessage(record);
              return;
            }

            const progressText =
              [step && `Stage: ${step}`, message].filter(Boolean).join('\n') ||
              'Generating LATAM summary…';
            updateDeepResearchJobMessage(record.conversationId, record.messageId, {
              text: progressText,
              deepResearchJobStatus: 'running',
              deepResearchJobStep: step,
            });
          },
          () => latamSseCancelledRef.current[record.jobId] === true,
        ).catch(() => {});
      };

      for (const job of knownJobs) {
        if (latamPollersRef.current[job.jobId]) continue;

        let conversationId = job.conversationId;
        let conversation = conversations.find((conv) => conv.id === conversationId);
        if (!conversation) {
          conversation =
            conversations.find((conv) => conv.id === activeConversationId) ||
            conversations[0];
          conversationId = conversation?.id;
        }
        if (!conversationId) continue;

        let messageId = job.messageId;
        const hasPlaceholder = (conversation?.messages || []).some(
          (msg) => msg.id === messageId
        );
        if (!hasPlaceholder) {
          messageId = generateId();
          const placeholder = {
            id: messageId,
            type: 'assistant',
            text: `Resumed LATAM summary job.\nJob ID: ${job.jobId}`,
            usedDeepResearch: true,
            deepResearchJobId: job.jobId,
            deepResearchJobStatus: 'running',
          };
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversationId
                ? { ...conv, messages: [...(conv.messages || []), placeholder] }
                : conv
            )
          );
          if (conversationId === activeConversationId) {
            setMessages((prev) => [...prev, placeholder]);
          }
        }

        startLatamPolling({ ...job, conversationId, messageId });
      }
    };

    resumeLatamJobs();
  }, [
    token,
    user,
    conversations,
    activeConversationId,
    pollLatamJobToMessage,
    saveActiveLatamJob,
    updateDeepResearchJobMessage,
  ]);

  // Resume active TWA summary jobs after page reload
  useEffect(() => {
    if (!token || !user?.id || conversations.length === 0) return;
    if (twaResumeCheckedRef.current === user.id) return;
    twaResumeCheckedRef.current = user.id;

    const resumeTwaJobs = async () => {
      const storageKey = getTwaJobsStorageKey(user.id);
      let savedJobs = [];
      try {
        savedJobs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (_) {
        savedJobs = [];
      }
      if (!savedJobs.length) return;

      let backendJobs = [];
      let loadedBackendJobs = false;
      try {
        backendJobs = await getActiveTwaSummaryJobs(token);
        loadedBackendJobs = true;
      } catch (error) {
        logger.error('Failed to load active TWA summary jobs', error);
      }

      const backendJobIds = new Set(backendJobs.map((j) => j.job_id));
      const knownJobs = savedJobs.filter((j) => backendJobIds.has(j.jobId));
      if (loadedBackendJobs && knownJobs.length !== savedJobs.length) {
        localStorage.setItem(storageKey, JSON.stringify(knownJobs));
      }

      // Check orphaned jobs that may have completed while offline
      const orphanedJobs = loadedBackendJobs
        ? savedJobs.filter((j) => !backendJobIds.has(j.jobId))
        : [];
      for (const job of orphanedJobs) {
        try {
          const status = await getTwaSummaryStatus(token, job.jobId);
          if (status?.status !== 'ready') continue;
          const reportText = status.response || status.report_markdown;
          if (!reportText) continue;
          const reportHtml = status.response_html || status.report_html || null;

          let conversationId = job.conversationId;
          let conversation = conversations.find((conv) => conv.id === conversationId);
          if (!conversation) {
            conversation =
              conversations.find((conv) => conv.id === activeConversationId) ||
              conversations[0];
            conversationId = conversation?.id;
          }
          if (!conversationId) continue;

          const hasPlaceholder = (conversation?.messages || []).some(
            (msg) => msg.id === job.messageId
          );
          if (hasPlaceholder) {
            updateDeepResearchJobMessage(conversationId, job.messageId, {
              text: reportText,
              reportHtml,
              deepResearchJobStatus: 'ready',
              usedDeepResearch: true,
            });
          } else {
            const newMsgId = generateId();
            const completedMsg = {
              id: newMsgId,
              type: 'assistant',
              text: reportText,
              reportHtml,
              usedDeepResearch: true,
              deepResearchJobId: job.jobId,
              deepResearchJobStatus: 'ready',
            };
            setConversations((prev) =>
              prev.map((conv) =>
                conv.id === conversationId
                  ? { ...conv, messages: [...(conv.messages || []), completedMsg] }
                  : conv
              )
            );
            if (conversationId === activeConversationId) {
              setMessages((prev) => [...prev, completedMsg]);
            }
          }
        } catch (_) {
          // Job expired or permanently lost — skip silently
        }
      }

      const startTwaPolling = (record) => {
        saveActiveTwaJob(record);
        twaSseCancelledRef.current[record.jobId] = false;
        pollTwaJobToMessage(record);
        streamTwaSummaryEvents(
          token,
          record.jobId,
          (event) => {
            const step = event?.step || '';
            const message = event?.message || '';

            if (step === 'COMPLETED' || step === 'FAILED' || step === 'CANCELLED') {
              const existingTimer = twaPollersRef.current[record.jobId];
              if (existingTimer) {
                clearTimeout(existingTimer);
                delete twaPollersRef.current[record.jobId];
              }
              pollTwaJobToMessage(record);
              return;
            }

            const progressText =
              [step && `Stage: ${step}`, message].filter(Boolean).join('\n') ||
              'Generating TWA summary…';
            updateDeepResearchJobMessage(record.conversationId, record.messageId, {
              text: progressText,
              deepResearchJobStatus: 'running',
              deepResearchJobStep: step,
            });
          },
          () => twaSseCancelledRef.current[record.jobId] === true,
        ).catch(() => {});
      };

      for (const job of knownJobs) {
        if (twaPollersRef.current[job.jobId]) continue;

        let conversationId = job.conversationId;
        let conversation = conversations.find((conv) => conv.id === conversationId);
        if (!conversation) {
          conversation =
            conversations.find((conv) => conv.id === activeConversationId) ||
            conversations[0];
          conversationId = conversation?.id;
        }
        if (!conversationId) continue;

        let messageId = job.messageId;
        const hasPlaceholder = (conversation?.messages || []).some(
          (msg) => msg.id === messageId
        );
        if (!hasPlaceholder) {
          messageId = generateId();
          const placeholder = {
            id: messageId,
            type: 'assistant',
            text: `Resumed TWA summary job.\nJob ID: ${job.jobId}`,
            usedDeepResearch: true,
            deepResearchJobId: job.jobId,
            deepResearchJobStatus: 'running',
          };
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversationId
                ? { ...conv, messages: [...(conv.messages || []), placeholder] }
                : conv
            )
          );
          if (conversationId === activeConversationId) {
            setMessages((prev) => [...prev, placeholder]);
          }
        }

        startTwaPolling({ ...job, conversationId, messageId });
      }
    };

    resumeTwaJobs();
  }, [
    token,
    user,
    conversations,
    activeConversationId,
    pollTwaJobToMessage,
    saveActiveTwaJob,
    updateDeepResearchJobMessage,
  ]);

  // Resume active Aviation report jobs after page reload
  useEffect(() => {
    if (!token || !user?.id || conversations.length === 0) return;
    if (aviationResumeCheckedRef.current === user.id) return;
    aviationResumeCheckedRef.current = user.id;

    const resumeAviationJobs = async () => {
      const storageKey = getAviationJobsStorageKey(user.id);
      let savedJobs = [];
      try {
        savedJobs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (_) {
        savedJobs = [];
      }
      if (!savedJobs.length) return;

      let backendJobs = [];
      let loadedBackendJobs = false;
      try {
        backendJobs = await getActiveAviationReportJobs(token);
        loadedBackendJobs = true;
      } catch (error) {
        logger.error('Failed to load active Aviation report jobs', error);
      }

      const backendJobIds = new Set(backendJobs.map((j) => j.job_id));
      const knownJobs = savedJobs.filter((j) => backendJobIds.has(j.jobId));
      if (loadedBackendJobs && knownJobs.length !== savedJobs.length) {
        localStorage.setItem(storageKey, JSON.stringify(knownJobs));
      }

      // Check orphaned jobs that may have completed while offline
      const orphanedJobs = loadedBackendJobs
        ? savedJobs.filter((j) => !backendJobIds.has(j.jobId))
        : [];
      for (const job of orphanedJobs) {
        try {
          const status = await getAviationReportStatus(token, job.jobId);
          if (status?.status !== 'ready') continue;
          const reportText = status.response || status.report_markdown;
          if (!reportText) continue;

          let conversationId = job.conversationId;
          let conversation = conversations.find((conv) => conv.id === conversationId);
          if (!conversation) {
            conversation =
              conversations.find((conv) => conv.id === activeConversationId) ||
              conversations[0];
            conversationId = conversation?.id;
          }
          if (!conversationId) continue;

          const hasPlaceholder = (conversation?.messages || []).some(
            (msg) => msg.id === job.messageId
          );
          if (hasPlaceholder) {
            updateDeepResearchJobMessage(conversationId, job.messageId, {
              text: reportText,
              deepResearchJobStatus: 'ready',
              usedDeepResearch: true,
            });
          } else {
            const newMsgId = generateId();
            const completedMsg = {
              id: newMsgId,
              type: 'assistant',
              text: reportText,
              usedDeepResearch: true,
              deepResearchJobId: job.jobId,
              deepResearchJobStatus: 'ready',
            };
            setConversations((prev) =>
              prev.map((conv) =>
                conv.id === conversationId
                  ? { ...conv, messages: [...(conv.messages || []), completedMsg] }
                  : conv
              )
            );
            if (conversationId === activeConversationId) {
              setMessages((prev) => [...prev, completedMsg]);
            }
          }
        } catch (_) {
          // Job expired or permanently lost — skip silently
        }
      }

      const startAviationPolling = (record) => {
        saveActiveAviationJob(record);
        aviationSseCancelledRef.current[record.jobId] = false;
        pollAviationJobToMessage(record);
        streamAviationReportEvents(
          token,
          record.jobId,
          (event) => {
            if (aviationSseCancelledRef.current[record.jobId]) return;
            const step = event?.step || '';
            const message = event?.message || '';

            if (step === 'COMPLETED' || step === 'FAILED' || step === 'CANCELLED') {
              const existingTimer = aviationPollersRef.current[record.jobId];
              if (existingTimer) {
                clearTimeout(existingTimer);
                delete aviationPollersRef.current[record.jobId];
              }
              pollAviationJobToMessage(record);
              return;
            }

            const progressText =
              [step && `Stage: ${step}`, message].filter(Boolean).join('\n') ||
              'Generating Aviation report…';
            updateDeepResearchJobMessage(record.conversationId, record.messageId, {
              text: progressText,
              deepResearchJobStatus: 'running',
              deepResearchJobStep: step,
            });
          },
          () => aviationSseCancelledRef.current[record.jobId] === true,
        ).catch(() => {});
      };

      for (const job of knownJobs) {
        if (aviationPollersRef.current[job.jobId]) continue;

        let conversationId = job.conversationId;
        let conversation = conversations.find((conv) => conv.id === conversationId);
        if (!conversation) {
          conversation =
            conversations.find((conv) => conv.id === activeConversationId) ||
            conversations[0];
          conversationId = conversation?.id;
        }
        if (!conversationId) continue;

        let messageId = job.messageId;
        const hasPlaceholder = (conversation?.messages || []).some(
          (msg) => msg.id === messageId
        );
        if (!hasPlaceholder) {
          messageId = generateId();
          const placeholder = {
            id: messageId,
            type: 'assistant',
            text: `Resumed Aviation report job.\nJob ID: ${job.jobId}`,
            usedDeepResearch: true,
            deepResearchJobId: job.jobId,
            deepResearchJobStatus: 'running',
          };
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversationId
                ? { ...conv, messages: [...(conv.messages || []), placeholder] }
                : conv
            )
          );
          if (conversationId === activeConversationId) {
            setMessages((prev) => [...prev, placeholder]);
          }
        }

        startAviationPolling({ ...job, conversationId, messageId });
      }
    };

    resumeAviationJobs();
  }, [
    token,
    user,
    conversations,
    activeConversationId,
    pollAviationJobToMessage,
    saveActiveAviationJob,
    updateDeepResearchJobMessage,
  ]);

  // Resume active Energy report jobs after page reload
  useEffect(() => {
    if (!token || !user?.id || conversations.length === 0) return;
    if (energyResumeCheckedRef.current === user.id) return;
    energyResumeCheckedRef.current = user.id;

    const resumeEnergyJobs = async () => {
      const storageKey = getEnergyJobsStorageKey(user.id);
      let savedJobs = [];
      try {
        savedJobs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (_) {
        savedJobs = [];
      }
      if (!savedJobs.length) return;

      let backendJobs = [];
      let loadedBackendJobs = false;
      try {
        backendJobs = await getActiveEnergyReportJobs(token);
        loadedBackendJobs = true;
      } catch (error) {
        logger.error('Failed to load active Energy report jobs', error);
      }

      const backendJobIds = new Set(backendJobs.map((j) => j.job_id));
      const knownJobs = savedJobs.filter((j) => backendJobIds.has(j.jobId));
      if (loadedBackendJobs && knownJobs.length !== savedJobs.length) {
        localStorage.setItem(storageKey, JSON.stringify(knownJobs));
      }

      const orphanedJobs = loadedBackendJobs
        ? savedJobs.filter((j) => !backendJobIds.has(j.jobId))
        : [];
      for (const job of orphanedJobs) {
        try {
          const status = await getEnergyReportStatus(token, job.jobId);
          if (status?.status !== 'ready') continue;
          const reportText = status.response || status.report_markdown;
          if (!reportText) continue;

          let conversationId = job.conversationId;
          let conversation = conversations.find((conv) => conv.id === conversationId);
          if (!conversation) {
            conversation =
              conversations.find((conv) => conv.id === activeConversationId) ||
              conversations[0];
            conversationId = conversation?.id;
          }
          if (!conversationId) continue;

          const hasPlaceholder = (conversation?.messages || []).some(
            (msg) => msg.id === job.messageId
          );
          if (hasPlaceholder) {
            updateDeepResearchJobMessage(conversationId, job.messageId, {
              text: reportText,
              deepResearchJobStatus: 'ready',
              usedDeepResearch: true,
            });
          } else {
            const newMsgId = generateId();
            const completedMsg = {
              id: newMsgId,
              type: 'assistant',
              text: reportText,
              usedDeepResearch: true,
              deepResearchJobId: job.jobId,
              deepResearchJobStatus: 'ready',
            };
            setConversations((prev) =>
              prev.map((conv) =>
                conv.id === conversationId
                  ? { ...conv, messages: [...(conv.messages || []), completedMsg] }
                  : conv
              )
            );
            if (conversationId === activeConversationId) {
              setMessages((prev) => [...prev, completedMsg]);
            }
          }
        } catch (_) {
          // Job expired or permanently lost — skip silently
        }
      }

      const startEnergyPolling = (record) => {
        saveActiveEnergyJob(record);
        energySseCancelledRef.current[record.jobId] = false;
        pollEnergyJobToMessage(record);
        streamEnergyReportEvents(
          token,
          record.jobId,
          (event) => {
            if (energySseCancelledRef.current[record.jobId]) return;
            const step = event?.step || '';
            const message = event?.message || '';

            if (step === 'COMPLETE' || step === 'COMPLETED' || step === 'FAILED' || step === 'CANCELLED') {
              const existingTimer = energyPollersRef.current[record.jobId];
              if (existingTimer) {
                clearTimeout(existingTimer);
                delete energyPollersRef.current[record.jobId];
              }
              getEnergyReportStatus(token, record.jobId).then((status) => {
                if (energySseCancelledRef.current[record.jobId]) return;
                if (status?.status === 'ready') {
                  energySseCancelledRef.current[record.jobId] = true;
                  const reportText = status.response || status.report_markdown || 'Report ready.';
                  updateDeepResearchJobMessage(record.conversationId, record.messageId, {
                    text: reportText,
                    deepResearchJobStatus: 'ready',
                    usedDeepResearch: true,
                  });
                  removeActiveEnergyJob(record.jobId);
                } else {
                  pollEnergyJobToMessage(record);
                }
              }).catch(() => {
                pollEnergyJobToMessage(record);
              });
              return;
            }

            const progressText =
              [step && `Stage: ${step}`, message].filter(Boolean).join('\n') ||
              'Generating Energy report…';
            updateDeepResearchJobMessage(record.conversationId, record.messageId, {
              text: progressText,
              deepResearchJobStatus: 'running',
              deepResearchJobStep: step,
            });
          },
          () => energySseCancelledRef.current[record.jobId] === true,
        ).catch(() => {});
      };

      for (const job of knownJobs) {
        if (energyPollersRef.current[job.jobId]) continue;

        let conversationId = job.conversationId;
        let conversation = conversations.find((conv) => conv.id === conversationId);
        if (!conversation) {
          conversation =
            conversations.find((conv) => conv.id === activeConversationId) ||
            conversations[0];
          conversationId = conversation?.id;
        }
        if (!conversationId) continue;

        let messageId = job.messageId;
        const hasPlaceholder = (conversation?.messages || []).some(
          (msg) => msg.id === messageId
        );
        if (!hasPlaceholder) {
          messageId = generateId();
          const placeholder = {
            id: messageId,
            type: 'assistant',
            text: `Resumed Energy report job.\nJob ID: ${job.jobId}`,
            usedDeepResearch: true,
            deepResearchJobId: job.jobId,
            deepResearchJobStatus: 'running',
          };
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversationId
                ? { ...conv, messages: [...(conv.messages || []), placeholder] }
                : conv
            )
          );
          if (conversationId === activeConversationId) {
            setMessages((prev) => [...prev, placeholder]);
          }
        }
        startEnergyPolling({ ...job, conversationId, messageId });
      }
    };

    resumeEnergyJobs();
  }, [
    token,
    user,
    conversations,
    activeConversationId,
    pollEnergyJobToMessage,
    saveActiveEnergyJob,
    updateDeepResearchJobMessage,
  ]);

  // Resume active Data Centers report jobs after page reload
  useEffect(() => {
    if (!token || !user?.id || conversations.length === 0) return;
    if (dataCenterResumeCheckedRef.current === user.id) return;
    dataCenterResumeCheckedRef.current = user.id;

    const resumeDataCenterJobs = async () => {
      const storageKey = getDataCenterJobsStorageKey(user.id);
      let savedJobs = [];
      try {
        savedJobs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (_) {
        savedJobs = [];
      }
      if (!savedJobs.length) return;

      let backendJobs = [];
      let loadedBackendJobs = false;
      try {
        backendJobs = await getActiveDataCenterReportJobs(token);
        loadedBackendJobs = true;
      } catch (error) {
        logger.error('Failed to load active Data Centers report jobs', error);
      }

      const backendJobIds = new Set(backendJobs.map((j) => j.job_id));
      const knownJobs = savedJobs.filter((j) => backendJobIds.has(j.jobId));
      if (loadedBackendJobs && knownJobs.length !== savedJobs.length) {
        localStorage.setItem(storageKey, JSON.stringify(knownJobs));
      }

      const orphanedJobs = loadedBackendJobs
        ? savedJobs.filter((j) => !backendJobIds.has(j.jobId))
        : [];
      for (const job of orphanedJobs) {
        try {
          const status = await getDataCenterReportStatus(token, job.jobId);
          if (status?.status !== 'ready') continue;
          const reportText = status.response || status.report_markdown;
          if (!reportText) continue;

          let conversationId = job.conversationId;
          let conversation = conversations.find((conv) => conv.id === conversationId);
          if (!conversation) {
            conversation =
              conversations.find((conv) => conv.id === activeConversationId)
              || conversations[0];
            conversationId = conversation?.id;
          }
          if (!conversationId) continue;

          const hasPlaceholder = (conversation?.messages || []).some(
            (msg) => msg.id === job.messageId
          );
          if (hasPlaceholder) {
            updateDeepResearchJobMessage(conversationId, job.messageId, {
              text: reportText,
              deepResearchJobStatus: 'ready',
              usedDeepResearch: true,
            });
          } else {
            const completedMsg = {
              id: generateId(),
              type: 'assistant',
              text: reportText,
              usedDeepResearch: true,
              deepResearchJobId: job.jobId,
              deepResearchJobStatus: 'ready',
            };
            setConversations((prev) =>
              prev.map((conv) =>
                conv.id === conversationId
                  ? { ...conv, messages: [...(conv.messages || []), completedMsg] }
                  : conv
              )
            );
            if (conversationId === activeConversationId) {
              setMessages((prev) => [...prev, completedMsg]);
            }
          }
        } catch (_) {
          // Job expired or permanently lost — skip silently
        }
      }

      const startDataCenterPolling = (record) => {
        saveActiveDataCenterJob(record);
        dataCenterSseCancelledRef.current[record.jobId] = false;
        pollDataCenterJobToMessage(record);
        streamDataCenterReportEvents(
          token,
          record.jobId,
          (event) => {
            if (dataCenterSseCancelledRef.current[record.jobId]) return;
            const step = event?.step || '';
            const message = event?.message || '';

            if (
              step === 'COMPLETE'
              || step === 'COMPLETED'
              || step === 'FAILED'
              || step === 'CANCELLED'
            ) {
              const existingTimer = dataCenterPollersRef.current[record.jobId];
              if (existingTimer) {
                clearTimeout(existingTimer);
                delete dataCenterPollersRef.current[record.jobId];
              }
              getDataCenterReportStatus(token, record.jobId).then((status) => {
                if (dataCenterSseCancelledRef.current[record.jobId]) return;
                if (status?.status === 'ready') {
                  dataCenterSseCancelledRef.current[record.jobId] = true;
                  const reportText = status.response || status.report_markdown || 'Report ready.';
                  updateDeepResearchJobMessage(record.conversationId, record.messageId, {
                    text: reportText,
                    deepResearchJobStatus: 'ready',
                    usedDeepResearch: true,
                  });
                  removeActiveDataCenterJob(record.jobId);
                } else {
                  pollDataCenterJobToMessage(record);
                }
              }).catch(() => {
                pollDataCenterJobToMessage(record);
              });
              return;
            }

            const progressText =
              [step && `Stage: ${step}`, message].filter(Boolean).join('\n')
              || 'Generating Data Centers report…';
            updateDeepResearchJobMessage(record.conversationId, record.messageId, {
              text: progressText,
              deepResearchJobStatus: 'running',
              deepResearchJobStep: step,
            });
          },
          () => dataCenterSseCancelledRef.current[record.jobId] === true,
        ).catch(() => {});
      };

      for (const job of knownJobs) {
        if (dataCenterPollersRef.current[job.jobId]) continue;

        let conversationId = job.conversationId;
        let conversation = conversations.find((conv) => conv.id === conversationId);
        if (!conversation) {
          conversation =
            conversations.find((conv) => conv.id === activeConversationId)
            || conversations[0];
          conversationId = conversation?.id;
        }
        if (!conversationId) continue;

        let messageId = job.messageId;
        const hasPlaceholder = (conversation?.messages || []).some(
          (msg) => msg.id === messageId
        );
        if (!hasPlaceholder) {
          messageId = generateId();
          const placeholder = {
            id: messageId,
            type: 'assistant',
            text: `Resumed Data Centers report job.\nJob ID: ${job.jobId}`,
            usedDeepResearch: true,
            deepResearchJobId: job.jobId,
            deepResearchJobStatus: 'running',
          };
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversationId
                ? { ...conv, messages: [...(conv.messages || []), placeholder] }
                : conv
            )
          );
          if (conversationId === activeConversationId) {
            setMessages((prev) => [...prev, placeholder]);
          }
        }
        startDataCenterPolling({ ...job, conversationId, messageId });
      }
    };

    resumeDataCenterJobs();
  }, [
    token,
    user,
    conversations,
    activeConversationId,
    pollDataCenterJobToMessage,
    saveActiveDataCenterJob,
    removeActiveDataCenterJob,
    updateDeepResearchJobMessage,
  ]);

  // Resume pending PDF task jobs after page reload.
  // We use a dedicated lightweight localStorage key so this survives even when
  // the full conversation can't be saved (large PDFs may exceed quota).
  useEffect(() => {
    if (!token || !user?.id || conversations.length === 0) return;
    if (pdfTaskResumeCheckedRef.current === user.id) return;
    pdfTaskResumeCheckedRef.current = user.id;

    const resumePdfTasks = async () => {
      const storageKey = getPdfTaskJobsStorageKey(user.id);
      let savedJobs = [];
      try {
        savedJobs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (_) {
        savedJobs = [];
      }
      // Also collect any 'running' PDF task messages from conversations that
      // weren't persisted via localStorage (e.g. jobs started before this fix).
      const convJobs = [];
      for (const conv of conversations) {
        for (const msg of (conv.messages || [])) {
          if (msg.pdfTaskJobStatus !== 'running' || !msg.pdfTaskJobId) continue;
          if (savedJobs.some((j) => j.jobId === msg.pdfTaskJobId)) continue;
          convJobs.push({ jobId: msg.pdfTaskJobId, conversationId: conv.id, messageId: msg.id });
        }
      }
      const allJobs = [...savedJobs, ...convJobs];
      if (!allJobs.length) return;

      for (const job of allJobs) {
        const { jobId, conversationId, messageId } = job;
        if (!jobId || pdfTaskPollersRef.current[jobId]) continue;

        try {
          const status = await getPdfTaskStatus(token, jobId);

          // Helper: find the conversation and optionally the placeholder message
          const conv = conversations.find((c) => c.id === conversationId) ||
            conversations.find((c) => c.id === activeConversationId) ||
            conversations[0];
          const convId = conv?.id;
          if (!convId) continue;

          const hasPlaceholder = (conv?.messages || []).some((m) => m.id === messageId);

          const injectResult = (text, jobStatus) => {
            // Remove from active-jobs localStorage
            removeActivePdfTaskJob(jobId);

            if (hasPlaceholder) {
              updatePdfTaskMessage(convId, messageId, { text, pdfTaskJobStatus: jobStatus });
            } else {
              // Conversation message was lost (e.g. localStorage quota failure) — append result
              const newMsg = {
                id: generateId(),
                type: 'assistant',
                text,
                pdfTaskJobId: jobId,
                pdfTaskJobStatus: jobStatus,
              };
              setConversations((prev) =>
                prev.map((c) =>
                  c.id === convId ? { ...c, messages: [...(c.messages || []), newMsg] } : c
                )
              );
              if (convId === activeConversationId) {
                setMessages((prev) => [...prev, newMsg]);
              }
            }
          };

          if (status?.status === 'ready' && status.result_text) {
            injectResult(status.result_text, 'ready');
          } else if (status?.status === 'failed') {
            injectResult(`PDF processing failed: ${status.error || 'Unknown error'}`, 'failed');
          } else if (status?.status === 'running') {
            // Still in progress — re-attach SSE + polling
            pdfTaskSseCancelledRef.current[jobId] = false;
            pollPdfTaskJobToMessage(jobId, convId, hasPlaceholder ? messageId : generateId());
            streamPdfTaskEvents(
              token,
              jobId,
              (event) => {
                if (event.step === 'COMPLETED' && event.result_text) {
                  pdfTaskSseCancelledRef.current[jobId] = true;
                  clearTimeout(pdfTaskPollersRef.current[jobId]);
                  delete pdfTaskPollersRef.current[jobId];
                  injectResult(event.result_text, 'ready');
                } else if (event.step === 'FAILED') {
                  pdfTaskSseCancelledRef.current[jobId] = true;
                  clearTimeout(pdfTaskPollersRef.current[jobId]);
                  delete pdfTaskPollersRef.current[jobId];
                  injectResult(`PDF processing failed: ${event.message || 'Unknown error'}`, 'failed');
                } else if (event.message && hasPlaceholder) {
                  updatePdfTaskMessage(convId, messageId, {
                    text: event.message,
                    pdfTaskJobStatus: 'running',
                  });
                }
              },
              () => pdfTaskSseCancelledRef.current[jobId] === true,
            ).catch(() => {});
          }
        } catch (_) {
          // 404 / network error — job is gone, remove from localStorage
          removeActivePdfTaskJob(jobId);
        }
      }
    };

    resumePdfTasks();
  }, [
    token,
    user,
    conversations,
    activeConversationId,
    pollPdfTaskJobToMessage,
    updatePdfTaskMessage,
    removeActivePdfTaskJob,
    saveActivePdfTaskJob,
  ]);

  return {
    // State
    conversations,
    activeConversationId,
    messages,
    input,
    setInput,
    selectedModel,
    setSelectedModel,
    selectedImage,
    setSelectedImage,
    selectedPdf,
    setSelectedPdf,
    selectedPdfName,
    setSelectedPdfName,
    selectedExcel,
    setSelectedExcel,
    selectedExcelName,
    setSelectedExcelName,
    selectedVideo,
    setSelectedVideo,
    selectedVideoName,
    setSelectedVideoName,
    isLoading,
    hasActiveAsyncJob: activeAsyncJobCount > 0,
    isSidebarOpen,
    setIsSidebarOpen,
    copiedMessageIdx,
    isModelDropdownOpen,
    setIsModelDropdownOpen,
    isUploadDropdownOpen,
    setIsUploadDropdownOpen,
    expandedCommunications,
    isMultimodal,
    
    // Refs
    messagesEndRef,
    imageInputRef,
    pdfInputRef,
    excelInputRef,
    videoInputRef,
    modelDropdownRef,
    uploadDropdownRef,
    textareaRef,
    
    // Handlers
    handleCopyMessage,
    toggleCommunications,
    handleImageChange,
    handlePdfChange,
    handleExcelChange,
    handleVideoChange,
    createNewConversation,
    selectConversation,
    deleteConversation,
    handleClearAllHistory,
    handleSubmit,
    handleStopChat,
    handleStopAsyncJob,
    handleKeyDown,
    clearState,
    handleInputChange,
    
    // Dictionary Analysis
    isDictionaryCanvasOpen,
    setIsDictionaryCanvasOpen,
    dictionaryResults,
    dictionaryEdits,
    dictionaryImageUrl,
    dictionaryImageData,
    dictionaryPoiName,
    setDictionaryPoiName,
    isDictionaryLoading,
    dictionaryError,
    detectedDictionaryUrl,
    checkForDictionaryUrl,
    openDictionaryCanvas,
    startDictionaryAnalysis,
    updateDictionaryEdit,
    submitDictionaryApproval,
    closeDictionaryCanvas,
    
    // Canvas link persistence
    currentCanvasEntryId,
    canvasStatus,
    isCanvasReadOnly,
    reopenDictionaryCanvas,
    
    // Per-question approval
    questionApprovals,
    approveQuestion,
    unapproveQuestion,
    loadQuestionApprovals,
    
    // Protest Query Modal
    isProtestModalOpen,
    isProtestLoading,
    protestProgress,
    protestInitialValues,
    openProtestModal,
    closeProtestModal,
    submitProtestQuery,

    // Daily Protest Monitor configuration
    isProtestMonitorConfigOpen,
    protestMonitorConfig,
    isProtestMonitorConfigLoading,
    isProtestMonitorConfigSaving,
    protestMonitorConfigError,
    protestMonitorConfigSuccess,
    openProtestMonitorConfig,
    closeProtestMonitorConfig,
    saveProtestMonitorConfig,
    
    // Video Summarize Modal
    isVideoModalOpen,
    isVideoLoading,
    isVideoScoreLoading,
    videoInitialValues,
    videoMode,
    openVideoModal,
    closeVideoModal,
    submitVideoSummarize,
    submitVideoSummarizeUrl: submitVideoSummarizeUrlHandler,
    submitVideoScore,
    submitVideoTranscribe,
    submitVideoTranscribeUrl: submitVideoTranscribeUrlHandler,

    // Deepfake Detection Modal
    isDeepfakeModalOpen,
    isDeepfakeLoading,
    deepfakeInitialValues,
    openDeepfakeModal,
    closeDeepfakeModal,
    submitDeepfakeFile,
    submitDeepfakeUrl: submitDeepfakeUrlHandler,

    // Deep Research Modal
    isDeepResearchModalOpen,
    isDeepResearchLoading,
    deepResearchProgress,
    deepResearchInitialValues,
    openDeepResearchModal,
    closeDeepResearchModal,
    submitDeepResearch,

    // GMS Report (separate pipeline)
    submitGmsReport,

    // Aviation Report (separate pipeline)
    submitAviationReport,
    retryAviationArtifacts,
    downloadAviationPptx,

    // Energy Report (separate pipeline)
    submitEnergyReport,

    // Data Centers Report (separate pipeline)
    submitDataCenterReport,

    // Gaming Report (separate pipeline)
    submitGamingReport,

    // US-China Trade Report (separate pipeline)
    submitUsChinaTradeReport,

    // LATAM Summary
    submitLatamSummary,

    // LATAM-to-CSV Tool
    submitLatamToCsv,

    // Sequoia Structure Tool
    submitSequoiaStructure,

    // TWA Summary Tool
    submitTwaSummary,

    // Statistical Summary Tool
    isStatisticalSummaryModalOpen,
    isStatisticalSummaryLoading,
    openStatisticalSummaryModal,
    closeStatisticalSummaryModal,
    submitStatisticalSummary,

    // Infographic Generator
    isInfographicModalOpen,
    openInfographicModal,
    closeInfographicModal,
    submitInfographic,
  };
}

export default useChat;

